import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';
import {
  AlertTriangle,
  ChevronDown,
  ClipboardCheck,
  Download,
  FileSpreadsheet,
  Flag,
  FolderOpen,
  History,
  Link2,
  Redo2,
  Send,
  Shield,
  StickyNote,
  Undo2,
} from 'lucide-react';
import Tooltip from '@mui/material/Tooltip';
import { PageHeader } from '@ravenappdev/raven-ui';
import { AppIcon } from '../components/LucideIcon';
import './drawing-workspace-rail.css';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import PidCanvas from '../components/PidCanvas';
import AlertDialog from '../components/AlertDialog';
import ConfirmDialog from '../components/ConfirmDialog';
import { WizardStep } from '../components/PnidWizardSidebar';
import PnidMarkDialog, { PnidDialogKind } from '../components/PnidMarkDialog';
import PnidReviewTables, { type EquipmentReviewEdit } from '../components/PnidReviewTables';
import ContextBar from '../components/ContextBar';
import SlidePanel from '../components/SlidePanel';
import ReviewIssuesPanel, { SEVERITY_COLORS as POLICY_SEVERITY_COLORS } from '../components/ReviewIssuesPanel';
import ReviewModal from '../components/ReviewModal';
import VersionHistoryModal from '../components/VersionHistoryModal';
import DrawingContextPanel, { type NoteTarget } from '../components/DrawingContextPanel';
import PublishCheckPanel from '../components/PublishCheckPanel';
import OverflowScroller from '../components/OverflowScroller';
// NodeOverviewDialog removed - functionality consolidated into PnidMarkDialog
// EquipmentOverviewDialog removed - functionality consolidated into PnidMarkDialog
import { useAuth } from '../contexts/AuthContext';
import { useStepTimer } from '../hooks/useStepTimer';
import { useInvalidateRegisterContext } from '../hooks/useRegisterContext';
import {
  findRedundancySet,
  redundancySetConflictOf,
  usePutRedundancySetTags,
} from '../hooks/useRedundancySets';
import { resolveRedundancySetSelection } from '../utils/redundancySetSelection';
import { splitCompoundEquipment } from '../utils/splitEquipment';
import type { CompoundSplitPart } from '../utils/splitEquipment';
import { useActionTracker } from '../hooks/useActionTracker';
import { useFormSchema } from '../hooks/useFormSchemas';
import { useAiData } from '../hooks/useAiData';
import { useSetPnidSetup } from '../hooks/useSetPnidSetup';
import { useComments } from '../hooks/useComments';
import { useGroqVisionStatus } from '../hooks/useGroqVisionStatus';
import { usePlantModelExists } from '../hooks/usePlantModelExists';
import { useTemplateAnnotations } from '../hooks/useTemplateAnnotations';
import { ViewModeToggle, EquipmentOperatingCard, ProcessLensPanel, ControlLensPanel, WalkthroughPanel } from '../components/intelligence';
import { documentApi, pnidApi, commentsApi, templateApi, reviewPolicyApi } from '../api';
import type { ReviewPolicyFinding } from '../types';
import type { PlantModel, ViewMode, WalkthroughHighlights } from '../types/plantModel';
import type { FormSchemaDefinition } from '../types/formSchema';
import { 
  BoundingBox, 
  PidDocument, 
  AnnotationInfo,
  PnidWizardState,
  DrawingClass,
  MarkingStatus,
  InstrumentCategory,
  PendingTemplateItem,
  PnidNode,
  NodePointCandidate,
  NodePointCandidateLabel,
  CanvasComment,
  CropResult,
  TemplateAnnotation,
  TemplateMatchResult,
  MatchingMode,
  MatchStats,
  PnidEquipmentUpdate,
  PnidHyperlinkRef,
  DocumentReviewCorrectionCreate,
  IdentifyAssetKind,
  instrumentRegions,
} from "../types";
import {
  appendRegion,
  armDraft,
  canFinish,
  committedRegions,
  removeLastRegion,
  withRegionAt,
} from "./boxDraft";
import type { BoxDraft, GeometryTarget, GeometryTargetKind } from "./boxDraft";
import TemplatePanel from '../components/TemplatePanel';
import CommentPopover from "../components/CommentPopover";
import CommentInput from "../components/CommentInput";
import LineMarkPopover, {
  LineMarkPopoverValues,
} from "../components/LineMarkPopover";
import LineSegmentReviewPopover from "../components/LineSegmentReviewPopover";
import InstrumentEditor, { INSTRUMENT_EDITOR_WIDTH } from "../components/InstrumentEditor";
import { assetApi, instrumentApi } from "../api";
import type { PnidInstrument, PnidInstrumentUpdate } from "../types";
import {
  detectOverlappingEntity,
  polylineIntersectsBbox,
} from "../utils/geometry";
import type { MergedLineSegment } from "../utils/geometry";
import { formatInstrumentIdentity } from "../utils/instrumentIdentity";
import {
  EDIT_EQUIPMENT_PARAM,
  EDIT_INSTRUMENT_PARAM,
  STEP_PARAM,
  buildPnidPath,
  parseEditEquipmentId,
  parseEditInstrumentId,
  parseTargetEquipmentId,
  parseTargetInstrumentId,
  parseTargetNodeId,
  parseWizardStep,
} from "../utils/pnidDeepLink";
import { queryKeys } from '../queries/keys';
import {
  resolveInitialStep,
  stepForInstrumentContext,
  winningRouteTarget,
} from './resolveInitialStep';
import WorkspaceSkeleton from './WorkspaceSkeleton';
import WorkspaceTargetRail from './WorkspaceTargetRail';
import EquipmentInstrumentRail from './EquipmentInstrumentRail';
import { usePublishableAssets } from '../hooks/usePublishableAssets';
import { nodeTargets, type TargetSortOrder } from './targetRailItems';
import {
  diagramTypeLabel,
  documentMatchesSearch,
  getDocumentDiagramType,
  getDocumentPrimaryLabel,
  getDocumentSecondaryLabel,
} from '../utils/documentLabels';
import { documentDomainPathLabel } from '../utils/domainTree';
import { useDomainIndex } from '../hooks/useDomainIndex';
import {
  getDirectNodeContextInstruments,
  getEquipmentContextInstruments,
  getInstrumentEffectiveContext,
  getNodeContextInstruments,
  getTemplateAnnotationsFromInstruments,
} from '../utils/unifiedInstruments';

type PnidInstrumentKind = "equipmentInstrument" | "nodeInstrument";
type WizardSelectionKind = PnidDialogKind | PnidInstrumentKind | "controlLoop" | "relationship";
type WizardTargetKind = PnidDialogKind | PnidInstrumentKind;
type EditableWizardTargetKind = WizardTargetKind | "controlLoop";
// "node" targets adjust a duct node's persisted duct_bbox.
type PersistedBboxTarget = { kind: "equipment" | PnidInstrumentKind | "controlLoop" | "node"; id: string };
type PnidAnnotationBackendTarget = {
  kind: "equipment" | "instrument" | "node" | "controlLoop" | "relationship";
  id: string;
};
type PnidUndoEntry = {
  docId: string;
  label: string;
  state: PnidWizardState;
};
type AnnotationImportAdjustment = {
  sourceLabel: string;
  copiedSummary: string;
  replacedSummary: string;
  overwroteExisting: boolean;
  skippedRelationships: number;
  geometryAdjustmentText?: string;
};
type ImportGeometryTransform = {
  applied: boolean;
  reason: string;
  scale_x: number;
  scale_y: number;
  offset_x: number;
  offset_y: number;
  source_reference?: string | null;
  target_reference?: string | null;
  source_width?: number | null;
  source_height?: number | null;
  target_width?: number | null;
  target_height?: number | null;
};

const WIZARD_HISTORY_LIMIT = 50;

const clonePnidState = (state: PnidWizardState): PnidWizardState =>
  JSON.parse(JSON.stringify(state)) as PnidWizardState;

const wizardItemKey = (target: { kind: WizardSelectionKind; id: string }) =>
  `${target.kind}:${target.id}`;

const parseWizardItemKey = (key: string): { kind: WizardSelectionKind; id: string } | null => {
  const index = key.indexOf(":");
  if (index <= 0 || index === key.length - 1) return null;
  const kind = key.slice(0, index) as WizardSelectionKind;
  const id = key.slice(index + 1);
  if (
    kind !== "equipment" &&
    kind !== "equipmentInstrument" &&
    kind !== "nodeInstrument" &&
    kind !== "node" &&
    kind !== "controlLoop" &&
    kind !== "relationship"
  ) {
    return null;
  }
  return { kind, id };
};

const toBackendAnnotationTarget = (
  target: { kind: WizardSelectionKind; id: string }
): PnidAnnotationBackendTarget => {
  if (target.kind === "equipmentInstrument" || target.kind === "nodeInstrument") {
    return { kind: "instrument", id: target.id };
  }
  return { kind: target.kind, id: target.id } as PnidAnnotationBackendTarget;
};

const getInstrumentSelectionKind = (instrument: Pick<PnidInstrument, "context_type">): PnidInstrumentKind =>
  instrument.context_type === "node" ? "nodeInstrument" : "equipmentInstrument";

const getAllSavedAnnotationKeys = (state: PnidWizardState): Set<string> => {
  const keys = new Set<string>();
  for (const equipment of state.equipments) keys.add(wizardItemKey({ kind: "equipment", id: equipment.id }));
  for (const node of state.nodes) keys.add(wizardItemKey({ kind: "node", id: node.id }));
  for (const instrument of state.instruments) {
    keys.add(wizardItemKey({ kind: getInstrumentSelectionKind(instrument), id: instrument.id }));
  }
  for (const loop of state.control_loops) keys.add(wizardItemKey({ kind: "controlLoop", id: loop.id }));
  return keys;
};

const getNodeLineProperties = (node?: Partial<PnidNode> | null) => ({
  line_type: node?.line_type ?? "process",
  instrument_line_subtype:
    node?.line_type === "instrument" ? node.instrument_line_subtype ?? null : null,
  installation_type: node?.installation_type ?? null,
  is_jacketed: node?.is_jacketed ?? false,
  tracing_type: node?.tracing_type ?? null,
  flow_profile: node?.flow_profile ?? null,
  slope_ratio: node?.slope_ratio ?? null,
  is_two_phase: node?.is_two_phase ?? false,
  flow_direction: node?.flow_direction ?? null,
  primary_direction: node?.primary_direction ?? null,
});

const getNodeLinePropertyPayload = (
  values: Partial<LineMarkPopoverValues> | Record<string, any>
) => {
  const flowProfile = values.flow_profile ?? null;
  const flowDirection = values.flow_direction ?? null;

  return {
    installation_type: values.installation_type ?? null,
    is_jacketed: Boolean(values.is_jacketed),
    tracing_type: values.tracing_type ?? null,
    flow_profile: flowProfile,
    slope_ratio: flowProfile === "sloped" ? values.slope_ratio ?? null : null,
    is_two_phase: Boolean(values.is_two_phase),
    flow_direction: flowDirection,
    primary_direction:
      flowDirection === "bidirectional_primary"
        ? values.primary_direction ?? null
        : null,
  };
};

const getInstrumentLineNoToken = (value: unknown, fallback: string) => {
  const text = String(value ?? "").trim();
  return text ? text.replace(/\s+/g, "_") : fallback;
};

const getNodeLineTypePayload = (
  values: Partial<LineMarkPopoverValues> | Record<string, any>
) => {
  const lineType = (values.line_type ?? "process") as "process" | "instrument";
  const instrumentLineSubtype =
    lineType === "instrument" ? values.instrument_line_subtype ?? null : null;
  const lineNo = String(values.line_no ?? "").trim();

  return {
    line_no:
      lineType === "instrument" && !lineNo
        ? [
            getInstrumentLineNoToken(values.source_ref, "source"),
            getInstrumentLineNoToken(values.dest_ref, "destination"),
            getInstrumentLineNoToken(instrumentLineSubtype, "instrument"),
          ].join("_")
        : lineNo,
    line_type: lineType,
    instrument_line_subtype: instrumentLineSubtype,
  };
};

const PFD_EQUIPMENT_FIELD_KEYS = [
  "equipment_no",
  "equipment_type",
  "description",
  "notes",
  "connected_equipments",
  "linked_pnids",
] as const;

const PFD_NODE_FIELD_KEYS = [
  "line_no",
  "source_ref",
  "source_pnid",
  "dest_ref",
  "dest_pnid",
  "linked_pfd_streams",
  "instrument_line_subtype",
  "line_spec.description",
  "line_spec.continuity",
  "line_spec.phase",
  "line_spec.flow_rate",
  "line_spec.temperature",
  "line_spec.pressure",
] as const;

function buildPfdPayload(
  values: Record<string, any>,
  schema: FormSchemaDefinition | null,
  fallbackFieldKeys: readonly string[],
  baseBuckets: Record<string, Record<string, any>> = {}
) {
  const payload: Record<string, any> = {};
  const fieldKeys = schema?.sections.flatMap((section) => section.fields.map((field) => field.key)) ?? fallbackFieldKeys;
  for (const [bucket, bucketValue] of Object.entries(baseBuckets)) {
    payload[bucket] = { ...(bucketValue ?? {}) };
  }

  for (const key of fieldKeys) {
    if (!(key in values)) continue;
    const value = values[key];
    if (key.includes(".")) {
      const [bucket, subKey] = key.split(".", 2);
      payload[bucket] = { ...(payload[bucket] ?? {}), [subKey]: value };
    } else {
      payload[key] = value;
    }
  }

  return payload;
}

/** The equipment fields the mark dialog saves. The two flags say whether the dialog
 *  sent those arrays at all: an absent key leaves the server's mirrored links alone. */
function buildEquipmentUpdatePayload(
  values: Record<string, any>,
  connectionsChanged: boolean,
  pfdLinksChanged: boolean
): PnidEquipmentUpdate {
  return {
    equipment_no: values.equipment_no,
    equipment_type: values.equipment_type,
    subtype: values.subtype ?? null,
    description: values.description,
    pnid_no: values.pnid_no ?? null,
    painting_spec: values.painting_spec?.trim() || null,
    ...(values.is_primary_source !== undefined
      ? { is_primary_source: values.is_primary_source }
      : {}),
    // Empty names the drawing's own plant, so the save states the owner every
    // time rather than leaving the server to decide. A mark whose picker the
    // reviewer never touched carries nothing yet, and means the same.
    owner_domain_id: values.owner_domain_id ?? '',
    ...(values.notes !== undefined ? { notes: values.notes || null } : {}),
    ...(connectionsChanged
      ? { connected_equipments: values.connected_equipments }
      : {}),
    ...(pfdLinksChanged ? { linked_pfds: values.linked_pfds } : {}),
  };
}

const ENABLE_NODE_POINT_CLASSIFICATION = false;

type DrawTargetTone = "new" | "missing" | MarkingStatus;

interface DrawTargetInfo {
  label: string;
  detail?: string;
  tone?: DrawTargetTone;
}

const STEP_LABELS: Record<WizardStep, string> = {
  1: "P&ID Setup",
  2: "Instruments",
  3: "Mark Equipment",
  4: "Mark Lines",
  5: "Control Loops",
};

const STEP_ORDER: WizardStep[] = [1, 2, 3, 4, 5];

// Helper to check if two bounding boxes overlap
function bboxesOverlap(a: BoundingBox, b: BoundingBox): boolean {
  return a.x1 < b.x2 && a.x2 > b.x1 && a.y1 < b.y2 && a.y2 > b.y1;
}

// Helper to compute bounding box from polyline points
function polylineToBbox(points: number[], includePolygon: boolean = false): BoundingBox {
  if (points.length < 2) {
    return { x1: 0, y1: 0, x2: 0, y2: 0 };
  }
  const xs: number[] = [];
  const ys: number[] = [];
  for (let i = 0; i < points.length; i += 2) {
    xs.push(points[i]);
    ys.push(points[i + 1]);
  }
  const padding = 10;
  const bbox: BoundingBox = {
    x1: Math.min(...xs) - padding,
    y1: Math.min(...ys) - padding,
    x2: Math.max(...xs) + padding,
    y2: Math.max(...ys) + padding,
  };
  if (includePolygon) {
    bbox.polygon = [...points];
  }
  return bbox;
}

function nodeGeometryBbox(node: PnidNode | null | undefined): BoundingBox | null {
  if (!node) return null;
  if (node.is_duct) return node.duct_bbox ?? node.label_bbox ?? null;
  return node.polyline ? polylineToBbox(node.polyline) : node.label_bbox ?? null;
}

function shiftBbox(bbox: BoundingBox, dx: number, dy: number): BoundingBox {
  return {
    ...bbox,
    x1: bbox.x1 + dx,
    y1: bbox.y1 + dy,
    x2: bbox.x2 + dx,
    y2: bbox.y2 + dy,
    polygon: bbox.polygon?.map((value, index) => value + (index % 2 === 0 ? dx : dy)),
  };
}

function polylineToPoints(
  points: number[],
  minDistance: number = 2
): { x: number; y: number }[] {
  const output: { x: number; y: number }[] = [];
  for (let i = 0; i < points.length; i += 2) {
    const x = points[i];
    const y = points[i + 1];
    if (typeof x !== "number" || typeof y !== "number") continue;
    const last = output[output.length - 1];
    if (!last) {
      output.push({ x, y });
      continue;
    }
    const dist = Math.hypot(x - last.x, y - last.y);
    if (dist >= minDistance) {
      output.push({ x, y });
    }
  }
  return output;
}

function pointToBbox(
  point: { x: number; y: number },
  size: number = 18
): BoundingBox {
  const half = size / 2;
  return {
    x1: point.x - half,
    y1: point.y - half,
    x2: point.x + half,
    y2: point.y + half,
  };
}

type AnnotationCountLike = {
  equipments: number;
  nodes: number;
  instruments: number;
  controlLoops?: number;
  control_loops?: number;
  relationships: number;
};

function formatAnnotationCountSummary(counts: AnnotationCountLike): string {
  const controlLoops = counts.controlLoops ?? counts.control_loops ?? 0;
  return [
    { count: counts.equipments, singular: "equipment", plural: "equipment" },
    { count: counts.nodes, singular: "line", plural: "lines" },
    { count: counts.instruments, singular: "instrument", plural: "instruments" },
    { count: controlLoops, singular: "control loop", plural: "control loops" },
    { count: counts.relationships, singular: "relationship", plural: "relationships" },
  ]
    .filter((entry) => entry.count > 0)
    .map((entry) => `${entry.count} ${entry.count === 1 ? entry.singular : entry.plural}`)
    .join(", ");
}

function formatImportGeometryAdjustment(transform?: ImportGeometryTransform | null): string {
  if (!transform?.applied) return "";
  const sourceSize = transform.source_width && transform.source_height
    ? `${Math.round(transform.source_width)}x${Math.round(transform.source_height)}`
    : "";
  const targetSize = transform.target_width && transform.target_height
    ? `${Math.round(transform.target_width)}x${Math.round(transform.target_height)}`
    : "";
  if (sourceSize && targetSize) {
    return `Auto-scaled geometry from ${sourceSize} to ${targetSize}.`;
  }
  return "Auto-scaled geometry to match the target P&ID image.";
}

interface DrawingWorkspaceProps {
  /** The drawing being annotated. The shell owns the selection. */
  selectedDoc: PidDocument;
  /** Every drawing in the user's group, for hyperlinks and annotation import. */
  documents: PidDocument[];
  annotations: Record<string, AnnotationInfo>;
  effectiveUserName: string;
  isAdmin: boolean;
  /** Applies a field change to the shell's copy of a document. */
  onPatchDocument: (docId: string, patch: Partial<PidDocument>) => void;
  onOpenDocument: (
    doc: PidDocument,
    options?: { replace?: boolean; targetNodeId?: string | null; targetEquipmentId?: string | null },
  ) => void;
  onReloadDocuments: () => Promise<void>;
  onOpenFileManager: () => void;
  onClose: () => void;
}

/**
 * The drawing editor: canvas, marking wizard, review panels, and every dialog
 * that acts on one drawing. Mount it keyed on the document id — every piece of
 * per-drawing state below returns to its initial value by remounting.
 */
/**
 * Where the instrument editor opens: pinned to the top-right of the viewport, clear
 * of the sheet under it.
 *
 * It used to be anchored to the drawn box, which put a sheet coordinate into a
 * viewport position. On a full-size sheet those numbers run past the bottom and the
 * right of the window, so `DraggablePanel`'s clamp parked the dialog in the
 * bottom-right corner on every draw. The panel adds a small offset to this anchor and
 * clamps to its own padding, so the value here is what lands its right edge one
 * padding in from the window edge, and the top clamp gives the rest.
 */
/**
 * What identify would be asked about when a draft targets an existing element. Only
 * instrument drafts reach identify today, so the rest of this map records the element's
 * own nature rather than a call anyone makes.
 */
const IDENTIFY_KIND_BY_GEOMETRY_TARGET: Record<GeometryTargetKind, IdentifyAssetKind> = {
  equipment: 'equipment',
  equipmentInstrument: 'instrument',
  node: 'node',
  nodeInstrument: 'instrument',
  controlLoop: 'instrument',
};

function instrumentEditorPosition(): { x: number; y: number } {
  const PANEL_OFFSET = 16;
  return {
    x: Math.max(0, window.innerWidth - INSTRUMENT_EDITOR_WIDTH - PANEL_OFFSET * 2),
    y: 0,
  };
}


/** One action in the workspace's left rail; states styled in drawing-workspace-rail.css. */
function WorkspaceRailItem({ icon, label, sub, variant, iconOnly, flagged, title, ...buttonProps }: {
  icon: LucideIcon;
  label: string;
  /** Second line, e.g. the import-replace warning. */
  sub?: string;
  /** CTAs take the shared DS button recipe; plain items stay action buttons. */
  variant?: 'contained' | 'outlined';
  /** Secondary actions collapse to a glyph so the band stays one row. */
  iconOnly?: boolean;
  /** Marks a collapsed glyph that carries a warning, e.g. import-replace. */
  flagged?: boolean;
} & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  const button = (
    <button
      type="button"
      className={variant
        ? `pnid-btn pnid-btn--${variant} pnid-btn--sm pnid-ws-rail-cta`
        : iconOnly
        ? `pnid-ws-rail-item pnid-ws-rail-item--icon${flagged ? ' pnid-ws-rail-item--flagged' : ''}`
        : 'pnid-ws-rail-item'}
      aria-label={iconOnly ? label : undefined}
      /* MUI refuses a native title on a Tooltip child — the tooltip IS the
         title in icon-only mode, and the native one stays otherwise. */
      title={iconOnly ? undefined : title}
      {...buttonProps}
    >
      <AppIcon icon={icon} size={16} />
      {!iconOnly && (
        <span className="pnid-ws-rail-item-label">
          <span>{label}</span>
          {sub && <span className="pnid-ws-rail-item-sub">{sub}</span>}
        </span>
      )}
    </button>
  );
  /* An icon-only control must still name itself on hover and to a screen
     reader; the aria-label above covers AT, the tooltip covers sighted use. */
  if (!iconOnly) return button;
  return (
    <Tooltip title={title || (sub ? `${label} — ${sub}` : label)} placement="bottom">
      {button}
    </Tooltip>
  );
}

const DrawingWorkspace: React.FC<DrawingWorkspaceProps> = ({
  selectedDoc,
  documents,
  annotations,
  effectiveUserName,
  isAdmin,
  onPatchDocument,
  onOpenDocument,
  onReloadDocuments,
  onOpenFileManager,
  onClose,
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { index: domainIndex } = useDomainIndex();
  const invalidateRegisterContext = useInvalidateRegisterContext();
  const routeTargetNodeId = useMemo(() => parseTargetNodeId(location.search), [location.search]);
  const routeTargetEquipmentId = useMemo(() => parseTargetEquipmentId(location.search), [location.search]);
  const routeTargetInstrumentId = useMemo(() => parseTargetInstrumentId(location.search), [location.search]);
  const routeEditEquipmentId = useMemo(() => parseEditEquipmentId(location.search), [location.search]);
  const routeEditInstrumentId = useMemo(() => parseEditInstrumentId(location.search), [location.search]);
  /**
   * Drops one deep-link parameter the workspace has just acted on, so a reload
   * after the panel is closed shows the drawing as the reviewer left it.
   *
   * The drop waits a microtask because the step mirror below is a layout effect
   * that queues its own replace: queued first, it writes a search string built
   * before this drop and puts the parameter straight back. Going second costs
   * the `?step=` this drop cannot see yet, and the mirror writes that again on
   * the render the drop causes.
   */
  const consumeRouteParam = useCallback((param: string) => {
    const remaining = new URLSearchParams(location.search);
    remaining.delete(param);
    const query = remaining.toString();
    const href = `${location.pathname}${query ? `?${query}` : ""}`;
    queueMicrotask(() => navigate(href, { replace: true }));
  }, [location.pathname, location.search, navigate]);
  const [linkedDocumentStates, setLinkedDocumentStates] = useState<Record<string, PnidWizardState>>({});
  const [diagramMode, setDiagramMode] = useState<'pid' | 'pfd'>(
    selectedDoc.diagram_type === 'pfd' ? 'pfd' : 'pid'
  );
  const pfdEquipmentSchema = useFormSchema('pfd_equipment');
  const pfdNodeSchema = useFormSchema('pfd_node');

  // PNID Wizard state
  const [wizardStep, setWizardStep] = useState<WizardStep>(1);
  useStepTimer(wizardStep, selectedDoc?.id ?? null, STEP_LABELS);
  const { trackAction } = useActionTracker(wizardStep, selectedDoc?.id ?? null, STEP_LABELS);
  const [pnidState, setPnidState] = useState<PnidWizardState | null>(null);
  const [pnidStateLoadedDocId, setPnidStateLoadedDocId] = useState<string | null>(null);
  const lastAppliedStateRef = useRef<PnidWizardState | null>(null);
  const resolvedStepForDocRef = useRef<string | null>(null);

  const pnidStateQuery = useQuery({
    queryKey: queryKeys.pnidState(selectedDoc.id),
    queryFn: () => pnidApi.get(selectedDoc.id),
  });
  const { hasAIData, aiData } = useAiData(selectedDoc.id);
  const hasPlantModel = usePlantModelExists(selectedDoc.id);
  const groqEnabled = useGroqVisionStatus();
  const commentsQuery = useComments(selectedDoc.id);
  const comments = commentsQuery.comments;
  const templateAnnotationsQuery = useTemplateAnnotations(selectedDoc.id);
  const lastTemplateQueryDataRef = useRef<unknown>(null);

  const isBooting = pnidStateLoadedDocId !== selectedDoc.id;

  const selectedDocIdRef = useRef<string | null>(null);
  const lastRouteTargetNodeKeyRef = useRef<string | null>(null);
  const lastRouteTargetEquipmentKeyRef = useRef<string | null>(null);
  const lastRouteTargetInstrumentKeyRef = useRef<string | null>(null);
  const lastRouteEditEquipmentKeyRef = useRef<string | null>(null);
  const lastRouteEditInstrumentKeyRef = useRef<string | null>(null);
  const [selectedWizardItemKeys, setSelectedWizardItemKeys] = useState<Set<string>>(new Set());
  const [savedAnnotationEditModeSignal, setSavedAnnotationEditModeSignal] = useState(0);
  const [annotationImportAdjustment, setAnnotationImportAdjustment] =
    useState<AnnotationImportAdjustment | null>(null);
  const [undoStack, setUndoStack] = useState<PnidUndoEntry[]>([]);
  const [redoStack, setRedoStack] = useState<PnidUndoEntry[]>([]);
  const [historyApplying, setHistoryApplying] = useState(false);
  const [pnidNoDraft, setPnidNoDraft] = useState<string>("");
  const [pnidSetupReturnStep, setPnidSetupReturnStep] = useState<WizardStep | null>(null);
  const [activeEquipmentId, setActiveEquipmentId] = useState<string | null>(
    null
  );
  const [activeNodeId, setActiveNodeId] = useState<string | null>(null);
  const [assetRailTab, setAssetRailTab] = useState<'equipment' | 'instruments'>('equipment');
  useEffect(() => setAssetRailTab('equipment'), [selectedDoc.id]);
  /* Unmarked first by default, the order the context bar shipped: marking runs
     down the lines that still need it. Reviewing wants the opposite, hence the
     toggle on the rail. */
  const [nodeSortOrder, setNodeSortOrder] = useState<TargetSortOrder>('unmarked');
  const [selectedInstrumentId, setSelectedInstrumentId] = useState<string | null>(null);
  // Show only active item (true) or all markup (false) on canvas
  const [showActiveOnly, setShowActiveOnly] = useState<boolean>(false);
  const [activeControlLoopId, _setActiveControlLoopId] = useState<
    string | null
  >(null);
  const [focusBbox, setFocusBbox] = useState<BoundingBox | null>(null);
  // Review policy (pnid_review_policy.md) UI state
  const [reviewIssuesOpen, setReviewIssuesOpen] = useState(false);
  const [drawingContextOpen, setDrawingContextOpen] = useState(false);
  // Publication (publish the working copy as a drawing version)
  const [publishPanelOpen, setPublishPanelOpen] = useState(false);
  const [publishedVersion, setPublishedVersion] = useState<number | null>(null);
  const [reviewPolicyRefreshSignal, setReviewPolicyRefreshSignal] = useState(0);
  const [objectPolicyFindings, setObjectPolicyFindings] = useState<{
    ref: string;
    findings: ReviewPolicyFinding[];
  } | null>(null);
  const [loadingAI, setLoadingAI] = useState<boolean>(false);
  const [_selectedAIInstrumentTag, _setSelectedAIInstrumentTag] = useState<
    string | null
  >(null);
  const [pnidTitleDraft, setPnidTitleDraft] = useState<string>("");
  const [drawingClassDraft, setDrawingClassDraft] = useState<DrawingClass | "">("");
  const setupMutation = useSetPnidSetup(selectedDoc.id);
  const [markDialog, setMarkDialog] = useState<{
    isOpen: boolean;
    kind: PnidDialogKind;
    bbox: BoundingBox | null;
    anchor: { x: number; y: number } | null;
    editTarget?: { kind: PnidDialogKind; id: string } | null;
    initialValues?: Record<string, any>;
  }>({
    isOpen: false,
    kind: "equipment",
    bbox: null,
    anchor: null,
    editTarget: null,
    initialValues: {},
  });

  // Step 2 Template Annotation Identification State
  const [pendingTemplateItems, setPendingTemplateItems] = useState<PendingTemplateItem[]>([]);
  const [activeAnnotationId, setActiveAnnotationId] = useState<string | null>(null);
  const [unverifiedOnlyFilter, setUnverifiedOnlyFilter] = useState<boolean>(true);
  const [templateToast, setTemplateToast] = useState<{ message: string; type: 'success' | 'info' } | null>(null);
  const instrumentSelectionContextRef = useRef<{
    wizardStep: WizardStep;
    activeEquipmentId: string | null;
    activeNodeId: string | null;
    docId: string | null;
  }>({
    wizardStep,
    activeEquipmentId,
    activeNodeId,
    docId: selectedDoc?.id ?? null,
  });

  useEffect(() => {
    const previous = instrumentSelectionContextRef.current;
    const nextDocId = selectedDoc?.id ?? null;
    const shouldClearSelection =
      previous.wizardStep !== wizardStep ||
      previous.activeEquipmentId !== activeEquipmentId ||
      previous.activeNodeId !== activeNodeId ||
      previous.docId !== nextDocId;

    if (shouldClearSelection) {
      setSelectedInstrumentId(null);
    }

    instrumentSelectionContextRef.current = {
      wizardStep,
      activeEquipmentId,
      activeNodeId,
      docId: nextDocId,
    };
  }, [wizardStep, activeEquipmentId, activeNodeId, selectedDoc?.id]);

  /**
   * Open one instrument: the step that manages it, its parent context active,
   * the instrument selected and the canvas framed on it. Every path that goes
   * to an instrument — a drawing-context note, a deep link — goes through here,
   * so one instrument opens one way.
   *
   * The move also carries the selection guard's baseline above, because the
   * step and context change this makes is exactly what that guard treats as a
   * signal to drop the selection.
   */
  const focusInstrument = useCallback((instrumentId: string): boolean => {
    const instrument = pnidState?.instruments.find((candidate) => candidate.id === instrumentId);
    if (!instrument) return false;

    const context = getInstrumentEffectiveContext(instrument, pnidState);
    const step = stepForInstrumentContext(context);
    const nextEquipmentId = context.context_type === 'equipment' ? context.context_id : null;
    const nextNodeId = context.context_type === 'node' ? context.context_id : null;

    instrumentSelectionContextRef.current = {
      wizardStep: step,
      activeEquipmentId: nextEquipmentId,
      activeNodeId: nextNodeId,
      docId: selectedDoc?.id ?? null,
    };
    setWizardStep(step);
    setActiveEquipmentId(nextEquipmentId);
    setActiveNodeId(nextNodeId);
    setSelectedInstrumentId(instrument.id);
    setFocusBbox(instrument.bbox ?? null);
    return true;
  }, [pnidState, selectedDoc?.id]);

  /**
   * The regions collected so far and what they are being collected for. Armed by the
   * instrument sub-mode, by Rebox, and by "Add region" in the instrument editor; the
   * canvas appends to it on every drag and nothing is written until Done.
   */
  const [boxDraft, setBoxDraft] = useState<BoxDraft | null>(null);
  const instrumentContextOverrideRef = useRef<{ contextType: 'equipment' | 'node'; contextId: string } | null>(null);
  /** The existing element the armed draft redraws or extends. Absent while drawing something new. */
  const geometryTarget = boxDraft?.target ?? null;
  const handleInstrumentSubModeToggle = useCallback((active: boolean) => {
    setBoxDraft(active ? armDraft('instrument') : null);
    // Always clear context override when mode is toggled (either on or off)
    instrumentContextOverrideRef.current = null;
  }, []);

  // Unified instrument editor state (right-click any instrument on canvas)
  const [highlightedEntityId, setHighlightedEntityId] = useState<string | null>(null);
  const [instrumentEditorState, setInstrumentEditorState] = useState<{
    instrument: PnidInstrument;
    position: { x: number; y: number };
    isUsingLocalSnapshot?: boolean;
    latestLoadError?: string | null;
    /** The draw-time identify answer, set only when the editor opens on a drawn box. */
    identifyPrefill?: Record<string, unknown>;
  } | null>(null);

  const handleRightClickInstrument = useCallback(async (id: string, screenPos: { x: number; y: number }) => {
    if (!selectedDoc?.id) return;
    // Toggle off if clicking the same instrument
    if (selectedInstrumentId === id && instrumentEditorState) {
      setSelectedInstrumentId(null);
      setInstrumentEditorState(null);
      return;
    }
    const localInstrument = pnidState?.instruments.find((instrument) => instrument.id === id);
    setSelectedInstrumentId(id);
    if (localInstrument) {
      setInstrumentEditorState({
        instrument: localInstrument,
        position: screenPos,
        isUsingLocalSnapshot: true,
        latestLoadError: null,
      });
    }
    try {
      const instrument = await instrumentApi.getInstrument(selectedDoc.id, id);
      setInstrumentEditorState((prev) => {
        if (prev && prev.instrument.id !== id) return prev;
        return {
          instrument,
          position: prev?.position ?? screenPos,
          isUsingLocalSnapshot: false,
          latestLoadError: null,
        };
      });
    } catch (err) {
      console.error('Failed to fetch instrument for editor:', err);
      if (!localInstrument) {
        setSelectedInstrumentId((prev) => (prev === id ? null : prev));
      } else {
        setInstrumentEditorState((prev) =>
          prev?.instrument.id === id
            ? {
                ...prev,
                isUsingLocalSnapshot: true,
                latestLoadError: 'Could not load the latest backend copy. Editing is disabled until you close and retry.',
              }
            : prev
        );
      }
    }
  }, [selectedDoc?.id, selectedInstrumentId, instrumentEditorState, pnidState?.instruments]);

  const refreshPnidState = useCallback(async (docId: string) => {
    const refreshed = await pnidApi.get(docId);
    lastAppliedStateRef.current = refreshed;
    setPnidState(refreshed);
    setPnidStateLoadedDocId(docId);
    setSavedTemplateAnnotations(getTemplateAnnotationsFromInstruments(refreshed.instruments));
    queryClient.setQueryData(queryKeys.pnidState(docId), refreshed);
    return refreshed;
  }, [queryClient]);

  // After an annotator saves an object, fetch its policy findings and surface
  // them as a transient callout. Never blocks the save path.
  const checkObjectPolicy = useCallback(
    async (kind: 'equipment' | 'node' | 'instrument', objectId: string, refLabel: string) => {
      const docId = selectedDocIdRef.current;
      if (!docId) return;
      try {
        const report = await reviewPolicyApi.getReport(docId, { kind, objectId });
        setObjectPolicyFindings(
          report.findings.length ? { ref: refLabel, findings: report.findings } : null,
        );
        setReviewPolicyRefreshSignal((prev) => prev + 1);
      } catch (err) {
        console.warn('Review policy check failed:', err);
      }
    },
    [],
  );

  const handleInstrumentEditorSave = useCallback(async (id: string, updates: PnidInstrumentUpdate) => {
    if (!selectedDoc?.id) return;
    await instrumentApi.updateInstrument(selectedDoc.id, id, updates);
    const refreshed = await refreshPnidState(selectedDoc.id);

    setInstrumentEditorState(null);
    setSelectedInstrumentId(null);
    const saved = refreshed.instruments.find((inst) => inst.id === id);
    void checkObjectPolicy(
      'instrument',
      id,
      saved?.instrument_text || saved?.generated_id || 'instrument',
    );
  }, [checkObjectPolicy, refreshPnidState, selectedDoc?.id]);

  const handleInstrumentEditorDelete = useCallback(async (id: string) => {
    if (!selectedDoc?.id) return;
    await instrumentApi.deleteInstrument(selectedDoc.id, id);
    await refreshPnidState(selectedDoc.id);
    setInstrumentEditorState(null);
    setSelectedInstrumentId(null);
  }, [refreshPnidState, selectedDoc?.id]);

  const unifiedInstrumentRefs = useMemo(() => {
    return (pnidState?.instruments ?? []).map((inst) => ({
      id: inst.id,
      instrument_text: inst.instrument_text,
      generated_id: inst.generated_id,
      instrument_type: inst.instrument_type,
    }));
  }, [pnidState?.instruments]);

  const equipmentInstruments = useMemo(
    () => getEquipmentContextInstruments(pnidState),
    [pnidState],
  );
  const nodeInstruments = useMemo(
    () => getNodeContextInstruments(pnidState),
    [pnidState],
  );
  const directNodeInstruments = useMemo(
    () => getDirectNodeContextInstruments(pnidState),
    [pnidState],
  );

  // Draft attachments (no API until Save & Mark)
  const [draftAttachmentByContext, setDraftAttachmentByContext] = useState<Record<string, string[]>>({});
  const [draftDetachedByContext, setDraftDetachedByContext] = useState<Record<string, string[]>>({});

  const getDraftContextKey = useCallback((): string | null => {
    if (wizardStep === 3 && activeEquipmentId) return `equipment:${activeEquipmentId}`;
    if (wizardStep === 4 && activeNodeId) return `node:${activeNodeId}`;
    return null;
  }, [wizardStep, activeEquipmentId, activeNodeId]);

  const draftAttachedAnnotationIds = useMemo(() => {
    const key = getDraftContextKey();
    if (!key) return undefined;
    return new Set(draftAttachmentByContext[key] ?? []);
  }, [draftAttachmentByContext, getDraftContextKey]);
  const draftDetachedAnnotationIds = useMemo(() => {
    const key = getDraftContextKey();
    if (!key) return undefined;
    return new Set(draftDetachedByContext[key] ?? []);
  }, [draftDetachedByContext, getDraftContextKey]);

  // Draft attachment IDs for the active node (works in step 4)
  const activeNodeDraftAttachmentIds = useMemo(() => {
    if (!activeNodeId) return [];
    return draftAttachmentByContext[`node:${activeNodeId}`] ?? [];
  }, [activeNodeId, draftAttachmentByContext]);

  // Draft attachment IDs for the active equipment (works in step 3)
  const activeEquipmentDraftAttachmentIds = useMemo(() => {
    if (!activeEquipmentId) return [];
    return draftAttachmentByContext[`equipment:${activeEquipmentId}`] ?? [];
  }, [activeEquipmentId, draftAttachmentByContext]);

  // SME capture state for Step 2 template annotations
  const [templateSmeCapture, setTemplateSmeCapture] = useState<{
    annotationId: string;
    annotation: TemplateAnnotation;
    anchor: { x: number; y: number };
  } | null>(null);
  const [templateSmeCaptureInput, setTemplateSmeCaptureInput] = useState("");
  const [templateSmePopupPos, setTemplateSmePopupPos] = useState<{ x: number; y: number } | null>(null);
  const [isTemplateSmePopupDragging, setIsTemplateSmePopupDragging] = useState(false);
  const templateSmePopupDragOffset = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const templateSmeInputRef = useRef<HTMLInputElement>(null);
  
  /** Regions being identified, outlined on the canvas until their answer arrives. */
  const [identifyingBboxes, setIdentifyingBboxes] = useState<BoundingBox[]>([]);
  const [isNodeMarkingMode, setIsNodeMarkingMode] = useState(false);
  const [nodeDrawingShape, setNodeDrawingShape] = useState<'pipeline' | 'duct'>('pipeline');
  const [lineMarkPopover, setLineMarkPopover] = useState<{
    nodeId: string | null;
    position: { x: number; y: number };
    values: LineMarkPopoverValues;
    stage: "source" | "destination";
    isOpen: boolean;
    pendingDraw?: { bbox: BoundingBox; anchor: { x: number; y: number } };
    sourcePoint?: { x: number; y: number };
    destPoint?: { x: number; y: number };
    aiDetectedSource?: boolean;
    aiDetectedDest?: boolean;
  } | null>(null);

  const [lineSegmentReviewPopover, setLineSegmentReviewPopover] = useState<{
    nodeId: string;
    position: { x: number; y: number };
    hoveredDistinctId: string | null;
    selectedDistinctId: string | null;
  } | null>(null);

  useEffect(() => {
    if (!isNodeMarkingMode || !activeNodeId) return;
    const activeNode = pnidState?.nodes.find((node) => node.id === activeNodeId);
    if (activeNode) setNodeDrawingShape(activeNode.is_duct ? 'duct' : 'pipeline');
  }, [activeNodeId, isNodeMarkingMode, pnidState]);


  // Groq vision detection state
  const [isDetectingSource, setIsDetectingSource] = useState(false);
  const [isDetectingDest, setIsDetectingDest] = useState(false);
  const [nodePointCandidates, setNodePointCandidates] = useState<
    NodePointCandidate[]
  >([]);
  const nodePointRunId = useRef(0);
  const wizardStepRef = useRef<WizardStep>(wizardStep);

  // Comment mode state
  const [commentMode, setCommentMode] = useState(false);
  const [selectedCommentId, setSelectedCommentId] = useState<string | null>(
    null
  );
  const [showResolvedComments, _setShowResolvedComments] = useState(false);
  const [commentInputPosition, setCommentInputPosition] = useState<{
    x: number;
    y: number;
    canvasX: number;
    canvasY: number;
  } | null>(null);

  // Template crop state
  const [croppedImage, setCroppedImage] = useState<Blob | null>(null);
  
  // Template annotation state (for template matching feature)
  const [savedTemplateAnnotations, setSavedTemplateAnnotationsLocal] = useState<TemplateAnnotation[]>([]);
  const [unsavedMatches, setUnsavedMatches] = useState<Map<string, TemplateMatchResult[]>>(new Map());
  const [templateVisibility, setTemplateVisibility] = useState<Map<string, boolean>>(new Map());
  const [matchStats, setMatchStats] = useState<Map<string, MatchStats>>(new Map());
  const [selectedAnnotationIds, setSelectedAnnotationIds] = useState<Set<string>>(new Set());

  // Keep react-query's copy in step with local edits so remounting the same
  // drawing does not flash stale template overlays from an earlier fetch.
  const setSavedTemplateAnnotations = useCallback(
    (update: TemplateAnnotation[] | ((prev: TemplateAnnotation[]) => TemplateAnnotation[])) => {
      setSavedTemplateAnnotationsLocal((prev) => {
        const next = typeof update === 'function' ? update(prev) : update;
        queryClient.setQueryData(queryKeys.templateAnnotations(selectedDoc.id), {
          annotations: next,
          count: next.length,
        });
        lastTemplateQueryDataRef.current =
          queryClient.getQueryData(queryKeys.templateAnnotations(selectedDoc.id)) ?? {
            annotations: next,
            count: next.length,
          };
        return next;
      });
    },
    [queryClient, selectedDoc.id],
  );

  // Bulk identify progress per template (templateId -> progress)
  const [bulkIdentifyProgress, setBulkIdentifyProgress] = useState<Map<string, {
    total: number;
    completed: number;
    errors: number;
  }>>(new Map());

  // Per-template annotation counts for the template panel
  const savedAnnotationCounts = useMemo(() => {
    const counts = new Map<string, { unverified: number; total: number }>();
    for (const a of savedTemplateAnnotations) {
      const existing = counts.get(a.template_id) || { unverified: 0, total: 0 };
      existing.total++;
      if (a.status === 'unverified') existing.unverified++;
      counts.set(a.template_id, existing);
    }
    return counts;
  }, [savedTemplateAnnotations]);
  
  const [selectedCommentScreenPos, setSelectedCommentScreenPos] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const canvasContainerRef = useRef<HTMLDivElement>(null);
  const [canvasImageSize, setCanvasImageSize] = useState<{
    width: number;
    height: number;
  } | null>(null);

  // Intelligence View state
  const [viewMode, setViewMode] = useState<ViewMode>('annotation');
  const [plantModel, setPlantModel] = useState<PlantModel | null>(null);
  const [selectedEquipmentTag, setSelectedEquipmentTag] = useState<string | null>(null);
  const [showProcessLens, setShowProcessLens] = useState(true);
  const [showControlLens, setShowControlLens] = useState(true);
  const [showWalkthrough, setShowWalkthrough] = useState(false);
  const [walkthroughHighlights, setWalkthroughHighlights] = useState<WalkthroughHighlights | null>(null);

  // Draggable context bar state (default to right side)

  useEffect(() => {
    wizardStepRef.current = wizardStep;
  }, [wizardStep]);

  useEffect(() => {
    if (wizardStep !== 4 && wizardStep !== 5) {
      setLineSegmentReviewPopover(null);
    }
  }, [wizardStep]);

  // Load plant model when switching to intelligence mode
  useEffect(() => {
    if (viewMode === 'intelligence' && selectedDoc?.id && hasPlantModel && !plantModel) {
      pnidApi.getPlantModel(selectedDoc.id)
        .then(setPlantModel)
        .catch((err) => {
          console.error('Failed to load plant model:', err);
          setViewMode('annotation');
        });
    }
  }, [viewMode, selectedDoc?.id, hasPlantModel, plantModel]);

  // Reset intelligence view state when document changes
  useEffect(() => {
    setPlantModel(null);
    setSelectedEquipmentTag(null);
  }, [selectedDoc?.id]);

  // Seed local template overlays from the cached/network list.
  useEffect(() => {
    const data = templateAnnotationsQuery.data;
    if (!data || data === lastTemplateQueryDataRef.current) return;
    lastTemplateQueryDataRef.current = data;
    setSavedTemplateAnnotationsLocal(data.annotations);
    const visibility = new Map<string, boolean>();
    data.annotations.forEach((ann) => {
      visibility.set(ann.template_id, true);
    });
    setTemplateVisibility(visibility);
  }, [templateAnnotationsQuery.data]);

  const getDefaultLinePopoverPosition = useCallback(() => {
    const rect = canvasContainerRef.current?.getBoundingClientRect();
    if (!rect) return { x: 24, y: 120 };
    // Place near top-right of the canvas by default
    return { x: Math.max(24, rect.right - 440), y: rect.top + 80 };
  }, []);

  const handleCanvasImageSize = useCallback((width: number, height: number) => {
    setCanvasImageSize((prev) => {
      if (prev && prev.width === width && prev.height === height) return prev;
      return { width, height };
    });
  }, []);

  const segmentCoordinateInfo = useMemo(() => {
    const targetW = canvasImageSize?.width ?? selectedDoc?.width ?? null;
    const targetH = canvasImageSize?.height ?? selectedDoc?.height ?? null;
    if (!targetW || !targetH) return null;

    const page = aiData?.page_image;
    const aiW = page?.width_px;
    const aiH = page?.height_px;
    const rs = page?.render_scale;

    let sourceW: number | null = null;
    let sourceH: number | null = null;
    let sourceLabel = "ai";

    if (aiW && aiH) {
      sourceW = aiW;
      sourceH = aiH;
    } else if (aiData?.line_segments?.length) {
      let maxX = 0;
      let maxY = 0;
      for (const s of aiData.line_segments) {
        maxX = Math.max(maxX, s.x1, s.x2);
        maxY = Math.max(maxY, s.y1, s.y2);
      }
      if (maxX > 0 && maxY > 0) {
        sourceW = maxX;
        sourceH = maxY;
        sourceLabel = "ai_segments";
      }
    } else if (pnidState?.nodes?.length) {
      let maxX = 0;
      let maxY = 0;
      for (const n of pnidState.nodes) {
        const segs = n.line_segments ?? [];
        for (const s of segs) {
          maxX = Math.max(maxX, s.x1, s.x2);
          maxY = Math.max(maxY, s.y1, s.y2);
        }
      }
      if (maxX > 0 && maxY > 0) {
        sourceW = maxX;
        sourceH = maxY;
        sourceLabel = "segments";
      }
    }

    if (!sourceW || !sourceH) return null;

    // Heuristic: sometimes the `page_image.width_px/height_px` describes the rendered image size
    // but the extracted segment coordinates are in the *base* (pre-render_scale) coordinate space.
    // Detect this by looking at observed segment coordinate ranges in the current PNID state.
    if (aiW && aiH && pnidState?.nodes?.length && rs && rs > 1.1) {
      let maxX = 0;
      let maxY = 0;
      for (const n of pnidState.nodes) {
        const segs = n.line_segments ?? [];
        for (const s of segs) {
          maxX = Math.max(maxX, s.x1, s.x2);
          maxY = Math.max(maxY, s.y1, s.y2);
        }
      }
      const ratioX = maxX / aiW;
      const ratioY = maxY / aiH;
      const ratio = Math.max(ratioX, ratioY);
      if (Number.isFinite(ratio) && ratio > 0 && ratio < 0.65) {
        sourceW = sourceW / rs;
        sourceH = sourceH / rs;
      }
    }

    let sx = 1;
    let sy = 1;
    if (targetW && targetH) {
      sx = targetW / sourceW;
      sy = targetH / sourceH;
    } else if (page?.render_scale && page.render_scale > 0) {
      sx = 1 / page.render_scale;
      sy = 1 / page.render_scale;
    }

    // Guardrails against clearly-wrong scaling
    const clamp = (v: number) =>
      Number.isFinite(v) ? Math.min(8, Math.max(0.05, v)) : 1;
    sx = clamp(sx);
    sy = clamp(sy);

    return {
      sx,
      sy,
      source: { width_px: sourceW, height_px: sourceH, render_scale: rs },
      source_label: sourceLabel,
      target:
        targetW && targetH
          ? { width_px: targetW, height_px: targetH }
          : undefined,
    };
  }, [
    aiData?.page_image,
    aiData?.line_segments,
    canvasImageSize?.width,
    canvasImageSize?.height,
    selectedDoc?.width,
    selectedDoc?.height,
    pnidState?.nodes,
  ]);

  // Groq vision detection callbacks
  const handleDetectSource = useCallback(async () => {
    if (!selectedDoc || !lineMarkPopover?.sourcePoint) return;
    setIsDetectingSource(true);
    trackAction('vlm_detection_triggered', { endpoint_type: 'source' });
    const vlmStart = Date.now();
    try {
      const result = await pnidApi.detectEndpoint(
        selectedDoc.id,
        lineMarkPopover.sourcePoint,
        "source"
      );
      trackAction('vlm_detection_completed', {
        endpoint_type: 'source',
        latency_ms: Date.now() - vlmStart,
        detected: !!(result.enabled && result.result?.ref),
      });
      if (result.enabled && result.result?.ref) {
        setLineMarkPopover((prev) =>
          prev
            ? {
                ...prev,
                values: {
                  ...prev.values,
                  source_ref: result.result!.ref,
                  // Use detected pnid if available, otherwise keep current (default) pnid
                  source_pnid: result.result!.pnid || prev.values.source_pnid,
                },
                aiDetectedSource: true,
              }
            : prev
        );
      }
    } catch (err) {
      console.error("Groq source detection failed:", err);
      trackAction('vlm_detection_completed', {
        endpoint_type: 'source',
        latency_ms: Date.now() - vlmStart,
        detected: false,
        error: true,
      });
    } finally {
      setIsDetectingSource(false);
    }
  }, [selectedDoc, lineMarkPopover?.sourcePoint, trackAction]);

  const handleDetectDest = useCallback(async () => {
    if (!selectedDoc || !lineMarkPopover?.destPoint) return;
    setIsDetectingDest(true);
    trackAction('vlm_detection_triggered', { endpoint_type: 'destination' });
    const vlmStart = Date.now();
    try {
      const result = await pnidApi.detectEndpoint(
        selectedDoc.id,
        lineMarkPopover.destPoint,
        "destination"
      );
      trackAction('vlm_detection_completed', {
        endpoint_type: 'destination',
        latency_ms: Date.now() - vlmStart,
        detected: !!(result.enabled && result.result?.ref),
      });
      if (result.enabled && result.result?.ref) {
        setLineMarkPopover((prev) =>
          prev
            ? {
                ...prev,
                values: {
                  ...prev.values,
                  dest_ref: result.result!.ref,
                  // Use detected pnid if available, otherwise keep current (default) pnid
                  dest_pnid: result.result!.pnid || prev.values.dest_pnid,
                },
                aiDetectedDest: true,
              }
            : prev
        );
      }
    } catch (err) {
      console.error("Groq destination detection failed:", err);
      trackAction('vlm_detection_completed', {
        endpoint_type: 'destination',
        latency_ms: Date.now() - vlmStart,
        detected: false,
        error: true,
      });
    } finally {
      setIsDetectingDest(false);
    }
  }, [selectedDoc, lineMarkPopover?.destPoint, trackAction]);

  useEffect(() => {
    if (wizardStep !== 4) {
      setLineMarkPopover(null);
    }
  }, [wizardStep]);

  const handleLinePathStart = useCallback(
    (
      screenPosition: { x: number; y: number },
      canvasPosition: { x: number; y: number }
    ) => {
      if (wizardStepRef.current !== 4) return;
      if (!pnidState) return;

      // Auto-detect source entity based on where user started drawing
      const detectedSource = detectOverlappingEntity(
        canvasPosition,
        pnidState.equipments,
        pnidState.nodes,
        { bboxThreshold: 60, polylineThreshold: 40 }
      );

      // Flag for triggering Groq fallback
      const needsGroqFallback = !detectedSource && groqEnabled;

      setLineMarkPopover((prev) => {
        if (prev)
          return {
            ...prev,
            position: screenPosition,
            sourcePoint: canvasPosition,
          };

        const selectedNode = activeNodeId
          ? pnidState.nodes.find((n) => n.id === activeNodeId)
          : null;
        const bindToSelectedNode = Boolean(
          selectedNode &&
            (isNodeMarkingMode || selectedNode.status === "ai_identified")
        );

        if (selectedNode && bindToSelectedNode) {
          const defaultLineNo = selectedNode.line_no?.trim()
            ? selectedNode.line_no
            : "";
          // Use existing node data, but auto-fill source if not already set
          const autoSource =
            !selectedNode.source_ref?.trim() && detectedSource
              ? detectedSource.ref
              : selectedNode.source_ref;
          return {
            nodeId: selectedNode.id,
            position: screenPosition,
            sourcePoint: canvasPosition,
            values: {
              line_no: defaultLineNo ?? "",
              source_ref: autoSource ?? "",
              source_pnid: selectedNode.source_pnid ?? pnidState.pnid_no ?? "",
              dest_ref: selectedNode.dest_ref ?? "",
              dest_pnid: selectedNode.dest_pnid ?? pnidState.pnid_no ?? "",
              ...getNodeLineProperties(selectedNode),
              drain_type: selectedNode.drain_type ?? null,
              is_duct: false,
            },
            stage: "source",
            isOpen: true,
            aiDetectedSource: false,
          };
        }

        // New line - auto-fill source from detection
        return {
          nodeId: null,
          position: screenPosition,
          sourcePoint: canvasPosition,
          values: {
            line_no: "",
            source_ref: detectedSource?.ref ?? "",
            source_pnid: pnidState.pnid_no ?? "",
            dest_ref: "",
            dest_pnid: pnidState.pnid_no ?? "",
            ...getNodeLineProperties(null),
            drain_type: null,
            is_duct: false,
          },
          stage: "source",
          isOpen: true,
          aiDetectedSource: Boolean(detectedSource),
        };
      });

      // Trigger Groq fallback if statistical detection failed
      if (needsGroqFallback && selectedDoc) {
        setIsDetectingSource(true);
        pnidApi
          .detectEndpoint(selectedDoc.id, canvasPosition, "source")
          .then((result) => {
            if (result.enabled && result.result?.ref) {
              setLineMarkPopover((prev) =>
                prev && !prev.values.source_ref
                  ? {
                      ...prev,
                      values: {
                        ...prev.values,
                        source_ref: result.result!.ref,
                        // Use detected pnid if available, otherwise keep current (default) pnid
                        source_pnid:
                          result.result!.pnid || prev.values.source_pnid,
                      },
                      aiDetectedSource: true,
                    }
                  : prev
              );
            }
          })
          .catch((err) => console.error("Groq source detection failed:", err))
          .finally(() => setIsDetectingSource(false));
      }
    },
    [pnidState, activeNodeId, isNodeMarkingMode, groqEnabled, selectedDoc]
  );

  const handleNodeDrawingShapeChange = useCallback(async (shape: 'pipeline' | 'duct') => {
    if (diagramMode !== 'pid') return;
    if (shape === nodeDrawingShape) return;
    const selectedNode = activeNodeId
      ? pnidState?.nodes.find((node) => node.id === activeNodeId) ?? null
      : null;
    const activeNode = selectedNode && (isNodeMarkingMode || selectedNode.status === 'ai_identified')
      ? selectedNode
      : null;
    const hasIncompatibleGeometry = Boolean(
      activeNode && (shape === 'duct' ? activeNode.polyline : activeNode.duct_bbox)
    );
    if (hasIncompatibleGeometry) {
      const confirmed = window.confirm(
        `Change this marked ${activeNode?.is_duct ? 'duct' : 'pipeline'} to a ${shape}? Its current geometry will be cleared and must be redrawn.`
      );
      if (!confirmed || !selectedDoc || !activeNode) return;
      try {
        const updated = await pnidApi.updateNode(selectedDoc.id, activeNode.id, {
          is_duct: shape === 'duct',
          polyline: null,
          duct_bbox: null,
          status: 'ai_identified',
        });
        setPnidState((prev) => prev ? {
          ...prev,
          nodes: prev.nodes.map((node) => node.id === updated.id ? updated : node),
        } : prev);
        setFocusBbox(null);
        setIsNodeMarkingMode(true);
        setCanvasClearSignal((value) => value + 1);
      } catch (error) {
        console.error('Failed to change node geometry mode:', error);
        showAlert('Mode change failed', 'Could not clear the existing geometry. Please try again.', 'error');
        return;
      }
    } else if (activeNode && selectedDoc && Boolean(activeNode.is_duct) !== (shape === 'duct')) {
      const updated = await pnidApi.updateNode(selectedDoc.id, activeNode.id, { is_duct: shape === 'duct' });
      setPnidState((prev) => prev ? {
        ...prev,
        nodes: prev.nodes.map((node) => node.id === updated.id ? updated : node),
      } : prev);
    }
    setNodeDrawingShape(shape);
    setLineMarkPopover(null);
    setCanvasClearSignal((value) => value + 1);
  }, [activeNodeId, diagramMode, isNodeMarkingMode, nodeDrawingShape, pnidState, selectedDoc]);

  const finalizeLineFromPopover = useCallback(async () => {
    if (!selectedDoc) return;
    if (!pnidState) return;
    if (!lineMarkPopover?.pendingDraw) return;

    const { bbox, anchor } = lineMarkPopover.pendingDraw;
    const boundNode = lineMarkPopover.nodeId
      ? pnidState.nodes.find((n) => n.id === lineMarkPopover.nodeId) ?? null
      : null;

    if (isNodeMarkingMode && boundNode) {
      try {
        const polyline = (bbox as any).polygon || [
          bbox.x1,
          bbox.y1,
          bbox.x2,
          bbox.y2,
        ];
        const v = lineMarkPopover.values;
        const isDuct = Boolean(v.is_duct);
        await pnidApi.updateNode(selectedDoc.id, boundNode.id, {
          is_duct: isDuct,
          duct_bbox: isDuct ? bbox : null,
          polyline: isDuct ? null : polyline,
          status: "in_progress",
          source_ref: v.source_ref?.trim() ?? "",
          source_pnid: v.source_pnid?.trim() ?? "",
          dest_ref: v.dest_ref?.trim() ?? "",
          dest_pnid: v.dest_pnid?.trim() ?? "",
          ...getNodeLineTypePayload(v),
          ...getNodeLinePropertyPayload(v),
          drain_type: v.drain_type ?? null,
        });
        const newState = await pnidApi.get(selectedDoc.id);
        setPnidState(newState);
        setIsNodeMarkingMode(false);
        setNodeDrawingShape('pipeline');
        setLineMarkPopover(null);
        closeMarkDialog();
        setCanvasClearSignal((prev) => prev + 1);
        if (!isDuct) void runNodePointClassification(boundNode.id, polyline);
        showAlert(
          "Line Marked",
          `Node #${boundNode.node_no} ${isDuct ? 'duct box' : 'path'} saved. Click on the node or use "Edit" to update details.`,
          "success"
        );
      } catch (err) {
        console.error("Failed to mark node:", err);
        showAlert(
          "Error",
          "Failed to mark the line. Please try again.",
          "error"
        );
      }
      return;
    }

    if (boundNode && boundNode.status === "ai_identified") {
      const v = lineMarkPopover.values;
      openMarkDialog("node", bbox, anchor, {
        node_no: boundNode.node_no,
        line_no: v.line_no,
        source_ref: v.source_ref,
        source_pnid: v.source_pnid,
        dest_ref: v.dest_ref,
        dest_pnid: v.dest_pnid,
        ...getNodeLinePropertyPayload(v),
        drain_type: v.drain_type ?? boundNode.drain_type ?? null,
        note: boundNode.note || "",
        line_spec: boundNode.line_spec,
        is_duct: Boolean(v.is_duct),
        _markExistingId: boundNode.id,
        _from_line_wizard: true,
      });
      setLineMarkPopover(null);
      return;
    }

    const maxNodeNo = pnidState.nodes.reduce(
      (max, n) => Math.max(max, n.node_no),
      0
    );
    const v = lineMarkPopover.values;
    openMarkDialog("node", bbox, anchor, {
      node_no: maxNodeNo + 1,
      line_no: v.line_no,
      source_ref: v.source_ref,
      source_pnid: v.source_pnid,
      dest_ref: v.dest_ref,
      dest_pnid: v.dest_pnid,
      ...getNodeLinePropertyPayload(v),
      drain_type: v.drain_type ?? null,
      is_duct: Boolean(v.is_duct),
      _from_line_wizard: true,
    });
    setLineMarkPopover(null);
  }, [
    selectedDoc,
    pnidState,
    lineMarkPopover,
    activeNodeId,
    isNodeMarkingMode,
  ]);

  const handlePendingMarkBboxUpdate = (bbox: BoundingBox) => {
    setMarkDialog((prev) => (prev.isOpen ? { ...prev, bbox } : prev));
  };

  // Alert dialog state
  const [alertDialog, setAlertDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: "success" | "error" | "info" | "warning";
  }>({
    isOpen: false,
    title: "",
    message: "",
    type: "info",
  });

  // Confirm dialog state (for delete confirmations)
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    type?: "danger" | "warning" | "info";
    confirmText?: string;
  }>({
    isOpen: false,
    title: "",
    message: "",
    onConfirm: () => {},
    type: "danger",
    confirmText: "Confirm",
  });

  // Modal states
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [versionHistoryOpen, setVersionHistoryOpen] = useState(false);
  const [previewingVersion, setPreviewingVersion] = useState<number | null>(
    null
  );
  // DECISION(2026-09-08, Abhishek): Review saved publishable assets on demand after marking.
  // An annotation touch pauses the list until the SME refreshes the saved snapshot.
  const publishableAssets = usePublishableAssets(
    selectedDoc.id, isBooting ? null : pnidState,
    !isBooting && wizardStep === 3 && previewingVersion === null,
  );
  const [originalStateBeforePreview, setOriginalStateBeforePreview] =
    useState<PnidWizardState | null>(null);
  // nodeOverviewOpen removed - consolidated into PnidMarkDialog
  // equipmentOverviewOpen removed - consolidated into PnidMarkDialog
  const [stepChangeConfirm, setStepChangeConfirm] = useState<{
    isOpen: boolean;
    targetStep: WizardStep | null;
  }>({
    isOpen: false,
    targetStep: null,
  });
  const [canvasHasDraft, setCanvasHasDraft] = useState(false);
  const [canvasClearSignal, setCanvasClearSignal] = useState(0);
  const [annotationImportOpen, setAnnotationImportOpen] = useState(false);
  const [annotationImportSourceId, setAnnotationImportSourceId] = useState("");
  const [annotationImportSearch, setAnnotationImportSearch] = useState("");
  const [annotationImporting, setAnnotationImporting] = useState(false);

  // Get annotation for a document
  const getAnnotation = (docId: string): AnnotationInfo | null =>
    annotations[docId] || null;

  const loadLinkedDocumentState = useCallback(async (docId: string) => {
    if (linkedDocumentStates[docId]) return;
    const state = await pnidApi.get(docId);
    setLinkedDocumentStates((prev) => ({ ...prev, [docId]: state }));
  }, [linkedDocumentStates]);

  // Group-based editing model:
  // - can_edit controls annotation edits.
  // - can_review controls Mark as Reviewed.
  // - raven_admin remains full-access.
  const userCanEdit = user?.permissions?.can_edit ?? !user?.is_reviewer;
  const readOnlyWizard = !isAdmin && !userCanEdit;

  const targetAnnotationCounts = useMemo(() => ({
    equipments: pnidState?.equipments.length ?? 0,
    nodes: pnidState?.nodes.length ?? 0,
    instruments: pnidState?.instruments.length ?? 0,
    controlLoops: pnidState?.control_loops.length ?? 0,
    relationships: pnidState?.instrument_relationships.length ?? 0,
  }), [pnidState]);

  const targetStateReady = Boolean(
    selectedDoc?.id &&
    pnidState &&
    pnidStateLoadedDocId === selectedDoc.id
  );
  const targetHasAnnotations = targetStateReady && Object.values(targetAnnotationCounts).some((count) => count > 0);
  const targetAnnotationSummary = useMemo(
    () => formatAnnotationCountSummary(targetAnnotationCounts),
    [targetAnnotationCounts],
  );
  const annotationImportDisabledReason = !targetStateReady
    ? "Loading target annotations..."
    : "";
  const annotationImportDisabled = Boolean(annotationImportDisabledReason) || annotationImporting;

  const annotationImportSources = useMemo(() => {
    const query = annotationImportSearch.trim();
    return documents
      .filter((doc) => doc.id !== selectedDoc?.id)
      .filter((doc) => getDocumentDiagramType(doc) === diagramMode)
      .filter((doc) => documentMatchesSearch(doc, getAnnotation(doc.id), query))
      .sort((a, b) => {
        const domainCompare = documentDomainPathLabel(domainIndex, a).localeCompare(
          documentDomainPathLabel(domainIndex, b),
        );
        if (domainCompare !== 0) return domainCompare;
        return getDocumentPrimaryLabel(a).localeCompare(getDocumentPrimaryLabel(b));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [annotationImportSearch, documents, selectedDoc?.id, diagramMode, annotations]);
  const selectedAnnotationImportSource = useMemo(
    () => documents.find((doc) => doc.id === annotationImportSourceId) ?? null,
    [annotationImportSourceId, documents],
  );

  const showAlert = (
    title: string,
    message: string,
    type: "success" | "error" | "info" | "warning" = "info"
  ) => {
    setAlertDialog({
      isOpen: true,
      title,
      message,
      type,
    });
  };

  const closeAlert = () => {
    setAlertDialog((prev) => ({ ...prev, isOpen: false }));
  };

  const applyPnidStateLocally = useCallback((
    state: PnidWizardState,
    options?: { clearSelection?: boolean },
  ) => {
    setPnidState(state);
    setPnidStateLoadedDocId(state.doc_id);
    setSavedTemplateAnnotations(getTemplateAnnotationsFromInstruments(state.instruments));
    if (options?.clearSelection !== false) {
      setSelectedWizardItemKeys(new Set());
    }
  }, []);

  const pushPnidUndoSnapshot = useCallback((label: string) => {
    if (!selectedDoc?.id || !pnidState || pnidStateLoadedDocId !== selectedDoc.id) return false;
    const entry: PnidUndoEntry = {
      docId: selectedDoc.id,
      label,
      state: clonePnidState(pnidState),
    };
    setUndoStack((prev) => [...prev.slice(-(WIZARD_HISTORY_LIMIT - 1)), entry]);
    setRedoStack([]);
    return true;
  }, [pnidState, pnidStateLoadedDocId, selectedDoc?.id]);

  const handleOpenPnidHyperlink = useCallback(async (
    link: PnidHyperlinkRef,
    options?: { newTab?: boolean },
  ) => {
    if (!link.pnid_doc_id) return;
    try {
      const doc = documents.find((candidate) => candidate.id === link.pnid_doc_id)
        ?? await documentApi.get(link.pnid_doc_id);
      if (options?.newTab !== false) {
        window.open(
          buildPnidPath(doc.id, { targetNodeId: link.target_node_id ?? null }),
          '_blank',
          'noopener,noreferrer',
        );
        return;
      }
      setInstrumentEditorState(null);
      setSelectedInstrumentId(null);
      setHighlightedEntityId(null);
      onOpenDocument(doc, { targetNodeId: link.target_node_id ?? null });
    } catch (error) {
      console.error("Failed to open linked P&ID:", error);
      showAlert("Linked P&ID unavailable", "The linked P&ID could not be opened.", "warning");
    }
  }, [documents, onOpenDocument]);

  const handleOpenLinkedDocument = useCallback(async (
    target: {
      docId: string;
      targetEquipmentId?: string | null;
      targetNodeId?: string | null;
      newTab?: boolean;
    },
  ) => {
    if (!target.docId) return;
    try {
      const doc = documents.find((candidate) => candidate.id === target.docId)
        ?? await documentApi.get(target.docId);
      const targetPath = buildPnidPath(doc.id, {
        targetEquipmentId: target.targetEquipmentId ?? null,
        targetNodeId: target.targetNodeId ?? null,
      });
      if (target.newTab !== false) {
        window.open(targetPath, '_blank', 'noopener,noreferrer');
        return;
      }
      setInstrumentEditorState(null);
      setSelectedInstrumentId(null);
      setHighlightedEntityId(null);
      onOpenDocument(doc, {
        targetEquipmentId: target.targetEquipmentId ?? null,
        targetNodeId: target.targetNodeId ?? null,
      });
    } catch (error) {
      console.error("Failed to open linked document:", error);
      showAlert("Linked document unavailable", "The linked document could not be opened.", "warning");
    }
  }, [documents, onOpenDocument]);

  const handleDiagramModeChange = useCallback(async (nextMode: 'pid' | 'pfd') => {
    if (readOnlyWizard) return;
    if (!selectedDoc || nextMode === diagramMode || markDialog.isOpen) return;

    const previousMode = diagramMode;
    setDiagramMode(nextMode);
    onPatchDocument(selectedDoc.id, { diagram_type: nextMode });

    try {
      await pnidApi.setDiagramType(selectedDoc.id, nextMode);
    } catch (error) {
      console.error("Failed to set diagram type:", error);
      setDiagramMode(previousMode);
      onPatchDocument(selectedDoc.id, { diagram_type: previousMode });
      showAlert("Mode update failed", "Could not persist the diagram mode.", "error");
    }
  }, [diagramMode, markDialog.isOpen, onPatchDocument, selectedDoc, readOnlyWizard]);

  const renderDiagramModeToggle = useCallback(() => (
    <div
      role="group"
      aria-label="Diagram mode"
      style={{
        display: "inline-grid",
        gridTemplateColumns: "1fr 1fr",
        gap: 2,
        padding: 2,
        borderRadius: 5,
        border: "1px solid rgba(0, 0, 0, 0.08)",
        backgroundColor: "#f8fafc",
        opacity: markDialog.isOpen || readOnlyWizard ? 0.55 : 1,
      }}>
      {(["pid", "pfd"] as const).map((mode) => {
        const active = diagramMode === mode;
        return (
          <button
            key={mode}
            type="button"
            onClick={() => void handleDiagramModeChange(mode)}
            disabled={markDialog.isOpen || active || readOnlyWizard}
            style={{
              border: "none",
              borderRadius: 4,
              padding: "4px 8px",
              minHeight: 24,
              backgroundColor: active ? "var(--raven-primary-main)" : "transparent",
              color: active ? "#fff" : "var(--raven-text-secondary)",
              cursor: markDialog.isOpen || active || readOnlyWizard ? "default" : "pointer",
              fontSize: 12,
              fontWeight: 700,
            }}>
            {mode.toUpperCase()}
          </button>
        );
      })}
    </div>
  ), [diagramMode, handleDiagramModeChange, markDialog.isOpen, readOnlyWizard]);

  // Prevent browser zoom (Ctrl+scroll / pinch) on the entire page except canvas
  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      if (e.ctrlKey || e.metaKey) {
        const target = e.target as HTMLElement;
        const isCanvasArea =
          target.closest("canvas") || target.closest(".konvajs-content");
        if (!isCanvasArea) {
          e.preventDefault();
        }
      }
    };

    document.addEventListener("wheel", handleWheel, { passive: false });
    return () => document.removeEventListener("wheel", handleWheel);
  }, []);

  useEffect(() => {
    selectedDocIdRef.current = selectedDoc?.id ?? null;
  }, [selectedDoc?.id]);

  useEffect(() => {
    if (!routeTargetNodeId) {
      lastRouteTargetNodeKeyRef.current = null;
      return;
    }
    if (!selectedDoc?.id || !pnidState || pnidStateLoadedDocId !== selectedDoc.id) {
      return;
    }

    const focusKey = `${selectedDoc.id}:node:${routeTargetNodeId}`;
    if (lastRouteTargetNodeKeyRef.current === focusKey) {
      return;
    }
    lastRouteTargetNodeKeyRef.current = focusKey;

    const node = pnidState.nodes.find((candidate) => candidate.id === routeTargetNodeId);
    if (!node) {
      console.warn("Linked P&ID target line not found:", routeTargetNodeId);
      return;
    }

    setWizardStep(4);
    setActiveNodeId(node.id);
    setFocusBbox(nodeGeometryBbox(node));
  }, [pnidState, pnidStateLoadedDocId, routeTargetNodeId, selectedDoc?.id]);

  useEffect(() => {
    if (!routeTargetEquipmentId) {
      lastRouteTargetEquipmentKeyRef.current = null;
      return;
    }
    if (!selectedDoc?.id || !pnidState || pnidStateLoadedDocId !== selectedDoc.id) {
      return;
    }

    const focusKey = `${selectedDoc.id}:equipment:${routeTargetEquipmentId}`;
    if (lastRouteTargetEquipmentKeyRef.current === focusKey) {
      return;
    }
    lastRouteTargetEquipmentKeyRef.current = focusKey;

    const equipment = pnidState.equipments.find((candidate) => candidate.id === routeTargetEquipmentId);
    if (!equipment) {
      console.warn("Linked target equipment not found:", routeTargetEquipmentId);
      return;
    }

    setWizardStep(3);
    setActiveEquipmentId(equipment.id);
    setFocusBbox(equipment.bbox ?? equipment.label_bbox ?? null);
  }, [pnidState, pnidStateLoadedDocId, routeTargetEquipmentId, selectedDoc?.id]);

  useEffect(() => {
    if (!routeTargetInstrumentId) {
      lastRouteTargetInstrumentKeyRef.current = null;
      return;
    }
    if (!selectedDoc?.id || !pnidState || pnidStateLoadedDocId !== selectedDoc.id) {
      return;
    }

    const focusKey = `${selectedDoc.id}:instrument:${routeTargetInstrumentId}`;
    if (lastRouteTargetInstrumentKeyRef.current === focusKey) {
      return;
    }
    lastRouteTargetInstrumentKeyRef.current = focusKey;

    // One link, one target. A higher-ranked target owns the step and the
    // viewport, and acting here as well would land on neither. `edit_instrument_id`
    // ranks with this one and does everything it does, so it wins too.
    if (
      winningRouteTarget({
        routeEditEquipmentId,
        routeTargetEquipmentId,
        routeTargetNodeId,
        routeEditInstrumentId,
        routeTargetInstrumentId,
        urlStep: null,
      }) !== 'instrument'
      || routeEditInstrumentId
    ) {
      console.warn(
        "target_instrument_id ignored: edit_instrument_id/edit_equipment_id/target_equipment_id/target_node_id takes precedence",
      );
      return;
    }

    if (!focusInstrument(routeTargetInstrumentId)) {
      console.warn("Linked target instrument not found:", routeTargetInstrumentId);
    }
  }, [
    focusInstrument,
    pnidState,
    pnidStateLoadedDocId,
    routeEditEquipmentId,
    routeEditInstrumentId,
    routeTargetEquipmentId,
    routeTargetInstrumentId,
    routeTargetNodeId,
    selectedDoc?.id,
  ]);

  useEffect(() => {
    setObjectPolicyFindings(null);
    setReviewIssuesOpen(false);
  }, [selectedDoc.id]);

  useEffect(() => {
    if (!activeNodeId) {
      setNodePointCandidates([]);
      return;
    }
    setNodePointCandidates((prev) =>
      prev.filter((candidate) => candidate.nodeId === activeNodeId)
    );
  }, [activeNodeId]);

  // Server → local: apply only when the query hands us a new reference that we
  // have not already applied. Write-back sets lastAppliedStateRef to the cache
  // copy react-query returns (structural sharing), so a local edit does not
  // re-enter here and wipe selection / Setup drafts.
  useEffect(() => {
    const state = pnidStateQuery.data;
    if (!state || state === lastAppliedStateRef.current) return;
    if (state.doc_id !== selectedDoc.id) return;
    const isFirstApplyForDoc = resolvedStepForDocRef.current !== selectedDoc.id;
    lastAppliedStateRef.current = state;
    applyPnidStateLocally(state, { clearSelection: isFirstApplyForDoc });
    if (!isFirstApplyForDoc) return;

    setPnidNoDraft(state.pnid_no ?? "");
    setPnidTitleDraft(state.pnid_title ?? "");
    setDrawingClassDraft(state.drawing_class ?? "");
    if (!activeEquipmentId && state.equipments.length > 0) {
      setActiveEquipmentId(state.equipments[0].id);
    }
    if (!activeNodeId && state.nodes.length > 0) {
      setActiveNodeId(state.nodes[0].id);
    }
    resolvedStepForDocRef.current = selectedDoc.id;
    setWizardStep(
      resolveInitialStep({
        state,
        routeEditEquipmentId,
        routeTargetEquipmentId,
        routeTargetNodeId,
        routeEditInstrumentId,
        routeTargetInstrumentId,
        urlStep: parseWizardStep(location.search),
      }),
    );
  }, [
    pnidStateQuery.data,
    selectedDoc.id,
    applyPnidStateLocally,
    activeEquipmentId,
    activeNodeId,
    routeEditEquipmentId,
    routeEditInstrumentId,
    routeTargetEquipmentId,
    routeTargetNodeId,
    routeTargetInstrumentId,
    location.search,
  ]);

  useEffect(() => {
    if (!pnidStateQuery.isError) return;
    if (pnidStateLoadedDocId === selectedDoc.id) return;
    const status = axios.isAxiosError(pnidStateQuery.error)
      ? pnidStateQuery.error.response?.status
      : undefined;
    console.error("Failed to load PNID wizard state:", pnidStateQuery.error);
    setPnidStateLoadedDocId(selectedDoc.id);
    if (status === 404) {
      setPnidState(null);
      if (resolvedStepForDocRef.current !== selectedDoc.id) {
        resolvedStepForDocRef.current = selectedDoc.id;
        setWizardStep(1);
      }
      return;
    }
    showAlert(
      "Could not load drawing",
      "The drawing state failed to load. Refresh and try again.",
      "error",
    );
  }, [pnidStateQuery.isError, pnidStateQuery.error, pnidStateLoadedDocId, selectedDoc.id]);

  // Local → cache. react-query may replaceEqualDeep the value, so pin the
  // guard to whatever the cache actually holds after the write.
  useEffect(() => {
    if (!pnidState || !pnidStateLoadedDocId) return;
    queryClient.setQueryData(queryKeys.pnidState(pnidStateLoadedDocId), pnidState);
    lastAppliedStateRef.current =
      queryClient.getQueryData<PnidWizardState>(queryKeys.pnidState(pnidStateLoadedDocId))
      ?? pnidState;
  }, [pnidState, pnidStateLoadedDocId, queryClient]);

  // Mirror wizard step into the URL with replace so Back leaves the drawing.
  // Layout, not passive: a passive effect can run after the user (or a test)
  // has already navigated to `/`, and this replace would yank them back onto
  // the drawing. Layout writes `?step=` before paint, and the cleanup flag
  // drops a navigate if this workspace unmounts first.
  useLayoutEffect(() => {
    if (isBooting) return;
    if (parseWizardStep(location.search) === wizardStep) return;
    if (!location.pathname.includes(`/pnid/${selectedDoc.id}`)) return;
    let cancelled = false;
    const next = new URLSearchParams(location.search);
    next.set(STEP_PARAM, String(wizardStep));
    const href = `${location.pathname}?${next}`;
    queueMicrotask(() => {
      if (cancelled) return;
      navigate(href, { replace: true });
    });
    return () => {
      cancelled = true;
    };
  }, [wizardStep, isBooting, location.search, location.pathname, navigate, selectedDoc.id]);

  const restorePnidSnapshot = useCallback(async (
    entry: PnidUndoEntry,
    direction: "undo" | "redo",
  ) => {
    if (!selectedDoc?.id || !pnidState || entry.docId !== selectedDoc.id) return;
    setHistoryApplying(true);
    const currentEntry: PnidUndoEntry = {
      docId: selectedDoc.id,
      label: entry.label,
      state: clonePnidState(pnidState),
    };
    try {
      const restored = await pnidApi.replaceState(selectedDoc.id, entry.state, {
        reason: `${direction}:${entry.label}`,
      });
      applyPnidStateLocally(restored);
      setAnnotationImportAdjustment(null);
      await onReloadDocuments();
      if (direction === "undo") {
        setUndoStack((prev) => prev.slice(0, -1));
        setRedoStack((prev) => [...prev.slice(-(WIZARD_HISTORY_LIMIT - 1)), currentEntry]);
        showAlert("Undone", entry.label, "success");
      } else {
        setRedoStack((prev) => prev.slice(0, -1));
        setUndoStack((prev) => [...prev.slice(-(WIZARD_HISTORY_LIMIT - 1)), currentEntry]);
        showAlert("Redone", entry.label, "success");
      }
    } catch (error) {
      console.error(`Failed to ${direction} P&ID action:`, error);
      showAlert("Undo failed", "Could not restore the saved annotation state.", "error");
    } finally {
      setHistoryApplying(false);
    }
  }, [applyPnidStateLocally, onReloadDocuments, pnidState, selectedDoc?.id]);

  const handleUndoPnidAction = useCallback(() => {
    if (historyApplying) return;
    const entry = undoStack[undoStack.length - 1];
    if (!entry) return;
    void restorePnidSnapshot(entry, "undo");
  }, [historyApplying, restorePnidSnapshot, undoStack]);

  const handleRedoPnidAction = useCallback(() => {
    if (historyApplying) return;
    const entry = redoStack[redoStack.length - 1];
    if (!entry) return;
    void restorePnidSnapshot(entry, "redo");
  }, [historyApplying, redoStack, restorePnidSnapshot]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const tagName = target?.tagName?.toLowerCase();
      const isEditable =
        tagName === "input" ||
        tagName === "textarea" ||
        tagName === "select" ||
        Boolean(target?.isContentEditable);
      if (isEditable) return;
      const isUndoKey = (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "z";
      if (!isUndoKey) return;
      event.preventDefault();
      if (event.shiftKey) {
        handleRedoPnidAction();
      } else {
        handleUndoPnidAction();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleRedoPnidAction, handleUndoPnidAction]);

  const selectedBackendAnnotationTargets = useMemo(() => {
    const targets: PnidAnnotationBackendTarget[] = [];
    const seen = new Set<string>();
    for (const key of selectedWizardItemKeys) {
      const parsed = parseWizardItemKey(key);
      if (!parsed) continue;
      const backendTarget = toBackendAnnotationTarget(parsed);
      const backendKey = `${backendTarget.kind}:${backendTarget.id}`;
      if (seen.has(backendKey)) continue;
      seen.add(backendKey);
      targets.push(backendTarget);
    }
    return targets;
  }, [selectedWizardItemKeys]);

  const handleSelectAllWizardAnnotations = useCallback(() => {
    if (!pnidState) return;
    setSelectedWizardItemKeys(getAllSavedAnnotationKeys(pnidState));
    setSavedAnnotationEditModeSignal((prev) => prev + 1);
  }, [pnidState]);

  const handleClearWizardAnnotationSelection = useCallback(() => {
    setSelectedWizardItemKeys(new Set());
  }, []);

  const handleShiftSelectedWizardAnnotations = useCallback(async (
    delta: { dx: number; dy: number },
    options?: { lineSegmentScale?: { sx: number; sy: number } },
  ) => {
    if (!selectedDoc?.id || selectedBackendAnnotationTargets.length === 0) return;
    const { dx, dy } = delta;
    if (!Number.isFinite(dx) || !Number.isFinite(dy)) {
      showAlert("Move failed", "Could not save an invalid selected-annotation offset.", "error");
      return;
    }
    if (Math.abs(dx) < 0.1 && Math.abs(dy) < 0.1) return;

    pushPnidUndoSnapshot(
      selectedBackendAnnotationTargets.length === 1
        ? "Move selected annotation"
        : `Move ${selectedBackendAnnotationTargets.length} selected annotations`,
    );
    try {
      const updated = await pnidApi.shiftSelectedAnnotations(
        selectedDoc.id,
        selectedBackendAnnotationTargets,
        dx,
        dy,
        options,
      );
      applyPnidStateLocally(updated, { clearSelection: false });
      showAlert(
        "Selection moved",
        `Shifted ${selectedBackendAnnotationTargets.length} selected annotation(s).`,
        "success",
      );
    } catch (error) {
      console.error("Failed to shift selected annotations:", error);
      try {
        const refreshed = await refreshPnidState(selectedDoc.id);
        setPnidState(refreshed);
      } catch (refreshError) {
        console.error("Failed to refresh after selected annotation shift error:", refreshError);
      }
      showAlert("Move failed", "Could not save the selected annotation offset.", "error");
      throw error;
    }
  }, [
    applyPnidStateLocally,
    pushPnidUndoSnapshot,
    refreshPnidState,
    selectedBackendAnnotationTargets,
    selectedDoc?.id,
  ]);

  const handleDeleteSelectedWizardAnnotations = useCallback(() => {
    if (!selectedDoc?.id || selectedBackendAnnotationTargets.length === 0) return;
    const count = selectedBackendAnnotationTargets.length;
    setConfirmDialog({
      isOpen: true,
      title: count === 1 ? "Delete selected annotation?" : `Delete ${count} selected annotations?`,
      message: "This can be undone with Cmd/Ctrl+Z.",
      type: "danger",
      confirmText: "Delete",
      onConfirm: async () => {
        setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
        pushPnidUndoSnapshot(count === 1 ? "Delete selected annotation" : `Delete ${count} selected annotations`);
        try {
          const updated = await pnidApi.deleteSelectedAnnotations(selectedDoc.id, selectedBackendAnnotationTargets);
          applyPnidStateLocally(updated);
          showAlert("Deleted", count === 1 ? "Selected annotation deleted." : `${count} selected annotations deleted.`, "success");
        } catch (error) {
          console.error("Failed to delete selected annotations:", error);
          showAlert("Delete failed", "Could not delete the selected annotations.", "error");
        }
      },
    });
  }, [
    applyPnidStateLocally,
    pushPnidUndoSnapshot,
    selectedBackendAnnotationTargets,
    selectedDoc?.id,
  ]);

  const putRedundancySetTags = usePutRedundancySetTags(selectedDoc?.domain_id ?? "");
  const handleMarkSelectedAsRedundancySet = useCallback(() => {
    if (!pnidState) return;
    const selection = resolveRedundancySetSelection(selectedBackendAnnotationTargets, pnidState);
    if (selection.untaggedItems.length > 0) {
      showAlert(
        "Not marked as a redundancy set",
        `Only tagged equipment and tagged instruments can be members. These selected items carry no tag: ${selection.untaggedItems.join(", ")}.`,
        "warning",
      );
      return;
    }
    if (selection.distinctTagCount < 2) {
      showAlert("Not marked as a redundancy set", "A redundancy set needs at least two different tags.", "warning");
      return;
    }
    putRedundancySetTags.mutate(selection.tags, {
      onSuccess: (sets) => {
        const markedSet = findRedundancySet(sets, selection.tags[0]);
        showAlert("Redundancy set saved", `Members: ${(markedSet?.tags ?? selection.tags).join(", ")}.`, "success");
      },
      onError: (error) => {
        const conflict = redundancySetConflictOf(error);
        if (!conflict) {
          showAlert("Redundancy set not saved", "Could not save the redundancy set.", "error");
          return;
        }
        const conflictingSets = conflict.sets.map((set) => set.tags.join(", ")).join("; ");
        showAlert(
          "Redundancy set not saved",
          `${conflict.message} Existing sets: ${conflictingSets}. Remove a member from one set first.`,
          "warning",
        );
      },
    });
    // showAlert is a new function every render and only sets state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pnidState, putRedundancySetTags.mutate, selectedBackendAnnotationTargets]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Delete" && event.key !== "Backspace") return;
      if (selectedBackendAnnotationTargets.length === 0) return;
      const target = event.target as HTMLElement | null;
      const tagName = target?.tagName?.toLowerCase();
      const isEditable =
        tagName === "input" ||
        tagName === "textarea" ||
        tagName === "select" ||
        Boolean(target?.isContentEditable);
      if (isEditable) return;
      event.preventDefault();
      handleDeleteSelectedWizardAnnotations();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleDeleteSelectedWizardAnnotations, selectedBackendAnnotationTargets.length]);

  const handleCopyDocumentLink = useCallback(async () => {
    if (!selectedDoc) return;

    const shareUrl = new URL(buildPnidPath(selectedDoc.id), window.location.origin).toString();
    try {
      await navigator.clipboard.writeText(shareUrl);
      showAlert("Link copied", "Shareable PNID link copied to clipboard.", "success");
    } catch (error) {
      console.error("Failed to copy PNID link:", error);
      showAlert("Copy failed", shareUrl, "info");
    }
  }, [selectedDoc]);

  const openAnnotationImportDialog = useCallback(() => {
    if (!selectedDoc) return;
    setAnnotationImportSearch("");
    setAnnotationImportSourceId("");
    setAnnotationImportOpen(true);
  }, [selectedDoc]);

  const runAnnotationImport = useCallback(async (overwriteExisting: boolean) => {
    if (!selectedDoc || !annotationImportSourceId) return;
    setAnnotationImporting(true);
    try {
      pushPnidUndoSnapshot(overwriteExisting ? "Replace imported annotations" : "Import annotations");
      const result = await pnidApi.importAnnotations(
        selectedDoc.id,
        annotationImportSourceId,
        { overwriteExisting },
      );
      const refreshed = await refreshPnidState(selectedDoc.id);
      setPnidState(refreshed);
      setAnnotationImportOpen(false);
      setAnnotationImportSourceId("");
      setAnnotationImportSearch("");
      await onReloadDocuments();
      const sourceLabel = selectedAnnotationImportSource
        ? getDocumentPrimaryLabel(selectedAnnotationImportSource)
        : "selected source";
      const copiedSummary = formatAnnotationCountSummary({
        equipments: result.copied.equipments,
        nodes: result.copied.nodes,
        instruments: result.copied.instruments,
        control_loops: result.copied.control_loops,
        relationships: result.copied.relationships,
      }) || "0 annotations";
      const replacedSummary = formatAnnotationCountSummary({
        equipments: result.replaced.equipments,
        nodes: result.replaced.nodes,
        instruments: result.replaced.instruments,
        control_loops: result.replaced.control_loops,
        relationships: result.replaced.relationships,
      });
      const geometryAdjustmentText = formatImportGeometryAdjustment(result.geometry_transform);
      setAnnotationImportAdjustment({
        sourceLabel,
        copiedSummary,
        replacedSummary: replacedSummary || "",
        overwroteExisting: result.overwrote_existing,
        skippedRelationships: result.skipped_relationships,
        geometryAdjustmentText,
      });
      setSelectedWizardItemKeys(getAllSavedAnnotationKeys(refreshed));
      setSavedAnnotationEditModeSignal((prev) => prev + 1);
      showAlert(
        "Annotations imported",
        `${result.overwrote_existing && replacedSummary ? `Replaced ${replacedSummary}. ` : ""}Imported ${copiedSummary} from ${sourceLabel}.${geometryAdjustmentText ? ` ${geometryAdjustmentText}` : ""}${result.skipped_relationships ? ` Skipped ${result.skipped_relationships} relationship(s) with missing copied endpoints.` : ""} Target document metadata was retained.`,
        "success",
      );
    } catch (error: any) {
      console.error("Failed to import annotations:", error);
      const detail = error?.response?.data?.detail;
      showAlert(
        "Import failed",
        typeof detail === "string" ? detail : "Could not import annotations from the selected document.",
        "error",
      );
    } finally {
      setAnnotationImporting(false);
    }
  }, [
    annotationImportSourceId,
    onReloadDocuments,
    pushPnidUndoSnapshot,
    refreshPnidState,
    selectedAnnotationImportSource,
    selectedDoc,
  ]);

  const handleImportAnnotations = useCallback(() => {
    if (!selectedDoc || !annotationImportSourceId) return;
    if (targetHasAnnotations) {
      const sourceLabel = selectedAnnotationImportSource
        ? getDocumentPrimaryLabel(selectedAnnotationImportSource)
        : "selected source";
      const sourceDomain = selectedAnnotationImportSource
        ? documentDomainPathLabel(domainIndex, selectedAnnotationImportSource)
        : "";
      const targetLabel = getDocumentPrimaryLabel(selectedDoc);
      const targetDomain = documentDomainPathLabel(domainIndex, selectedDoc);
      setConfirmDialog({
        isOpen: true,
        title: "Replace target annotations?",
        message: `Import ${diagramTypeLabel(diagramMode)} annotations from ${sourceLabel}${sourceDomain ? ` (${sourceDomain})` : ""} into ${targetLabel}${targetDomain ? ` (${targetDomain})` : ""}. This will replace ${targetAnnotationSummary || "the existing target annotations"}. Target P&ID number, title, document ID, plant, notes, metadata, and diagram type will be retained.`,
        type: "danger",
        confirmText: "Replace & Import",
        onConfirm: () => {
          setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
          void runAnnotationImport(true);
        },
      });
      return;
    }
    void runAnnotationImport(false);
  }, [
    annotationImportSourceId,
    diagramMode,
    getAnnotation,
    runAnnotationImport,
    selectedAnnotationImportSource,
    selectedDoc,
    targetAnnotationSummary,
    targetHasAnnotations,
  ]);

  useEffect(() => {
    if (!annotationImportOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !annotationImporting) {
        setAnnotationImportOpen(false);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [annotationImportOpen, annotationImporting]);

  const handleMarkReviewed = useCallback(
    async (target: {
      kind: "equipment" | "equipmentInstrument" | "node" | "nodeInstrument";
      id: string;
    }, correction?: DocumentReviewCorrectionCreate) => {
      if (!selectedDoc?.id) return;

      const reviewKind: "equipments" | "nodes" | "instruments" =
        target.kind === "equipment"
          ? "equipments"
          : target.kind === "node"
            ? "nodes"
            : "instruments";

      try {
        await pnidApi.reviewItem(selectedDoc.id, reviewKind, target.id, correction);

        await refreshPnidState(selectedDoc.id);
        if (target.kind === "equipmentInstrument" || target.kind === "nodeInstrument") {
          try {
            const inst = await instrumentApi.getInstrument(selectedDoc.id, target.id);
            setInstrumentEditorState((prev) =>
              prev?.instrument.id === target.id ? { ...prev, instrument: inst } : prev,
            );
          } catch (e) {
            console.warn("Could not refresh instrument after review:", e);
          }
        }
        showAlert("Review saved", "The reviewed item is now marked complete and shown in green.", "success");
      } catch (err: any) {
        const detail = err?.response?.data?.detail ?? (err instanceof Error ? err.message : String(err));
        showAlert("Mark as Reviewed failed", typeof detail === "string" ? detail : JSON.stringify(detail), "error");
      }
    },
    [refreshPnidState, selectedDoc?.id],
  );

  /**
   * Saves one equipment's fields edited straight from the review table. The
   * caller shows the failure on the cell it came from, so the error is thrown
   * on rather than swallowed into an alert.
   */
  const handleReviewTableEquipmentEdit = useCallback(
    async (equipmentId: string, edit: EquipmentReviewEdit) => {
      if (!selectedDoc) throw new Error("No drawing is open.");
      const updated = await pnidApi.updateEquipment(selectedDoc.id, equipmentId, edit);
      setPnidState((current) =>
        current
          ? {
              ...current,
              equipments: current.equipments.map((equipment) =>
                equipment.id === updated.id ? updated : equipment
              ),
            }
          : current
      );
      // The claim moves which drawing owns the asset, so every reader of the
      // register's answer for this drawing is now stale. A rename does the same
      // by a different route: the API resets the marks the old tag carried and
      // copies the new tag's family onto this row, so the register no longer
      // says what it said before the save. The row's own fields come back
      // converged in `updated` above; the register has to be re-read.
      if (edit.is_primary_source !== undefined || edit.equipment_no !== undefined) {
        invalidateRegisterContext(selectedDoc.id);
      }
    },
    [selectedDoc]
  );

  // Comment handlers
  const handleCreateComment = (
    canvasX: number,
    canvasY: number,
    screenX: number,
    screenY: number
  ) => {
    // Use the screen coordinates passed from canvas for precise positioning
    setCommentInputPosition({
      x: screenX,
      y: screenY,
      canvasX,
      canvasY,
    });
  };

  const handleToggleCommentMode = () => {
    const newMode = !commentMode;
    setCommentMode(newMode);
    if (!newMode) {
      // Exiting comment mode - clear any selections
      setSelectedCommentId(null);
      setCommentInputPosition(null);
      setSelectedCommentScreenPos(null);
    }
  };

  // Handle crop from canvas (for template creation)
  const handleCrop = useCallback((result: CropResult) => {
    setCroppedImage(result.imageBlob);
  }, []);

  // === Template Matching Handlers ===

  /**
   * Run template matching for a template on the current document.
   * Results are stored in unsavedMatches until saved.
   */
  const handleRunMatch = useCallback(async (templateId: string, _templateName: string, threshold: number = 1.0, mode: MatchingMode = 'untagged') => {
    if (!selectedDoc) return;
    trackAction('template_match_run', { template_id: templateId, threshold, mode });
    
    const startTime = Date.now();
    const result = await templateApi.runMatch(selectedDoc.id, templateId, threshold, mode);
    const latencyMs = Date.now() - startTime;
    const processingTime = latencyMs / 1000;
    trackAction('template_match_completed', {
      template_id: templateId,
      match_count: result.matches.length,
      latency_ms: latencyMs,
    });
    
    // Store matches
    setUnsavedMatches(prev => {
      const next = new Map(prev);
      next.set(templateId, result.matches);
      return next;
    });
    
    // Make template visible
    setTemplateVisibility(prev => {
      const next = new Map(prev);
      next.set(templateId, true);
      return next;
    });
    
    // Calculate and store stats
    const totalMatches = result.matches.length;
    const avgConfidence = totalMatches > 0
      ? result.matches.reduce((sum, m) => sum + m.confidence, 0) / totalMatches
      : 0;
    const avgChamferDistance = totalMatches > 0
      ? result.matches.reduce((sum, m) => sum + m.chamfer_distance, 0) / totalMatches
      : 0;
    
    setMatchStats(prev => {
      const next = new Map(prev);
      next.set(templateId, {
        totalMatches,
        processingTime,
        avgConfidence,
        avgChamferDistance,
      });
      return next;
    });
    
    return result;
  }, [selectedDoc, trackAction]);

  /**
   * Save unsaved matches for a template to the backend.
   */
  const handleSaveMatches = useCallback(async (templateId: string, templateName: string) => {
    if (!selectedDoc) return;
    
    const matches = unsavedMatches.get(templateId);
    if (!matches || matches.length === 0) return;
    trackAction('template_match_saved', { template_id: templateId, match_count: matches.length });
    
    const result = await templateApi.saveAnnotations(
      selectedDoc.id,
      templateId,
      templateName,
      300, // DPI hardcoded
      matches
    );
    
    // Move to saved annotations
    setSavedTemplateAnnotations(prev => [...prev, ...result.annotations]);
    
    // Clear unsaved matches for this template
    setUnsavedMatches(prev => {
      const next = new Map(prev);
      next.delete(templateId);
      return next;
    });
    
    return result;
  }, [selectedDoc, unsavedMatches, trackAction]);

  /**
   * Delete a single template annotation (saved or unsaved).
   */
  const handleDeleteTemplateAnnotation = useCallback(async (
    annotationId: string,
    isUnsaved: boolean,
    templateId?: string,
    index?: number
  ) => {
    if (isUnsaved && templateId !== undefined && index !== undefined) {
      // Remove from unsaved matches
      setUnsavedMatches(prev => {
        const next = new Map(prev);
        const matches = next.get(templateId);
        if (matches) {
          const updated = [...matches];
          updated.splice(index, 1);
          if (updated.length === 0) {
            next.delete(templateId);
          } else {
            next.set(templateId, updated);
          }
          
          // Update stats
          setMatchStats(prevStats => {
            const nextStats = new Map(prevStats);
            const stats = nextStats.get(templateId);
            if (stats && updated.length > 0) {
              const avgConfidence = updated.reduce((sum, m) => sum + m.confidence, 0) / updated.length;
              const avgChamferDistance = updated.reduce((sum, m) => sum + m.chamfer_distance, 0) / updated.length;
              nextStats.set(templateId, {
                ...stats,
                totalMatches: updated.length,
                avgConfidence,
                avgChamferDistance,
              });
            } else if (stats) {
              nextStats.delete(templateId);
            }
            return nextStats;
          });
        }
        return next;
      });
    } else if (!isUnsaved && selectedDoc) {
      // Delete from backend
      await templateApi.deleteAnnotation(selectedDoc.id, annotationId);
      setSavedTemplateAnnotations(prev => prev.filter(a => a.id !== annotationId));
    }
    
    // Remove from selection
    setSelectedAnnotationIds(prev => {
      const next = new Set(prev);
      next.delete(annotationId);
      return next;
    });
  }, [selectedDoc]);

  /**
   * Delete all annotations for a template (saved and unsaved).
   */
  const handleDeleteAllTemplateAnnotations = useCallback(async (templateId: string) => {
    if (!selectedDoc) return;
    
    // Delete saved annotations from backend
    await templateApi.deleteAllAnnotations(selectedDoc.id, templateId);
    setSavedTemplateAnnotations(prev => prev.filter(a => a.template_id !== templateId));
    
    // Clear unsaved matches
    setUnsavedMatches(prev => {
      const next = new Map(prev);
      next.delete(templateId);
      return next;
    });
    
    // Clear stats
    setMatchStats(prev => {
      const next = new Map(prev);
      next.delete(templateId);
      return next;
    });
    
    // Clear selection for this template's annotations
    setSelectedAnnotationIds(prev => {
      const next = new Set(prev);
      savedTemplateAnnotations
        .filter(a => a.template_id === templateId)
        .forEach(a => next.delete(a.id));
      return next;
    });
  }, [selectedDoc, savedTemplateAnnotations]);

  /**
   * Bulk delete selected annotations.
   */
  const handleBulkDeleteAnnotations = useCallback(async () => {
    if (!selectedDoc || selectedAnnotationIds.size === 0) return;
    
    const idsToDelete = Array.from(selectedAnnotationIds);
    
    // Separate saved and unsaved
    const savedIds = idsToDelete.filter(id => !id.startsWith('unsaved-'));
    const unsavedIds = idsToDelete.filter(id => id.startsWith('unsaved-'));
    
    // Delete saved annotations using bulk API
    if (savedIds.length > 0) {
      await templateApi.bulkDeleteAnnotations(selectedDoc.id, savedIds);
    }
    setSavedTemplateAnnotations(prev => prev.filter(a => !savedIds.includes(a.id)));
    
    // Group unsaved by template and delete
    const unsavedByTemplate = new Map<string, number[]>();
    for (const id of unsavedIds) {
      // Parse "unsaved-{templateId}-{index}"
      const match = id.match(/^unsaved-(.+)-(\d+)$/);
      if (match) {
        const [, templateId, indexStr] = match;
        const index = parseInt(indexStr, 10);
        if (!unsavedByTemplate.has(templateId)) {
          unsavedByTemplate.set(templateId, []);
        }
        unsavedByTemplate.get(templateId)!.push(index);
      }
    }
    
    // Remove unsaved matches by index (in reverse order to maintain indices)
    setUnsavedMatches(prev => {
      const next = new Map(prev);
      for (const [templateId, indices] of unsavedByTemplate) {
        const matches = next.get(templateId);
        if (matches) {
          const sortedIndices = indices.sort((a, b) => b - a); // Reverse order
          const updated = [...matches];
          for (const idx of sortedIndices) {
            updated.splice(idx, 1);
          }
          if (updated.length === 0) {
            next.delete(templateId);
          } else {
            next.set(templateId, updated);
          }
        }
      }
      return next;
    });
    
    // Clear selection
    setSelectedAnnotationIds(new Set());
  }, [selectedDoc, selectedAnnotationIds]);

  /**
   * Toggle visibility of annotations for a template.
   */
  const handleToggleTemplateVisibility = useCallback((templateId: string) => {
    setTemplateVisibility(prev => {
      const next = new Map(prev);
      next.set(templateId, !prev.get(templateId));
      return next;
    });
  }, []);

  /**
   * Select/deselect an annotation for bulk operations.
   */
  const handleSelectAnnotation = useCallback((annotationId: string, isMultiSelect: boolean) => {
    setSelectedAnnotationIds(prev => {
      const next = new Set(prev);
      if (next.has(annotationId)) {
        next.delete(annotationId);
      } else {
        if (!isMultiSelect) {
          next.clear();
        }
        next.add(annotationId);
      }
      return next;
    });
  }, []);

  /**
   * Clear all selected annotations.
   */
  const handleClearAnnotationSelection = useCallback(() => {
    setSelectedAnnotationIds(new Set());
  }, []);

  const handleSubmitComment = async (text: string) => {
    if (!selectedDoc || !commentInputPosition) return;
    try {
      const newComment = await commentsApi.create(selectedDoc.id, {
        x: commentInputPosition.canvasX,
        y: commentInputPosition.canvasY,
        text,
        author: effectiveUserName || "Anonymous",
      });
      queryClient.setQueryData<CanvasComment[]>(
        queryKeys.comments(selectedDoc.id),
        (prev) => [...(prev ?? []), newComment],
      );
      setCommentInputPosition(null);
    } catch (err) {
      console.error("Failed to create comment:", err);
    }
  };

  const handleSelectComment = (
    commentId: string,
    screenX: number,
    screenY: number
  ) => {
    setSelectedCommentId(commentId);
    setSelectedCommentScreenPos({ x: screenX, y: screenY });
  };

  const handleResolveComment = async () => {
    if (!selectedDoc || !selectedCommentId) return;
    try {
      const updated = await commentsApi.resolve(
        selectedDoc.id,
        selectedCommentId,
        effectiveUserName || "Anonymous"
      );
      queryClient.setQueryData<CanvasComment[]>(
        queryKeys.comments(selectedDoc.id),
        (prev) => (prev ?? []).map((c) => (c.id === selectedCommentId ? updated : c)),
      );
      setSelectedCommentId(null);
      setSelectedCommentScreenPos(null);
    } catch (err) {
      console.error("Failed to resolve comment:", err);
    }
  };

  const handleUnresolveComment = async () => {
    if (!selectedDoc || !selectedCommentId) return;
    try {
      const updated = await commentsApi.unresolve(
        selectedDoc.id,
        selectedCommentId
      );
      queryClient.setQueryData<CanvasComment[]>(
        queryKeys.comments(selectedDoc.id),
        (prev) => (prev ?? []).map((c) => (c.id === selectedCommentId ? updated : c)),
      );
    } catch (err) {
      console.error("Failed to unresolve comment:", err);
    }
  };

  const handleDeleteComment = async () => {
    if (!selectedDoc || !selectedCommentId) return;
    try {
      await commentsApi.delete(selectedDoc.id, selectedCommentId);
      queryClient.setQueryData<CanvasComment[]>(
        queryKeys.comments(selectedDoc.id),
        (prev) => (prev ?? []).filter((c) => c.id !== selectedCommentId),
      );
      setSelectedCommentId(null);
      setSelectedCommentScreenPos(null);
    } catch (err) {
      console.error("Failed to delete comment:", err);
    }
  };

  const handlePrefillFromAI = async () => {
    if (!selectedDoc) return;
    setLoadingAI(true);
    trackAction('ai_prefill_triggered');
    const prefillStart = Date.now();
    try {
      const updatedState = await pnidApi.prefillFromAI(selectedDoc.id);
      setPnidState(updatedState);
      setPnidNoDraft(updatedState.pnid_no ?? "");
      setPnidTitleDraft(updatedState.pnid_title ?? "");
      setDrawingClassDraft(updatedState.drawing_class ?? "");
      queryClient.setQueryData(queryKeys.pnidState(selectedDoc.id), updatedState);
      void queryClient.invalidateQueries({ queryKey: queryKeys.aiData(selectedDoc.id) });

      const firstUnmarked = updatedState.equipments.find(
        (e) => e.status === "ai_identified"
      );
      if (firstUnmarked) {
        setActiveEquipmentId(firstUnmarked.id);
      } else if (updatedState.equipments.length > 0) {
        setActiveEquipmentId(updatedState.equipments[0].id);
      }

      trackAction('ai_prefill_completed', {
        equipment_count: updatedState.equipments.length,
        line_count: updatedState.nodes.length,
        latency_ms: Date.now() - prefillStart,
      });
      showAlert(
        "AI Data Loaded",
        `Loaded ${updatedState.equipments.length} equipment items and ${updatedState.nodes.length} lines from AI data.`,
        "success"
      );
      if (updatedState.equipments.length > 0) {
        setWizardStep(3);
      }
    } catch (error) {
      console.error("Failed to prefill from AI:", error);
      showAlert(
        "Prefill Failed",
        "No AI data found for this document or failed to load.",
        "error"
      );
    } finally {
      setLoadingAI(false);
    }
  };

  const savePnidSetup = async () => {
    if (!selectedDoc) return;
    if (readOnlyWizard) return; // Reviewers and viewing docs assigned to others cannot save
    const nextPnidNo = pnidNoDraft.trim();
    if (!nextPnidNo) {
      showAlert("PnID No required", "Please enter a P&ID number.", "warning");
      return;
    }
    try {
      const updated = await setupMutation.mutateAsync({
        pnid_no: nextPnidNo,
        pnid_title: pnidTitleDraft.trim() || undefined,
        drawing_class: drawingClassDraft || null,
      });
      setPnidState(updated);
      setPnidNoDraft(updated.pnid_no ?? "");
      setPnidTitleDraft(updated.pnid_title ?? "");
      setDrawingClassDraft(updated.drawing_class ?? "");
      onPatchDocument(selectedDoc.id, {
        pnid_no: updated.pnid_no ?? null,
        pnid_title: updated.pnid_title ?? null,
      });
      showAlert(
        "Saved",
        "Drawing setup saved.",
        "success"
      );
      const nextStep: WizardStep = pnidSetupReturnStep ?? 3;
      setPnidSetupReturnStep(null);
      setWizardStep(nextStep);
    } catch (error) {
      console.error(error);
      showAlert("Save failed", "Could not save drawing setup.", "error");
    }
  };

  const openPnidSetupEditor = () => {
    if (!pnidState) return;
    setPnidNoDraft(pnidState.pnid_no ?? "");
    setPnidTitleDraft(pnidState.pnid_title ?? "");
    setDrawingClassDraft(pnidState.drawing_class ?? "");
    setPnidSetupReturnStep(wizardStep === 1 ? null : wizardStep);
    setWizardStep(1);
  };

  const getActiveEquipment = () =>
    pnidState?.equipments.find((e) => e.id === activeEquipmentId) ?? null;
  const getActiveNode = () =>
    pnidState?.nodes.find((n) => n.id === activeNodeId) ?? null;

  const nextEquipment = () => {
    const list = pnidState?.equipments ?? [];
    if (list.length === 0) return;
    const idx = list.findIndex((e) => e.id === activeEquipmentId);
    const next = list[(idx + 1) % list.length];
    setActiveEquipmentId(next.id);
    if (next.bbox) setFocusBbox(next.bbox);
  };

  const nextNode = () => {
    const list = (pnidState?.nodes ?? [])
      .slice()
      .sort((a, b) => a.node_no - b.node_no);
    if (list.length === 0) return;
    const idx = list.findIndex((n) => n.id === activeNodeId);
    const next = list[(idx + 1) % list.length];
    setActiveNodeId(next.id);
    setFocusBbox(nodeGeometryBbox(next));
  };

  // Jump to the object a review-policy finding points at.
  const handleReviewFindingNavigate = (finding: ReviewPolicyFinding) => {
    if (!pnidState || !finding.target_id) return;
    if (finding.target_kind === 'equipment') {
      const equipment = pnidState.equipments.find((e) => e.id === finding.target_id);
      if (!equipment) return;
      setWizardStep(3);
      setActiveEquipmentId(equipment.id);
      setFocusBbox(equipment.bbox ?? equipment.label_bbox ?? null);
    } else if (finding.target_kind === 'node') {
      const node = pnidState.nodes.find((n) => n.id === finding.target_id);
      if (!node) return;
      setWizardStep(4);
      setActiveNodeId(node.id);
      setFocusBbox(nodeGeometryBbox(node));
    } else if (finding.target_kind === 'instrument') {
      const instrument = pnidState.instruments.find((i) => i.id === finding.target_id);
      if (!instrument) return;
      setSelectedInstrumentId(instrument.id);
      if (instrument.bbox) setFocusBbox(instrument.bbox);
    } else {
      return;
    }
    setReviewIssuesOpen(false);
  };

  // Node marking actions
  const handleMarkNode = (
    nodeId: string,
    screenPosition?: { x: number; y: number }
  ) => {
    const node = pnidState?.nodes.find((candidate) => candidate.id === nodeId);
    if (diagramMode === 'pfd' && node?.is_duct) {
      showAlert('P&ID-only geometry', 'Existing ducts remain visible in PFD mode, but duct authoring is available only in P&ID mode.', 'info');
      return;
    }
    // Set the node as active and enter marking mode
    setActiveNodeId(nodeId);
    setIsNodeMarkingMode(true);
    setNodeDrawingShape(node?.is_duct ? 'duct' : 'pipeline');
    setFocusBbox(nodeGeometryBbox(node));
    if (node && pnidState) {
      const defaultLineNo = node.line_no?.trim() ? node.line_no : "";
      setLineMarkPopover({
        nodeId,
        position: screenPosition ?? getDefaultLinePopoverPosition(),
        values: {
          line_no: defaultLineNo ?? "",
          source_ref: node.source_ref ?? "",
          source_pnid: node.source_pnid ?? pnidState.pnid_no ?? "",
          dest_ref: node.dest_ref ?? "",
          dest_pnid: node.dest_pnid ?? pnidState.pnid_no ?? "",
          ...getNodeLineProperties(node),
          drain_type: node.drain_type ?? null,
          is_duct: Boolean(node.is_duct),
        },
        stage: "source",
        isOpen: true,
      });
    }
    // The drawing will be handled by handleWizardDraw when user draws on canvas
  };

  const handleUnmarkNode = async (nodeId: string) => {
    if (!selectedDoc) return;
    const existingNode = pnidState?.nodes.find((node) => node.id === nodeId);
    if (diagramMode === 'pfd' && existingNode?.is_duct) {
      showAlert('P&ID-only geometry', 'Existing ducts remain visible in PFD mode, but duct authoring is available only in P&ID mode.', 'info');
      return;
    }
    trackAction('item_unmarked', { item_type: 'node', item_id: nodeId });
    try {
      // Clear whichever mutually exclusive geometry marks this connection.
      await pnidApi.updateNode(selectedDoc.id, nodeId, {
        polyline: null,
        duct_bbox: null,
        status: "ai_identified",
      });
      // Refresh state
      const newState = await pnidApi.get(selectedDoc.id);
      setPnidState(newState);
      setFocusBbox(null);
      // Automatically enter marking mode for redrawing
      setIsNodeMarkingMode(true);
      const node = newState.nodes.find((n) => n.id === nodeId);
      if (node) {
        const defaultLineNo = node.line_no?.trim() ? node.line_no : "";
        setLineMarkPopover({
          nodeId,
          position: getDefaultLinePopoverPosition(),
          values: {
            line_no: defaultLineNo ?? "",
            source_ref: node.source_ref ?? "",
            source_pnid: node.source_pnid ?? newState.pnid_no ?? "",
            dest_ref: node.dest_ref ?? "",
            dest_pnid: node.dest_pnid ?? newState.pnid_no ?? "",
            ...getNodeLineProperties(node),
            drain_type: node.drain_type ?? null,
            is_duct: Boolean(node.is_duct),
          },
          stage: "source",
          isOpen: true,
        });
      }
    } catch (err) {
      console.error("Failed to unmark node:", err);
    }
  };

  // Instrument marking actions
  const openMarkDialog = (
    kind: PnidDialogKind,
    bbox: BoundingBox,
    anchor: { x: number; y: number },
    initialValues?: any
  ) => {
    setMarkDialog({
      isOpen: true,
      kind,
      bbox,
      anchor,
      editTarget: null,
      initialValues: initialValues ?? {},
    });
  };


  const openEditDialog = (
    kind: EditableWizardTargetKind,
    id: string,
    options?: {
      shouldFocus?: boolean;
      screenPosition?: { x: number; y: number };
    }
  ) => {
    if (!pnidState) return;
    // Both instrument kinds look through every instrument on the drawing: the kind says
    // which parent to activate, not which list the instrument is in, and an unattached
    // one is in neither parent-keyed list.
    const found =
      kind === "equipment"
        ? pnidState.equipments.find((e) => e.id === id)
        : kind === "equipmentInstrument"
        ? pnidState.instruments.find((i) => i.id === id)
        : kind === "node"
        ? pnidState.nodes.find((n) => n.id === id)
        : kind === "nodeInstrument"
        ? pnidState.instruments.find((i) => i.id === id)
        : pnidState.control_loops.find((l) => l.id === id);
    if (!found) return;
    if (kind === "equipmentInstrument") {
      const inst = found as PnidInstrument;
      const effectiveContext = getInstrumentEffectiveContext(inst, pnidState);
      setActiveEquipmentId(effectiveContext.context_type === 'equipment' ? effectiveContext.context_id : null);
    }
    if (kind === "nodeInstrument") {
      const inst = found as PnidInstrument;
      const effectiveContext = getInstrumentEffectiveContext(inst, pnidState);
      setActiveNodeId(effectiveContext.context_type === 'node' ? effectiveContext.context_id : null);
    }
    if (kind === "node") {
      setActiveNodeId(id);
    }
    if (kind === "controlLoop") {
      // For now, just show an alert - control loop editing can be added later
      showAlert(
        "Edit Control Loop",
        `Loop ${(found as any).loop_tag}: ${
          (found as any).members?.join(", ") || "No members"
        }`,
        "info"
      );
      return;
    }
    const shouldFocus = options?.shouldFocus !== false;
    if (shouldFocus && (found as any).bbox) {
      setFocusBbox((found as any).bbox);
    }

    // For instrument kinds, open the unified InstrumentEditor
    if (kind === "equipmentInstrument" || kind === "nodeInstrument") {
      const bbox = (found as any).bbox as BoundingBox | null;
      const defaultX =
        options?.screenPosition?.x ??
        (bbox ? (bbox.x1 + bbox.x2) / 2 + 200 : window.innerWidth / 2);
      const defaultY = options?.screenPosition?.y ?? (bbox ? bbox.y1 : 100);
      const screenPos = {
        x: Math.min(defaultX, window.innerWidth - 420),
        y: Math.max(80, defaultY),
      };
      handleRightClickInstrument(id, screenPos);
      return;
    }

    setMarkDialog({
      isOpen: true,
      kind,
      bbox: kind === "node"
        ? ((found as PnidNode).is_duct
            ? (found as PnidNode).duct_bbox ?? null
            : (found as PnidNode).polyline
              ? polylineToBbox((found as PnidNode).polyline!, true)
              : null)
        : (found as any).bbox,
      anchor: null,
      editTarget: { kind, id },
      initialValues: {
        ...(found as any),
        // For node/equipment editing, set _markExistingId so PnidMarkDialog shows instrument list UI
        ...(kind === "node" || kind === "equipment" ? { _markExistingId: id } : {}),
      },
    });
  };

  // A Primary P&ID link lands here. The panel can only open once this drawing's
  // annotation is loaded, and it opens once per link: the parameter is dropped
  // from the URL straight after, so reopening or reloading the drawing later
  // shows it as the reviewer left it.
  useEffect(() => {
    if (!routeEditEquipmentId) {
      lastRouteEditEquipmentKeyRef.current = null;
      return;
    }
    if (!selectedDoc?.id || !pnidState || pnidStateLoadedDocId !== selectedDoc.id) {
      return;
    }

    const editKey = `${selectedDoc.id}:edit:${routeEditEquipmentId}`;
    if (lastRouteEditEquipmentKeyRef.current === editKey) {
      return;
    }
    lastRouteEditEquipmentKeyRef.current = editKey;

    const equipment = pnidState.equipments.find((candidate) => candidate.id === routeEditEquipmentId);
    if (equipment) {
      setWizardStep(3);
      setActiveEquipmentId(equipment.id);
      setFocusBbox(equipment.bbox ?? equipment.label_bbox ?? null);
      openEditDialog("equipment", equipment.id);
    } else {
      console.warn("Linked equipment to edit not found:", routeEditEquipmentId);
    }

    consumeRouteParam(EDIT_EQUIPMENT_PARAM);
  }, [pnidState, pnidStateLoadedDocId, routeEditEquipmentId, selectedDoc?.id]);

  // The instrument twin of the link above, and it waits on the same things: this
  // drawing's annotation loaded, the instrument saved on it, and one shot per
  // link before the parameter is dropped from the URL. `focusInstrument` puts the
  // wizard on the instrument and `handleRightClickInstrument` opens the editor
  // over it, so the link lands exactly where a right-click on the sheet does.
  //
  // When it cannot open the editor — a higher-ranked target in the same URL, or
  // an instrument this drawing does not hold — it says so and leaves the canvas
  // alone, as a missing equipment target does.
  useEffect(() => {
    if (!routeEditInstrumentId) {
      lastRouteEditInstrumentKeyRef.current = null;
      return;
    }
    if (!selectedDoc?.id || !pnidState || pnidStateLoadedDocId !== selectedDoc.id) {
      return;
    }

    const editKey = `${selectedDoc.id}:edit-instrument:${routeEditInstrumentId}`;
    if (lastRouteEditInstrumentKeyRef.current === editKey) {
      return;
    }
    lastRouteEditInstrumentKeyRef.current = editKey;

    if (
      winningRouteTarget({
        routeEditEquipmentId,
        routeTargetEquipmentId,
        routeTargetNodeId,
        routeEditInstrumentId,
        routeTargetInstrumentId,
        urlStep: null,
      }) !== 'instrument'
    ) {
      console.warn(
        "edit_instrument_id ignored: edit_equipment_id/target_equipment_id/target_node_id takes precedence",
      );
    } else if (focusInstrument(routeEditInstrumentId)) {
      void handleRightClickInstrument(routeEditInstrumentId, instrumentEditorPosition());
    } else {
      console.warn("Linked instrument to edit not found:", routeEditInstrumentId);
    }

    consumeRouteParam(EDIT_INSTRUMENT_PARAM);
  }, [pnidState, pnidStateLoadedDocId, routeEditInstrumentId, selectedDoc?.id]);

  const updateNodeSegmentsForDistinct = useCallback(
    async (
      nodeId: string,
      distinct: MergedLineSegment,
      nextStatus: "confirmed" | "deleted"
    ) => {
      if (!selectedDoc) return;
      if (!pnidState) return;
      const node = pnidState.nodes.find((n) => n.id === nodeId);
      if (!node) return;
      const current = node.line_segments ?? [];
      const ids = new Set(distinct.source_segment_ids);
      const updated = current.map((s) =>
        ids.has(s.id) ? { ...s, status: nextStatus } : s
      );
      await pnidApi.updateNode(selectedDoc.id, nodeId, {
        line_segments: updated,
      });
      const newState = await pnidApi.get(selectedDoc.id);
      setPnidState(newState);
    },
    [selectedDoc, pnidState]
  );

  const confirmAllNodeSegments = useCallback(
    async (nodeId: string) => {
      if (!selectedDoc) return;
      if (!pnidState) return;
      const node = pnidState.nodes.find((n) => n.id === nodeId);
      if (!node) return;
      const current = node.line_segments ?? [];
      const updated = current.map((s) =>
        s.status === "deleted" ? s : { ...s, status: "confirmed" as const }
      );
      await pnidApi.updateNode(selectedDoc.id, nodeId, {
        line_segments: updated,
      });
      const newState = await pnidApi.get(selectedDoc.id);
      setPnidState(newState);
    },
    [selectedDoc, pnidState]
  );

  const handleWizardCanvasSelect = (target: {
    kind: WizardSelectionKind;
    id: string;
    screenPosition?: { x: number; y: number };
    isMultiSelect?: boolean;
  }) => {
    const key = wizardItemKey(target);
    if (target.isMultiSelect) {
      setSelectedWizardItemKeys((prev) => {
        const next = new Set(prev);
        if (next.has(key)) {
          next.delete(key);
        } else {
          next.add(key);
        }
        return next;
      });
      return;
    }
    setSelectedWizardItemKeys(new Set([key]));

    if (target.kind === 'equipment') {
      // Intelligence mode: show equipment operating card (dev only)
      if (import.meta.env.DEV && viewMode === 'intelligence' && plantModel) {
        const equipment = pnidState?.equipments.find(e => e.id === target.id);
        if (equipment?.equipment_no && plantModel.equipment?.[equipment.equipment_no]) {
          setSelectedEquipmentTag(equipment.equipment_no);
          return;
        }
      }
      
      // Annotation mode: Set active equipment and open edit dialog
      setActiveEquipmentId(target.id);
      setFocusBbox(null);
      openEditDialog(target.kind, target.id, { shouldFocus: false });
      return;
    }

    if (target.kind === "node") {
      setActiveNodeId(target.id);
      setFocusBbox(null);

      if (wizardStep === 4) {
        // Prefer the segment review popover when line segments exist for this node
        const node = pnidState?.nodes.find((n) => n.id === target.id);
        const hasSegments = Boolean(
          node?.line_segments && node.line_segments.length > 0
        );
        if (hasSegments) {
          const pos = target.screenPosition ?? getDefaultLinePopoverPosition();
          closeMarkDialog();
          setLineSegmentReviewPopover({
            nodeId: target.id,
            position: pos,
            hoveredDistinctId: null,
            selectedDistinctId: null,
          });
          return;
        }

        // Otherwise open the detailed line dialog directly
        openEditDialog("node", target.id, { shouldFocus: false });
        return;
      } else {
        // On other steps (including step 2), open the edit dialog directly
        openEditDialog("node", target.id, { shouldFocus: false });
      }
      return;
    }

    if (target.kind === "controlLoop") {
      openEditDialog("controlLoop", target.id, { shouldFocus: false });
      return;
    }
    if (target.kind === "relationship") return;

    openEditDialog(target.kind, target.id, { shouldFocus: false });
  };

  const closeMarkDialog = () => {
    setMarkDialog((p) => ({ ...p, isOpen: false }));
  };

  // For line drawing: continue drawing without clearing the path
  const handleContinueDrawing = () => {
    closeMarkDialog();
    // Path remains on canvas - user can continue adding points
  };

  // For line drawing: clear the path and close dialog
  const handleClearPath = () => {
    setCanvasClearSignal((prev) => prev + 1);
    closeMarkDialog();
  };

  const handleRedrawPath = async () => {
    const nodeId = markDialog.initialValues?._markExistingId;
    if (!nodeId || markDialog.kind !== "node") return;
    const confirmed = window.confirm(
      "Redraw this line? Current line marking will be removed so you can draw it again."
    );
    if (!confirmed) return;
    closeMarkDialog();
    await handleUnmarkNode(nodeId);
  };

  const discardStepDraft = () => {
    closeMarkDialog();
    setBoxDraft(null);
    setIsNodeMarkingMode(false);
    setNodeDrawingShape('pipeline');
    setLineMarkPopover(null);
    setCanvasClearSignal((prev) => prev + 1);
    setCanvasHasDraft(false);
  };

  const applyStepChange = (nextStep: WizardStep) => {
    setWizardStep(nextStep);
    closeMarkDialog();
    const keepsFocus =
      (nextStep === 3 && Boolean(activeEquipmentId)) ||
      (nextStep === 4 && Boolean(activeNodeId));
    if (!keepsFocus) {
      setFocusBbox(null);
    }
  };

  const handleStepChangeRequest = (nextStep: WizardStep) => {
    if (nextStep === wizardStep) return;
    // Collected regions are unsaved work, and so is an armed redraw the way Rebox
    // has always been — leaving the step would throw either away silently.
    const hasCollectedRegions =
      boxDraft !== null && (boxDraft.boxes.length > 0 || boxDraft.target !== undefined);
    const hasDraft =
      markDialog.isOpen ||
      isNodeMarkingMode ||
      hasCollectedRegions ||
      canvasHasDraft;
    if (hasDraft) {
      setStepChangeConfirm({ isOpen: true, targetStep: nextStep });
      return;
    }
    applyStepChange(nextStep);
  };

  const confirmStepChange = () => {
    if (!stepChangeConfirm.targetStep) {
      setStepChangeConfirm({ isOpen: false, targetStep: null });
      return;
    }
    discardStepDraft();
    applyStepChange(stepChangeConfirm.targetStep);
    setStepChangeConfirm({ isOpen: false, targetStep: null });
  };

  // === Step 2 Template Annotation Handlers ===
  
  // Handle annotation click in Step 2 (triggered from PidCanvas)
  const handleAnnotationClick = (annotationId: string, action: 'sme-capture' | 'edit', screenPos?: { x: number; y: number }) => {
    const annotation = savedTemplateAnnotations.find(a => a.id === annotationId);
    if (!annotation) return;
    
    setActiveAnnotationId(annotationId);
    
    // Focus canvas on the annotation bbox
    const annotationBbox: BoundingBox = {
      x1: annotation.x,
      y1: annotation.y,
      x2: annotation.x + annotation.width,
      y2: annotation.y + annotation.height,
    };
    setFocusBbox(annotationBbox);
    
    if (action === 'sme-capture') {
      // If annotation already has AI results (in_progress), open unified InstrumentEditor
      if (annotation.status === 'in_progress') {
        const editorPos = screenPos || {
          x: Math.min(window.innerWidth / 2, window.innerWidth - 420),
          y: Math.max(80, window.innerHeight / 2 - 200),
        };
        handleRightClickInstrument(annotationId, editorPos);
        return;
      }

      // Show SME capture popup for unverified annotation
      // Use screen position if provided, otherwise position below center
      // (since canvas will center on the annotation during keyboard navigation)
      const rawPos = screenPos || {
        x: window.innerWidth / 2 - 280, // Center horizontally (280px popup width / 2)
        y: window.innerHeight / 2 + 100  // Below center where annotation will be
      };
      // Clamp to viewport bounds (280px width, ~220px height estimate)
      const clampedPos = {
        x: Math.max(20, Math.min(rawPos.x, window.innerWidth - 300)),
        y: Math.max(80, Math.min(rawPos.y, window.innerHeight - 240)),
      };
      setTemplateSmeCapture({
        annotationId,
        annotation,
        anchor: clampedPos,
      });
      setTemplateSmePopupPos(null); // Reset drag position
      setTemplateSmeCaptureInput("");
    } else {
      // Show unified InstrumentEditor for verified annotation
      const editorPos = screenPos || {
        x: Math.min(window.innerWidth / 2, window.innerWidth - 420),
        y: Math.max(80, window.innerHeight / 2 - 200),
      };
      handleRightClickInstrument(annotationId, editorPos);
    }
  };

  // Draft-only attach/detach (no API calls; persisted only on Save & Mark)
  const handleDraftAttachTemplateAnnotations = useCallback((annotationIds: string[], action: 'attach' | 'detach') => {
    const key = getDraftContextKey();
    if (!key) return;

    setDraftAttachmentByContext(prev => {
      const existing = new Set(prev[key] ?? []);
      if (action === 'attach') {
        annotationIds.forEach(id => existing.add(id));
      } else {
        annotationIds.forEach(id => existing.delete(id));
      }
      return { ...prev, [key]: Array.from(existing) };
    });

    setDraftDetachedByContext(prev => {
      const existing = new Set(prev[key] ?? []);
      if (action === 'attach') {
        annotationIds.forEach(id => existing.delete(id));
      } else {
        annotationIds.forEach(id => existing.add(id));
      }
      return { ...prev, [key]: Array.from(existing) };
    });
  }, [getDraftContextKey]);

  const syncTemplateAttachmentChanges = useCallback(async (
    contextType: 'equipment' | 'node',
    contextId: string
  ) => {
    if (!selectedDoc) return;
    const key = `${contextType}:${contextId}`;
    const attachIds = draftAttachmentByContext[key] ?? [];
    const detachIds = draftDetachedByContext[key] ?? [];
    if (attachIds.length === 0 && detachIds.length === 0) return;

    try {
      await Promise.all([
        ...attachIds.map((annotationId) =>
          instrumentApi.updateInstrument(selectedDoc.id, annotationId, {
            context_type: contextType,
            context_id: contextId,
          }),
        ),
        ...detachIds.map((annotationId) =>
          instrumentApi.updateInstrument(selectedDoc.id, annotationId, {
            context_type: null,
            context_id: null,
          }),
        ),
      ]);
    } catch (error) {
      console.error("Failed to sync template attachments:", error);
      showAlert("Save failed", "Could not sync template attachments.", "error");
      return;
    }

    await refreshPnidState(selectedDoc.id);

    setDraftAttachmentByContext(prev => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setDraftDetachedByContext(prev => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, [draftAttachmentByContext, draftDetachedByContext, refreshPnidState, selectedDoc]);

  // Click-to-attach for unattached (context-free) AI/template instruments in steps 3/4.
  // AI-published instruments (provenance.source='template_match') land without any
  // context_type/context_id; this adopts them onto the active equipment/node immediately
  // so they become first-class equipment/node instruments everywhere.
  const handleDirectAttachAnnotation = useCallback(async (annotationId: string) => {
    if (!selectedDoc || readOnlyWizard) return;
    const contextType: 'equipment' | 'node' | null =
      wizardStep === 3 ? 'equipment' : wizardStep === 4 ? 'node' : null;
    const contextId = wizardStep === 3 ? activeEquipmentId : wizardStep === 4 ? activeNodeId : null;
    if (!contextType || !contextId) return;
    const annotation = savedTemplateAnnotations.find((a) => a.id === annotationId);
    // Only adopt instruments that are not attached anywhere; never steal from another context.
    if (!annotation || annotation.attached_context_type) return;

    try {
      await instrumentApi.updateInstrument(selectedDoc.id, annotationId, {
        context_type: contextType,
        context_id: contextId,
      });
    } catch (error) {
      console.error('Failed to attach AI instrument:', error);
      showAlert('Attach failed', 'Could not attach this instrument. Please try again.', 'error');
      return;
    }

    // Drop any stale draft attach/detach entries for this annotation so a later
    // "Save & Mark" sync cannot override the direct attachment.
    const key = `${contextType}:${contextId}`;
    setDraftAttachmentByContext((prev) => {
      if (!prev[key]?.includes(annotationId)) return prev;
      return { ...prev, [key]: prev[key].filter((id) => id !== annotationId) };
    });
    setDraftDetachedByContext((prev) => {
      if (!prev[key]?.includes(annotationId)) return prev;
      return { ...prev, [key]: prev[key].filter((id) => id !== annotationId) };
    });

    await refreshPnidState(selectedDoc.id);
    trackAction('template_annotation_direct_attached', {
      annotation_id: annotationId,
      context_type: contextType,
      context_id: contextId,
    });
  }, [selectedDoc, readOnlyWizard, wizardStep, activeEquipmentId, activeNodeId, savedTemplateAnnotations, refreshPnidState, trackAction]);

  // Detach a single draft annotation from the active node (used by PnidMarkDialog)
  const handleNodeOverviewDetach = useCallback((annotationId: string) => {
    if (!activeNodeId) return;
    const key = `node:${activeNodeId}`;
    setDraftAttachmentByContext(prev => {
      const existing = new Set(prev[key] ?? []);
      existing.delete(annotationId);
      return { ...prev, [key]: Array.from(existing) };
    });
    setDraftDetachedByContext(prev => {
      const existing = new Set(prev[key] ?? []);
      existing.add(annotationId);
      return { ...prev, [key]: Array.from(existing) };
    });
  }, [activeNodeId]);

  // Persist draft attachments for the active node (used by PnidMarkDialog)
  const handleNodeOverviewSaveAttachments = useCallback(async () => {
    if (!activeNodeId) return;
    await syncTemplateAttachmentChanges('node', activeNodeId);
  }, [activeNodeId, syncTemplateAttachmentChanges]);

  // Detach a single draft annotation from the active equipment (used by EquipmentOverviewDialog)
  const handleEquipmentOverviewDetach = useCallback((annotationId: string) => {
    if (!activeEquipmentId) return;
    const key = `equipment:${activeEquipmentId}`;
    setDraftAttachmentByContext(prev => {
      const existing = new Set(prev[key] ?? []);
      existing.delete(annotationId);
      return { ...prev, [key]: Array.from(existing) };
    });
    setDraftDetachedByContext(prev => {
      const existing = new Set(prev[key] ?? []);
      existing.add(annotationId);
      return { ...prev, [key]: Array.from(existing) };
    });
  }, [activeEquipmentId]);

  // Persist draft attachments for the active equipment (used by EquipmentOverviewDialog)
  const handleEquipmentOverviewSaveAttachments = useCallback(async () => {
    if (!activeEquipmentId) return;
    await syncTemplateAttachmentChanges('equipment', activeEquipmentId);
  }, [activeEquipmentId, syncTemplateAttachmentChanges]);

  // Update pending template item
  const updatePendingTemplateItem = (
    id: string,
    patch: Partial<PendingTemplateItem>
  ) => {
    setPendingTemplateItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, ...patch } : item))
    );
  };
  
  // Remove pending template item
  const removePendingTemplateItem = (id: string) => {
    setPendingTemplateItems((prev) => prev.filter((item) => item.id !== id));
  };

  // Bulk identify all unverified annotations for a template
  const handleBulkIdentify = async (templateId: string, templateName: string) => {
    if (!selectedDoc) return;

    const annotations = savedTemplateAnnotations.filter(
      a => a.template_id === templateId && a.status === 'unverified'
    );
    if (annotations.length === 0) return;

    const annotationIds = annotations.map(a => a.id);

    // Initialize progress
    setBulkIdentifyProgress(prev => new Map(prev).set(templateId, {
      total: annotations.length, completed: 0, errors: 0
    }));

    try {
      // Single bulk API call — backend handles parallelism
      const response = await templateApi.bulkIdentifyAnnotations(
        selectedDoc.id, annotationIds, templateName
      );

      // Update all annotations with results
      const updateMap = new Map<string, any>();
      for (const r of response.results) {
        if (r.success) {
          const cf = r.core_fields ?? {};
          updateMap.set(r.annotation_id, {
            status: 'in_progress' as const,
            instrument_text: r.instrument_text ?? "",
            instrument_type: r.instrument_type ?? null,
            subtype: cf.subtype ?? null,
            core_fields: cf,
          });
        }
      }

      // Batch update local state
      setSavedTemplateAnnotations(prev => prev.map(a => {
        const update = updateMap.get(a.id);
        return update ? { ...a, ...update } : a;
      }));

      // Persist each successful result to backend
      const persistPromises = Array.from(updateMap.entries()).map(([annId, update]) =>
        templateApi.updateAnnotation(selectedDoc.id, annId, update).catch(() => {})
      );
      await Promise.all(persistPromises);

      // Set final progress
      setBulkIdentifyProgress(prev => new Map(prev).set(templateId, {
        total: response.total,
        completed: response.completed,
        errors: response.errors,
      }));
    } catch (err) {
      setBulkIdentifyProgress(prev => new Map(prev).set(templateId, {
        total: annotations.length,
        completed: 0,
        errors: annotations.length,
      }));
    }

    // Clear progress after brief delay so user sees final state
    setTimeout(() => {
      setBulkIdentifyProgress(prev => {
        const m = new Map(prev);
        m.delete(templateId);
        return m;
      });
    }, 3000);
  };

  // Handle template SME capture submission
  const handleTemplateSmeSubmit = async () => {
    if (!templateSmeCapture || !selectedDoc) return;
    
    const { annotationId, annotation } = templateSmeCapture;
    const smeInput = templateSmeCaptureInput.trim() || undefined;
    
    const pendingId = `pending-template-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    
    // Add to pending queue
    setPendingTemplateItems((prev) => [
      ...prev,
      {
        id: pendingId,
        annotationId,
        templateName: annotation.template_name || 'Template',
        templateCategory: annotation.template_category,
        bbox: { x: annotation.x, y: annotation.y, width: annotation.width, height: annotation.height },
        status: 'running',
        smeInput,
      },
    ]);
    
    // Close SME capture popup
    setTemplateSmeCapture(null);
    setTemplateSmeCaptureInput("");
    
    // Check if this was the last navigable annotation
    const currentNavigable = getNavigableAnnotations();
    const remainingAfterSubmit = currentNavigable.filter(a => a.id !== annotationId);

    if (remainingAfterSubmit.length === 0) {
      // Last annotation submitted -- show toast, don't navigate
      setTemplateToast({ message: 'All annotations identified! Review the results in the queue.', type: 'success' });
    } else {
      // Navigate to next annotation if available
      // Use setTimeout to ensure state updates have propagated
      setTimeout(() => {
        handleNavigateAnnotation('next');
      }, 100);
    }
    
    // Fire AI identification in background
    try {
      const result = await templateApi.identifyAnnotation(selectedDoc.id, annotationId, smeInput);
      
      if (result.success) {
        updatePendingTemplateItem(pendingId, {
          status: 'ready',
          initialValues: {
            instrument_text: result.instrument_text ?? "",
            instrument_type: result.instrument_type ?? null,
            ...(result.core_fields ?? {}),
          },
        });
      } else {
        updatePendingTemplateItem(pendingId, {
          status: 'error',
          error: result.notes || 'Identification failed',
        });
      }
    } catch (error) {
      updatePendingTemplateItem(pendingId, {
        status: 'error',
        error: error instanceof Error ? error.message : 'Identification failed',
      });
    }
  };
  
  // Cancel template SME capture
  const handleTemplateSmeCancel = () => {
    setTemplateSmeCapture(null);
    setTemplateSmeCaptureInput("");
    setActiveAnnotationId(null); // Clear active annotation so it returns to status color
  };
  
  // Open pending template item (show mark dialog)
  const openPendingTemplateItem = (id: string) => {
    const item = pendingTemplateItems.find((entry) => entry.id === id);
    if (!item) return;
    
    const annotation = savedTemplateAnnotations.find(a => a.id === item.annotationId);
    if (!annotation) return;
    
    setActiveAnnotationId(item.annotationId);
    
    // Open unified InstrumentEditor for the annotation
    const editorPos = {
      x: Math.min(window.innerWidth / 2, window.innerWidth - 420),
      y: Math.max(80, window.innerHeight / 2 - 200),
    };
    handleRightClickInstrument(item.annotationId, editorPos);
  };
  
  // Retry pending template item
  const retryPendingTemplateItem = async (id: string) => {
    const item = pendingTemplateItems.find((entry) => entry.id === id);
    if (!item || !selectedDoc) return;
    
    updatePendingTemplateItem(id, { status: 'running', error: undefined });
    
    try {
      const result = await templateApi.identifyAnnotation(selectedDoc.id, item.annotationId, item.smeInput);
      
      if (result.success) {
        updatePendingTemplateItem(id, {
          status: 'ready',
          initialValues: {
            instrument_text: result.instrument_text ?? "",
            instrument_type: result.instrument_type ?? null,
            ...(result.core_fields ?? {}),
          },
        });
      } else {
        updatePendingTemplateItem(id, {
          status: 'error',
          error: result.notes || 'Identification failed',
        });
      }
    } catch (error) {
      updatePendingTemplateItem(id, {
        status: 'error',
        error: error instanceof Error ? error.message : 'Identification failed',
      });
    }
  };

  // Sort annotations for navigation: by template class, then by vertical band (left to right)
  const sortAnnotationsForNavigation = useCallback((annotations: TemplateAnnotation[], canvasWidth: number = 10000) => {
    const bandWidth = canvasWidth / 5; // Divide into 5 vertical bands
    return [...annotations].sort((a, b) => {
      // Primary: template_id (group by class)
      if (a.template_id !== b.template_id) return a.template_id.localeCompare(b.template_id);
      // Secondary: vertical band (left to right)
      const bandA = Math.floor(a.x / bandWidth);
      const bandB = Math.floor(b.x / bandWidth);
      if (bandA !== bandB) return bandA - bandB;
      // Tertiary: Y coordinate within band
      return a.y - b.y;
    });
  }, []);
  
  // Get filtered and sorted annotations for navigation
  const getNavigableAnnotations = useCallback(() => {
    const pendingAnnotationIds = new Set(
      pendingTemplateItems.map(p => p.annotationId)
    );

    const filtered = unverifiedOnlyFilter
      ? savedTemplateAnnotations.filter(
          a => a.status === 'unverified' && !pendingAnnotationIds.has(a.id)
        )
      : savedTemplateAnnotations.filter(
          a => !pendingAnnotationIds.has(a.id)
        );

    return sortAnnotationsForNavigation(filtered);
  }, [savedTemplateAnnotations, unverifiedOnlyFilter, sortAnnotationsForNavigation, pendingTemplateItems]);
  
  // Navigate to next/previous annotation
  const handleNavigateAnnotation = useCallback((direction: 'prev' | 'next') => {
    const navigable = getNavigableAnnotations();
    if (navigable.length === 0) {
      setTemplateSmeCapture(null);
      setTemplateSmeCaptureInput("");
      setTemplateToast({ message: 'No more unverified annotations to navigate to.', type: 'info' });
      return;
    }
    
    const currentIndex = navigable.findIndex(a => a.id === activeAnnotationId);

    // Only one navigable annotation and it's the current one -- nowhere to go
    if (navigable.length === 1 && currentIndex === 0) {
      setTemplateToast({ message: 'No more unverified annotations to navigate to.', type: 'info' });
      return;
    }
    let nextIndex: number;
    
    if (currentIndex === -1) {
      // No active annotation, start from beginning or end
      nextIndex = direction === 'next' ? 0 : navigable.length - 1;
    } else {
      nextIndex = direction === 'next' 
        ? (currentIndex + 1) % navigable.length
        : (currentIndex - 1 + navigable.length) % navigable.length;
    }
    
    const nextAnnotation = navigable[nextIndex];
    if (nextAnnotation) {
      setActiveAnnotationId(nextAnnotation.id);
      // Trigger the appropriate action based on status
      const action = nextAnnotation.status === 'unverified' ? 'sme-capture' : 'edit';
      handleAnnotationClick(nextAnnotation.id, action);
    }
  }, [getNavigableAnnotations, activeAnnotationId, handleAnnotationClick]);
  
  // Keyboard navigation for Step 2 (Templates)
  useEffect(() => {
    if (wizardStep !== 2) return;
    
    const handleKeyDown = (e: KeyboardEvent) => {
      // Skip if focus is in an input/textarea
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT') {
        return;
      }
      
      // Tab = next, Shift+Tab = prev. Arrow keys pan the canvas instead, so
      // one drawing-wide meaning for them holds in every wizard step.
      if (e.key === 'Tab') {
        e.preventDefault();
        handleNavigateAnnotation(e.shiftKey ? 'prev' : 'next');
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [wizardStep, handleNavigateAnnotation]);
  
  // Auto-dismiss template toast after 3 seconds
  useEffect(() => {
    if (!templateToast) return;
    const timer = setTimeout(() => setTemplateToast(null), 3000);
    return () => clearTimeout(timer);
  }, [templateToast]);

  // Focus the SME input when popup appears
  useEffect(() => {
    if (templateSmeCapture && templateSmeInputRef.current) {
      // Small delay to ensure the input is rendered
      setTimeout(() => {
        templateSmeInputRef.current?.focus();
      }, 50);
    }
  }, [templateSmeCapture]);
  
  // Compute template annotation counts for ContextBar
  const templateAnnotationCount = useMemo(() => {
    const total = savedTemplateAnnotations.length;
    const verified = savedTemplateAnnotations.filter(
      a => a.status === 'final_and_reviewed' || a.status === 'final'
    ).length;
    return { total, verified };
  }, [savedTemplateAnnotations]);

  const buildCandidateInstrumentText = (
    candidate: NodePointCandidate,
    fallbackLabel: string
  ) => {
    const tag = candidate.tagText?.trim() || "";
    const symbol =
      candidate.symbolMatch?.trim() || candidate.identifiedText?.trim() || "";
    if (tag) {
      return symbol ? `${tag} (${symbol})` : tag;
    }
    return symbol || fallbackLabel;
  };

  const runNodePointClassification = async (
    nodeId: string,
    polyline: number[]
  ) => {
    if (!ENABLE_NODE_POINT_CLASSIFICATION) return;
    if (!selectedDoc) return;
    if (!polyline || polyline.length < 4) return;

    const points = polylineToPoints(polyline);
    if (points.length === 0) return;

    const runId = nodePointRunId.current + 1;
    nodePointRunId.current = runId;

    const initialCandidates: NodePointCandidate[] = points.map((point) => ({
      id: crypto.randomUUID(),
      nodeId,
      point,
      status: "running",
      options: [],
      source: "classification",
    }));
    setNodePointCandidates(initialCandidates);

    const updateCandidate = (
      id: string,
      patch: Partial<NodePointCandidate>
    ) => {
      if (nodePointRunId.current !== runId) return;
      setNodePointCandidates((prev) =>
        prev.map((candidate) =>
          candidate.id === id ? { ...candidate, ...patch } : candidate
        )
      );
    };

    const processCandidate = async (candidate: NodePointCandidate) => {
      let bbox: BoundingBox | null = null;
      let samError: string | undefined;
      const samStart = Date.now();
      try {
        const samResult = await documentApi.samSegment(
          selectedDoc.id,
          Math.round(candidate.point.x),
          Math.round(candidate.point.y)
        );
        bbox = {
          x1: samResult.bbox.x1,
          y1: samResult.bbox.y1,
          x2: samResult.bbox.x2,
          y2: samResult.bbox.y2,
        };
        trackAction('sam_detection_completed', {
          latency_ms: Date.now() - samStart,
          success: true,
        });
      } catch (error) {
        console.error("SAM detection failed for node point:", error);
        samError = "SAM failed";
        bbox = pointToBbox(candidate.point, 24);
        trackAction('sam_detection_completed', {
          latency_ms: Date.now() - samStart,
          success: false,
        });
      }

      updateCandidate(candidate.id, { bbox, error: samError });

      try {
        const classify = await pnidApi.classifyInstrumentCandidates(
          selectedDoc.id,
          bbox!,
          "node",
          nodeId,
          3
        );
        const result = classify.result || {};
        const options = (result.candidates || []).map((item) => ({
          label: item.label as NodePointCandidateLabel,
          score: item.score,
        }));

        updateCandidate(candidate.id, {
          status: "ready",
          options,
          identifiedText: result.identified_text ?? null,
          symbolMatch: result.symbol_match ?? null,
          tagText: result.tag_text ?? null,
          confidence: result.confidence ?? null,
        });
      } catch (error) {
        console.error("Classification failed for node point:", error);
        updateCandidate(candidate.id, {
          status: "error",
          error: "Classification failed",
        });
      }
    };

    const runWithConcurrency = async <T,>(
      items: T[],
      limit: number,
      task: (item: T) => Promise<void>
    ) => {
      const executing = new Set<Promise<void>>();
      for (const item of items) {
        const promise = task(item);
        executing.add(promise);
        const cleanup = () => executing.delete(promise);
        promise.then(cleanup).catch(cleanup);
        if (executing.size >= limit) {
          await Promise.race(executing);
        }
      }
      await Promise.all(executing);
    };

    await runWithConcurrency(initialCandidates, 2, processCandidate);
  };

  const resolveNodePointCandidate = async (
    candidateId: string,
    label: NodePointCandidateLabel | "ignore"
  ) => {
    if (!selectedDoc || !pnidState) return;
    const candidate = nodePointCandidates.find(
      (item) => item.id === candidateId
    );
    if (!candidate) return;
    const isBranchCandidate = candidate.source === "branch";
    if (!ENABLE_NODE_POINT_CLASSIFICATION && !isBranchCandidate) return;

    if (label === "ignore") {
      setNodePointCandidates((prev) =>
        prev.filter((item) => item.id !== candidateId)
      );
      return;
    }

    const isBranch = label === "Branch";
    const bbox = isBranch
      ? pointToBbox(candidate.point, 14)
      : candidate.bbox || pointToBbox(candidate.point, 24);
    // DECISION(2026-10-16, Abhishek Kumar): a branch saved from a candidate carries no
    // instrument text, the same as a branch marked by hand.
    // Instrument text is unique across the drawing, so "Branch to <line_no>" was refused
    // whenever two branches met lines sharing a line_no (two "BYPASS LINE"s on PR/13B);
    // the row already shows the target through `branch_ref`.
    const instrumentText = isBranch
      ? null
      : buildCandidateInstrumentText(candidate, label);
    const instrumentType = isBranch ? null : (label as InstrumentCategory);

    try {
      await instrumentApi.createInstrument(selectedDoc.id, {
        context_type: 'node',
        context_id: candidate.nodeId,
        instrument_type: instrumentType,
        instrument_text: instrumentText,
        bbox,
        branch_ref: isBranch ? candidate.branchRef ?? null : undefined,
        branch_direction: isBranch ? candidate.branchDirection ?? "IN" : null,
      });
      await refreshPnidState(selectedDoc.id);
      setNodePointCandidates((prev) =>
        prev.filter((item) => item.id !== candidateId)
      );
    } catch (error) {
      console.error("Failed to create node instrument from candidate:", error);
      showAlert(
        "Save failed",
        "Could not save this instrument. Please try again.",
        "error"
      );
    }
  };

  const handleCanvasInteraction = useCallback((counts: Record<string, number>) => {
    trackAction('canvas_interaction_batch', { ...counts, flush_interval_s: 10 });
  }, [trackAction]);

  // Trigger instrument drawing mode from the mark dialog's "Mark Instrument" button
  const handleMarkInstrumentFromDialog = useCallback((contextType: 'equipment' | 'node', contextId: string) => {
    instrumentContextOverrideRef.current = { contextType, contextId };
    setBoxDraft(armDraft('instrument'));
  }, []);

  /**
   * The equipment or line a new instrument hangs off. The symbol region decides it, so
   * only `boxes[0]` is tested here — a balloon drawn out in the margin must not move the
   * instrument to whatever it happens to sit over. Null when nothing resolves, and the
   * instrument is then saved unattached.
   */
  const resolveInstrumentContext = (
    symbolRegion: BoundingBox,
  ): { contextType: 'equipment' | 'node'; contextId: string } | null => {
    if (!pnidState) return null;

    // Use explicit context override if set (from "Mark Instrument" button in dialog)
    const override = instrumentContextOverrideRef.current;
    if (override) return override;

    // Auto-detect context based on current step and active item
    if (wizardStep === 3 && activeEquipmentId) {
      return { contextType: 'equipment', contextId: activeEquipmentId };
    }
    if (wizardStep === 4 && activeNodeId) {
      return { contextType: 'node', contextId: activeNodeId };
    }

    // Fallback: auto-detect from the symbol's position
    const allEquipments = pnidState.equipments ?? [];
    const overlappingEquipment = allEquipments.find(
      (equipment) =>
        equipment.status !== 'ai_identified' && equipment.bbox && bboxesOverlap(symbolRegion, equipment.bbox),
    );
    if (overlappingEquipment) return { contextType: 'equipment', contextId: overlappingEquipment.id };

    const allNodes = pnidState.nodes ?? [];
    const crossedNode = allNodes.find(
      (node) =>
        node.status !== 'ai_identified' &&
        node.polyline &&
        node.polyline.length >= 4 &&
        polylineIntersectsBbox(node.polyline, symbolRegion),
    );
    if (crossedNode) return { contextType: 'node', contextId: crossedNode.id };

    const overlappingDuct = allNodes.find(
      (node) =>
        node.status !== 'ai_identified' && node.is_duct && node.duct_bbox && bboxesOverlap(node.duct_bbox, symbolRegion),
    );
    if (overlappingDuct) return { contextType: 'node', contextId: overlappingDuct.id };

    return null;
  };

  /**
   * Commits a draft collected for equipment, a line, or a control loop. These stay
   * single-region, so the redraw is the same one-box update it has always been.
   */
  const commitSingleRegionRedraw = async (target: GeometryTarget, bbox: BoundingBox) => {
    if (!selectedDoc || !pnidState) return;
    try {
      pushPnidUndoSnapshot("Rebox annotation");
      if (target.kind === "equipment") {
        const updated = await pnidApi.updateEquipment(selectedDoc.id, target.id, {
          bbox,
          status: "in_progress",
        });
        setPnidState({
          ...pnidState,
          equipments: pnidState.equipments.map((e) => (e.id === updated.id ? updated : e)),
        });
      } else if (target.kind === "node") {
        const existingNode = pnidState.nodes.find((node) => node.id === target.id);
        if (diagramMode === 'pfd' && existingNode?.is_duct) {
          showAlert('P&ID-only geometry', 'Existing ducts remain visible in PFD mode, but duct authoring is available only in P&ID mode.', 'info');
          setBoxDraft(null);
          return;
        }
        const isDuct = Boolean(existingNode?.is_duct);
        const polyline = bbox.polygon || [];
        const updated = await pnidApi.updateNode(selectedDoc.id, target.id, {
          polyline: isDuct ? null : polyline,
          duct_bbox: isDuct ? bbox : null,
          status: "in_progress",
        });
        setPnidState({
          ...pnidState,
          nodes: pnidState.nodes.map((n) => (n.id === updated.id ? updated : n)),
        });
      } else if (target.kind === "controlLoop") {
        const updated = await pnidApi.updateControlLoop(selectedDoc.id, target.id, {
          bbox,
          status: "in_progress",
        });
        setPnidState({
          ...pnidState,
          control_loops: pnidState.control_loops.map((loop) => (loop.id === updated.id ? updated : loop)),
        });
      }
      setBoxDraft(null);
      setFocusBbox(bbox);
      showAlert("Marked", "Marked successfully.", "success");
    } catch (error) {
      console.error(error);
      showAlert("Mark failed", "Could not mark item.", "error");
    }
  };

  /**
   * Commits a draft collected for an instrument: one write and one identify, fired
   * together and only here. A draft the SME abandons therefore leaves no row behind,
   * and a failure keeps the regions so the next Done can retry them.
   */
  const commitInstrumentRegions = async (draft: BoxDraft) => {
    if (!selectedDoc?.id || !pnidState) return;
    const symbolRegion = draft.boxes[0];
    const target = draft.target;

    const existing = target ? pnidState.instruments.find((inst) => inst.id === target.id) : undefined;
    if (target && !existing) {
      showAlert("Mark failed", "Could not find the instrument to mark.", "error");
      return;
    }

    // A redraw arms with the marking's own regions already in the draft, so what is
    // committed is what the SME has been looking at and popping from. Nothing is
    // replaced behind their back, and the one destructive move — popping the final
    // region — asks on the spot.
    const existingRegions = existing ? instrumentRegions(existing) : [];
    const bboxes = committedRegions(draft, existingRegions);

    // Only a brand-new instrument resolves a parent; a redraw already hangs off one.
    let writeInstrument: () => Promise<PnidInstrument>;
    if (existing) {
      writeInstrument = () =>
        instrumentApi.updateInstrument(selectedDoc.id, existing.id, { bboxes, status: 'in_progress' });
    } else {
      // DECISION(2026-09-07, Abhishek): An instrument drawn with no resolvable parent is saved with a null context instead of being rejected.
      // Some P&IDs contain no equipment and no lines, so no parent can exist. There is no way to distinguish a deliberate orphan from one that lost its parent, and both are context_type: null.
      const context = resolveInstrumentContext(symbolRegion);
      writeInstrument = () =>
        instrumentApi.createInstrument(selectedDoc.id, {
          context_type: context?.contextType ?? null,
          context_id: context?.contextId ?? null,
          bboxes,
          status: 'in_progress',
        });
    }

    // Identify runs alongside the write so the editor waits on whichever is slower
    // rather than on the two in sequence. A failure here costs the SME a pre-fill,
    // never the instrument, so it resolves to null instead of rejecting.
    setIdentifyingBboxes(draft.boxes);
    const identified = assetApi
      .identify(selectedDoc.id, { bboxes })
      .catch((err) => {
        console.warn('Identify failed; the instrument editor opens empty:', err);
        return null;
      })
      .finally(() => setIdentifyingBboxes([]));

    try {
      if (existing) pushPnidUndoSnapshot(target?.op === 'append' ? "Add instrument region" : "Rebox annotation");
      const saved = await writeInstrument();
      instrumentContextOverrideRef.current = null;
      // Drawing a new instrument re-arms, so an SME marking a sheet draws one after
      // another without returning to the toolbar between them. A redraw or an added
      // region is finished work for one instrument, so it disarms.
      setBoxDraft(target ? null : armDraft('instrument'));
      await refreshPnidState(selectedDoc.id);

      // A redraw re-focuses the wizard on the instrument's owner, the way Rebox has.
      if (target?.kind === 'equipmentInstrument') setActiveEquipmentId(saved.context_id);
      else if (target?.kind === 'nodeInstrument') setActiveNodeId(saved.context_id);

      const instrument = await instrumentApi.getInstrument(selectedDoc.id, saved.id);
      // The kind decides which dialog the answer belongs in. This draw path opens the
      // instrument editor, so any other kind is a suggestion it cannot show.
      const suggestion = await identified;
      if (suggestion && suggestion.kind !== 'instrument') {
        console.warn(`Identify answered with kind "${suggestion.kind}"; the instrument editor opens empty.`);
      }
      const anchorBox = saved.bbox ?? symbolRegion;
      setSelectedInstrumentId(saved.id);
      setInstrumentEditorState({
        instrument,
        position: existing
          ? {
              x: Math.min((anchorBox.x1 + anchorBox.x2) / 2 + 200, window.innerWidth - 420),
              y: Math.max(80, anchorBox.y1),
            }
          : instrumentEditorPosition(),
        identifyPrefill: suggestion?.kind === 'instrument' ? suggestion.fields : {},
      });
      setFocusBbox(symbolRegion);
    } catch (err) {
      console.error('Failed to save instrument regions:', err);
      showAlert(
        existing ? 'Mark failed' : 'Create failed',
        existing ? 'Could not mark item.' : 'Could not create instrument.',
        'error',
      );
    }
  };

  /** Done: the collected regions become one element. Fires once, at the end of the draw. */
  const handleFinishRegions = async () => {
    const draft = boxDraft;
    if (!draft || !canFinish(draft)) return;
    const target = draft.target;
    if (target && target.kind !== 'equipmentInstrument' && target.kind !== 'nodeInstrument') {
      await commitSingleRegionRedraw(target, draft.boxes[0]);
      return;
    }
    await commitInstrumentRegions(draft);
  };

  /**
   * Escape with regions collected: back out the last one and stay armed.
   *
   * A redraw seeds the draft with the marking's saved regions, so Escape pops those
   * as well — one list, popped from the end. Popping the last region of an existing
   * marking would leave it with no geometry, which is a delete, so it asks first.
   */
  const handleRemoveLastRegion = () => {
    const draft = boxDraft;
    if (!draft) return;
    const target = draft.target;
    if (target && draft.boxes.length === 1) {
      setConfirmDialog({
        isOpen: true,
        title: "Delete this marking?",
        message: "That is its only region. Removing it deletes the marking from the sheet.",
        type: "danger",
        confirmText: "Delete marking",
        onConfirm: () => {
          setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
          setBoxDraft(null);
          instrumentContextOverrideRef.current = null;
          void handleInstrumentEditorDelete(target.id);
        },
      });
      return;
    }
    setBoxDraft(removeLastRegion(draft));
  };

  /** Escape on an empty draft: leave the collect mode entirely. */
  const handleExitCollect = () => {
    setBoxDraft(null);
    instrumentContextOverrideRef.current = null;
  };

  const handleWizardDraw = async (
    bbox: BoundingBox,
    anchor: { x: number; y: number }
  ) => {
    if (!selectedDoc) return;
    trackAction('item_drawn_on_canvas');

    // Collecting: a drag adds a region to the draft and does nothing else. The write
    // waits for Done, so an abandoned drawing leaves nothing behind.
    if (boxDraft) {
      setBoxDraft(appendRegion(boxDraft, bbox));
      return;
    }

    if (wizardStep === 1) {
      showAlert(
        "Setup first",
        "Step 1 does not create marks. Set PnID No and move to Step 3.",
        "info"
      );
      return;
    }

    if (wizardStep === 3) {
      const selectedEq = pnidState?.equipments.find(
        (e) => e.id === activeEquipmentId
      );
      if (selectedEq && selectedEq.status === "ai_identified") {
        openMarkDialog("equipment", bbox, anchor, {
          equipment_no: selectedEq.equipment_no,
          equipment_type: selectedEq.equipment_type,
          description: selectedEq.description,
          pnid_no: selectedEq.pnid_no ?? pnidState?.pnid_no ?? "",
          details: selectedEq.details,
          equipment_data: selectedEq.equipment_data,
          ai_instruments: selectedEq.ai_instruments,
          connected_equipments: selectedEq.connected_equipments ?? [],
          // The stored owner rides along because the save always states the
          // owner. Without it a foreign owner would read as this drawing's own,
          // and the save would hand the asset back to this drawing.
          owner_domain_id: selectedEq.owner_domain_id,
          _markExistingId: selectedEq.id,
        });
      } else {
        openMarkDialog("equipment", bbox, anchor, {
          pnid_no: pnidState?.pnid_no ?? "",
        });
      }
      return;
    }

    if (wizardStep === 4) {
      if (diagramMode === 'pid' && nodeDrawingShape === 'duct') {
        const selectedNode = activeNodeId
          ? pnidState?.nodes.find((node) => node.id === activeNodeId) ?? null
          : null;
        const bindToSelectedNode = Boolean(
          selectedNode && (isNodeMarkingMode || selectedNode.status === 'ai_identified')
        );
        setLineMarkPopover({
          nodeId: bindToSelectedNode ? selectedNode!.id : null,
          position: getDefaultLinePopoverPosition(),
          values: {
            line_no: selectedNode?.line_no ?? '',
            source_ref: selectedNode?.source_ref ?? '',
            source_pnid: selectedNode?.source_pnid ?? pnidState?.pnid_no ?? '',
            dest_ref: selectedNode?.dest_ref ?? '',
            dest_pnid: selectedNode?.dest_pnid ?? pnidState?.pnid_no ?? '',
            ...getNodeLineProperties(selectedNode),
            drain_type: selectedNode?.drain_type ?? null,
            is_duct: true,
          },
          stage: 'source',
          isOpen: true,
          pendingDraw: { bbox, anchor },
          aiDetectedSource: false,
          aiDetectedDest: false,
        });
        return;
      }
      // Auto-detect destination from path endpoint
      const polygon = bbox.polygon || [];
      let detectedDest: { ref: string } | null = null;
      let destPoint: { x: number; y: number } | undefined;
      if (polygon.length >= 4) {
        destPoint = {
          x: polygon[polygon.length - 2],
          y: polygon[polygon.length - 1],
        };
        detectedDest = detectOverlappingEntity(
          destPoint,
          pnidState?.equipments ?? [],
          pnidState?.nodes ?? [],
          { bboxThreshold: 60, polylineThreshold: 40 }
        );
      }

      // Flag for triggering Groq fallback
      const needsGroqFallback = !detectedDest && groqEnabled && destPoint;

      setLineMarkPopover((prev) => {
        const boundNodeId =
          prev?.nodeId ?? (isNodeMarkingMode ? activeNodeId : null);
        const boundNode = boundNodeId
          ? pnidState?.nodes.find((n) => n.id === boundNodeId) ?? null
          : null;

        const defaultLineNo = boundNode?.line_no?.trim()
          ? boundNode.line_no
          : "";

        const seededValues = prev?.values ?? {
          line_no: defaultLineNo ?? "",
          source_ref: boundNode?.source_ref ?? "",
          source_pnid: boundNode?.source_pnid ?? pnidState?.pnid_no ?? "",
          dest_ref: boundNode?.dest_ref ?? "",
          dest_pnid: boundNode?.dest_pnid ?? pnidState?.pnid_no ?? "",
          ...getNodeLineProperties(boundNode),
          drain_type: boundNode?.drain_type ?? null,
          is_duct: false,
        };

        const destRef = seededValues.dest_ref?.trim()
          ? seededValues.dest_ref
          : detectedDest?.ref ?? seededValues.dest_ref;
        const lineNo = seededValues.line_no?.trim()
          ? seededValues.line_no
          : defaultLineNo ?? seededValues.line_no;

        return {
          ...(prev ?? {}),
          nodeId: boundNode?.id ?? prev?.nodeId ?? null,
          position: prev?.position ?? getDefaultLinePopoverPosition(),
          stage: "destination",
          isOpen: true,
          destPoint,
          pendingDraw: { bbox, anchor },
          values: {
            ...seededValues,
            line_no: lineNo ?? "",
            dest_ref: destRef ?? "",
            drain_type:
              seededValues.drain_type ?? boundNode?.drain_type ?? null,
          },
          aiDetectedDest: Boolean(detectedDest) || prev?.aiDetectedDest,
        };
      });

      // Trigger Groq fallback if statistical detection failed
      if (needsGroqFallback && selectedDoc) {
        setIsDetectingDest(true);
        pnidApi
          .detectEndpoint(selectedDoc.id, destPoint!, "destination")
          .then((result) => {
            if (result.enabled && result.result?.ref) {
              setLineMarkPopover((prev) =>
                prev && !prev.values.dest_ref
                  ? {
                      ...prev,
                      values: {
                        ...prev.values,
                        dest_ref: result.result!.ref,
                        // Use detected pnid if available, otherwise keep current (default) pnid
                        dest_pnid: result.result!.pnid || prev.values.dest_pnid,
                      },
                      aiDetectedDest: true,
                    }
                  : prev
              );
            }
          })
          .catch((err) => console.error("Groq dest detection failed:", err))
          .finally(() => setIsDetectingDest(false));
      }
      return;
    }

    // Step 6 (Line Instruments) removed - consolidated into wizardStep === 2

    if (wizardStep === 5) {
      if (!activeControlLoopId) {
        showAlert(
          "Select control loop",
          "Pick a control loop first (left panel), then mark it on the canvas.",
          "warning"
        );
        return;
      }
      // Update the control loop with the new bbox
      if (!pnidState) return;
      try {
        const updated = await pnidApi.updateControlLoop(
          selectedDoc.id,
          activeControlLoopId,
          { bbox }
        );
        const newState: PnidWizardState = {
          ...pnidState,
          control_loops: pnidState.control_loops.map((l) =>
            l.id === updated.id ? updated : l
          ),
        };
        setPnidState(newState);
        setFocusBbox(bbox);
        showAlert("Marked", `Control loop marked on canvas.`, "success");
      } catch (error) {
        console.error("Failed to mark control loop:", error);
        showAlert("Error", "Failed to mark control loop.", "error");
      }
      return;
    }
  };

  /** The saved P&ID equipment the mark dialog is editing, if any.
   *  A compound tag can only be split on an equipment that already exists. */
  const compoundSplitTargetId =
    diagramMode === "pfd" || markDialog.kind !== "equipment"
      ? null
      : markDialog.editTarget?.kind === "equipment"
        ? markDialog.editTarget.id
        : (markDialog.initialValues?._markExistingId as string | undefined) ?? null;

  /** The type the split's new assets would inherit, read from the same stored
   *  record the split sends it from. */
  const compoundSplitInheritedType =
    (compoundSplitTargetId
      ? pnidState?.equipments.find((e) => e.id === compoundSplitTargetId)?.equipment_type
      : null) ?? null;

  /**
   * Splits a compound-tagged equipment on the open drawing into one asset per
   * part, and brings the drawing back in step with what the run wrote. Shared by
   * the equipment edit panel and the review table's split badge.
   */
  const splitCompoundEquipmentOnDrawing = useCallback(
    async (equipmentId: string, parts: CompoundSplitPart[]) => {
      if (!selectedDoc) {
        throw new Error("This equipment is no longer open. Reopen it and split again.");
      }
      const docId = selectedDoc.id;
      const savedTags = await splitCompoundEquipment({
        docId,
        equipmentId,
        parts,
        refresh: async () => {
          await refreshPnidState(docId);
        },
      });
      // The rename resets the marks the old tag carried and copies the new tag's
      // family onto the row, so the register no longer says what it said before
      // the split — the same reason a row rename invalidates it.
      invalidateRegisterContext(docId);
      return savedTags;
    },
    [refreshPnidState, selectedDoc]
  );

  /** The edit panel's split: the same run, followed by the panel's own close and
   *  the alert naming what is now on the drawing. */
  const handleSplitCompoundEquipment = async (parts: CompoundSplitPart[]) => {
    if (!compoundSplitTargetId) {
      throw new Error("This equipment is no longer open. Reopen it and split again.");
    }
    const savedTags = await splitCompoundEquipmentOnDrawing(compoundSplitTargetId, parts);
    closeMarkDialog();
    showAlert(
      "Compound tag split",
      `${savedTags.join(", ")} are now separate assets on this drawing.`,
      "success"
    );
  };

  const handleMarkDialogSubmit = async (values: Record<string, any>) => {
    if (!selectedDoc) return;
    if (!pnidState) return;
    // Only require bbox for new items (not edits or marking existing AI items)
    const isEdit = Boolean(markDialog.editTarget?.id);
    const isMarkingExisting = Boolean(values._markExistingId);
    if (!markDialog.bbox && !isEdit && !isMarkingExisting) return;

    let savedEquipmentId: string | null = null;
    let savedNodeId: string | null = null;
    let savedItemId: string | null = null;
    const savedParentId: string | null = null;

    try {
      if (markDialog.kind === "equipment") {
        const markExistingId = values._markExistingId;

        const connectionsChanged = Array.isArray(values.connected_equipments);
        const pfdLinksChanged = Array.isArray(values.linked_pfds);
        if (markDialog.editTarget?.id) {
          const updated = await pnidApi.updateEquipment(
            selectedDoc.id,
            markDialog.editTarget.id,
            buildEquipmentUpdatePayload(values, connectionsChanged, pfdLinksChanged)
          );
          let next: PnidWizardState;
          if (connectionsChanged || pfdLinksChanged) {
            // Peers/cross-document targets were mirrored server-side — refetch current doc.
            next = await pnidApi.get(selectedDoc.id);
          } else {
            next = {
              ...pnidState,
              equipments: pnidState.equipments.map((e) =>
                e.id === updated.id ? updated : e
              ),
            };
          }
          setPnidState(next);
          setActiveEquipmentId(updated.id);
          setFocusBbox(updated.bbox ?? null);
          savedEquipmentId = updated.id;
          savedItemId = updated.id;
        } else if (markExistingId) {
          const updated = await pnidApi.updateEquipment(
            selectedDoc.id,
            markExistingId,
            {
              ...buildEquipmentUpdatePayload(values, connectionsChanged, pfdLinksChanged),
              bbox: markDialog.bbox,
            }
          );
          let next: PnidWizardState;
          if (connectionsChanged || pfdLinksChanged) {
            next = await pnidApi.get(selectedDoc.id);
          } else {
            next = {
              ...pnidState,
              equipments: pnidState.equipments.map((e) =>
                e.id === updated.id ? updated : e
              ),
            };
          }
          setPnidState(next);
          setActiveEquipmentId(updated.id);
          setFocusBbox(updated.bbox ?? null);
          savedEquipmentId = updated.id;
          savedItemId = updated.id;
          const nextUnmarked = next.equipments.find(
            (e) => e.status === "ai_identified"
          );
          if (nextUnmarked) {
            setActiveEquipmentId(nextUnmarked.id);
          }
        } else {
          const created = await pnidApi.createEquipment(selectedDoc.id, {
            equipment_no: values.equipment_no,
            equipment_type: values.equipment_type,
            subtype: values.subtype ?? null,
            description: values.description,
            pnid_no: values.pnid_no ?? null,
            bbox: markDialog.bbox,
            painting_spec: values.painting_spec?.trim() || null,
            notes: values.notes ?? null,
            linked_pfds: pfdLinksChanged ? values.linked_pfds : null,
            ...(values.is_primary_source !== undefined
              ? { is_primary_source: values.is_primary_source }
              : {}),
            owner_domain_id: values.owner_domain_id ?? '',
          });
          const next = {
            ...pnidState,
            equipments: [...pnidState.equipments, created],
          };
          setPnidState(next);
          setActiveEquipmentId(created.id);
          setFocusBbox(created.bbox ?? null);
          savedEquipmentId = created.id;
          savedItemId = created.id;
        }
        if (values.is_primary_source !== undefined) {
          // The saved mark is part of the register's answer for this drawing, so
          // the edit panel and the review table must re-read it: their locks and
          // their Primary column were computed before the reviewer marked it.
          invalidateRegisterContext(selectedDoc.id);
        }
      }

      if (markDialog.kind === "node") {
        const markExistingId = values._markExistingId;

        if (markDialog.editTarget?.id) {
          const existingNode = pnidState.nodes.find((node) => node.id === markDialog.editTarget?.id);
          const nextIsDuct = Boolean(values.is_duct);
          const geometryModeChanged = Boolean(existingNode?.is_duct) !== nextIsDuct;
          if (geometryModeChanged && (existingNode?.polyline || existingNode?.duct_bbox)) {
            const confirmed = window.confirm(
              `Change this marked ${existingNode?.is_duct ? 'duct' : 'pipeline'} to a ${nextIsDuct ? 'duct' : 'pipeline'}? Its current geometry will be cleared and must be redrawn.`
            );
            if (!confirmed) return;
          }
          const updated = await pnidApi.updateNode(
            selectedDoc.id,
            markDialog.editTarget.id,
            {
              node_no: values.node_no ?? undefined,
              source_ref: values.source_ref,
              source_pnid: values.source_pnid,
              dest_ref: values.dest_ref,
              dest_pnid: values.dest_pnid,
              ...getNodeLineTypePayload(values),
              note: values.note ?? null,
              painting_spec: values.painting_spec?.trim() || null,
              is_duct: nextIsDuct,
              ...(geometryModeChanged ? { polyline: null, duct_bbox: null } : {}),
              ...(values.notes !== undefined ? { notes: values.notes || null } : {}),
              ...getNodeLinePropertyPayload(values),
              drain_type: values.drain_type ?? null,
            }
          );
          const next = {
            ...pnidState,
            nodes: pnidState.nodes.map((n) =>
              n.id === updated.id ? updated : n
            ),
          };
          setPnidState(next);
          setActiveNodeId(updated.id);
          setFocusBbox(nodeGeometryBbox(updated));
          savedNodeId = updated.id;
          savedItemId = updated.id;
        } else if (markExistingId) {
          const polyline = markDialog.bbox?.polygon || [];
          const isDuct = Boolean(values.is_duct);
          const updated = await pnidApi.updateNode(
            selectedDoc.id,
            markExistingId,
            {
              node_no: values.node_no ?? undefined,
              source_ref: values.source_ref,
              source_pnid: values.source_pnid,
              dest_ref: values.dest_ref,
              dest_pnid: values.dest_pnid,
              ...getNodeLineTypePayload(values),
              note: values.note ?? null,
              painting_spec: values.painting_spec?.trim() || null,
              is_duct: isDuct,
              ...(values.notes !== undefined ? { notes: values.notes || null } : {}),
              ...getNodeLinePropertyPayload(values),
              drain_type: values.drain_type ?? null,
              polyline: isDuct ? null : polyline,
              duct_bbox: isDuct ? markDialog.bbox : null,
            }
          );
          const next = {
            ...pnidState,
            nodes: pnidState.nodes.map((n) =>
              n.id === updated.id ? updated : n
            ),
          };
          setPnidState(next);
          setActiveNodeId(updated.id);
          setFocusBbox(
            updated.is_duct ? updated.duct_bbox ?? null : updated.polyline ? polylineToBbox(updated.polyline) : null
          );
          savedNodeId = updated.id;
          savedItemId = updated.id;
          if (!isDuct && polyline.length >= 4) {
            void runNodePointClassification(updated.id, polyline);
          }
          const nextUnmarked = next.nodes.find(
            (n) => n.status === "ai_identified"
          );
          if (nextUnmarked) {
            setActiveNodeId(nextUnmarked.id);
          }
        } else {
          const polyline = markDialog.bbox?.polygon || [];
          const isDuct = Boolean(values.is_duct);
          const created = await pnidApi.createNode(selectedDoc.id, {
            node_no: values.node_no ?? null,
            source_ref: values.source_ref,
            source_pnid: values.source_pnid,
            dest_ref: values.dest_ref,
            dest_pnid: values.dest_pnid,
            ...getNodeLineTypePayload(values),
            note: values.note ?? null,
            painting_spec: values.painting_spec?.trim() || null,
            is_duct: isDuct,
            notes: values.notes ?? null,
            ...getNodeLinePropertyPayload(values),
            drain_type: values.drain_type ?? null,
            polyline: isDuct ? null : polyline,
            duct_bbox: isDuct ? markDialog.bbox : null,
          });
          const next = { ...pnidState, nodes: [...pnidState.nodes, created] };
          setPnidState(next);
          setActiveNodeId(created.id);
          setFocusBbox(
            created.is_duct ? created.duct_bbox ?? null : created.polyline ? polylineToBbox(created.polyline) : null
          );
          savedNodeId = created.id;
          savedItemId = created.id;
          if (!isDuct && polyline.length >= 4) {
            void runNodePointClassification(created.id, polyline);
          }
        }
        // Clear the canvas path after successfully saving a node
        setCanvasClearSignal((prev) => prev + 1);
        setIsNodeMarkingMode(false);
        setNodeDrawingShape('pipeline');
        setLineMarkPopover(null);
      }

      // === Convert attached template annotations to instruments ===
      // This runs after equipment/node save to create instruments from attached annotations
      if (markDialog.kind === 'equipment') {
        const equipmentId = savedEquipmentId;
        if (equipmentId) {
          await syncTemplateAttachmentChanges('equipment', equipmentId);
        }
      } else if (markDialog.kind === 'node') {
        const nodeId = savedNodeId;
        if (nodeId) {
          await syncTemplateAttachmentChanges('node', nodeId);
        }
      }

      trackAction(isEdit ? 'item_edited' : 'item_marked', {
        item_type: markDialog.kind,
        item_id: savedItemId ?? markDialog.editTarget?.id ?? null,
        parent_id: savedParentId,
        field_change_count: values._field_change_count ?? 0,
        fields_changed_count: (values._fields_changed as string[] | undefined)?.length ?? 0,
      });

      const policyTargetId = savedItemId ?? markDialog.editTarget?.id ?? null;
      if (policyTargetId) {
        void checkObjectPolicy(
          markDialog.kind,
          policyTargetId,
          values.equipment_no || values.line_no || markDialog.kind,
        );
      }

      closeMarkDialog();
    } catch (error) {
      console.error(error);
      showAlert("Save failed", "Could not save this mark.", "error");
    }
  };

  const handleMarkDialogSubmitPfd = async (values: Record<string, any>) => {
    if (!selectedDoc || !pnidState) return;

    const isEdit = Boolean(markDialog.editTarget?.id);
    const isMarkingExisting = Boolean(values._markExistingId);
    if (!markDialog.bbox && !isEdit && !isMarkingExisting) return;

    let savedEquipmentId: string | null = null;
    let savedNodeId: string | null = null;
    let savedItemId: string | null = null;

    try {
      if (markDialog.kind === "equipment") {
        const targetId = markDialog.editTarget?.id ?? values._markExistingId ?? null;
        const existing = targetId ? pnidState.equipments.find((e) => e.id === targetId) : null;
        const payload = buildPfdPayload(values, pfdEquipmentSchema, PFD_EQUIPMENT_FIELD_KEYS, {
          details: existing?.details ?? {},
        });
        // Connected equipment is mirrored to peers server-side; refetch when it changes.
        const connectionsChanged = Array.isArray(values.connected_equipments);
        const pnidLinksChanged = Array.isArray(values.linked_pnids);

        if (targetId) {
          const updated = await pnidApi.updateEquipment(selectedDoc.id, targetId, {
            ...payload,
            painting_spec: values.painting_spec?.trim() || null,
            ...(values._markExistingId ? { bbox: markDialog.bbox } : {}),
          });
          const next = connectionsChanged || pnidLinksChanged
            ? await pnidApi.get(selectedDoc.id)
            : {
                ...pnidState,
                equipments: pnidState.equipments.map((e) => e.id === updated.id ? updated : e),
              };
          setPnidState(next);
          setActiveEquipmentId(updated.id);
          setFocusBbox(updated.bbox ?? null);
          savedEquipmentId = updated.id;
          savedItemId = updated.id;
        } else {
          const created = await pnidApi.createEquipment(selectedDoc.id, {
            equipment_no: payload.equipment_no,
            equipment_type: payload.equipment_type,
            description: payload.description,
            pnid_no: pnidState.pnid_no ?? null,
            bbox: markDialog.bbox,
            painting_spec: values.painting_spec?.trim() || null,
            details: payload.details ?? {},
            connected_equipments: payload.connected_equipments ?? null,
            linked_pnids: payload.linked_pnids ?? null,
            notes: payload.notes ?? null,
          });
          const next = connectionsChanged || pnidLinksChanged
            ? await pnidApi.get(selectedDoc.id)
            : { ...pnidState, equipments: [...pnidState.equipments, created] };
          setPnidState(next);
          setActiveEquipmentId(created.id);
          setFocusBbox(created.bbox ?? null);
          savedEquipmentId = created.id;
          savedItemId = created.id;
        }
      }

      if (markDialog.kind === "node") {
        const targetId = markDialog.editTarget?.id ?? values._markExistingId ?? null;
        const existing = targetId ? pnidState.nodes.find((n) => n.id === targetId) : null;
        const payload = buildPfdPayload(values, pfdNodeSchema, PFD_NODE_FIELD_KEYS, {
          line_spec: existing?.line_spec ?? {},
        });
        // Signal === P&ID: a selected instrument_line_subtype marks the stream an
        // instrument line and auto-generates the Stream No (<source>_<dest>_<signal>)
        // when blank — exactly like P&ID. getNodeLineTypePayload keys off
        // values.line_type, so derive it from whether a signal is set.
        const lineTypePayload = getNodeLineTypePayload({
          ...values,
          line_type: values.instrument_line_subtype ? "instrument" : "process",
        });

        if (targetId) {
          const polyline = values._markExistingId ? markDialog.bbox?.polygon || [] : undefined;
          const isDuct = Boolean(existing?.is_duct ?? values.is_duct);
          const updated = await pnidApi.updateNode(selectedDoc.id, targetId, {
            ...payload,
            ...lineTypePayload,
            // drain_type is set as a side-effect of the dest SourceDestSelector and
            // is not a schema field, so it is not in payload — pass it explicitly.
            drain_type: values.drain_type ?? null,
            painting_spec: values.painting_spec?.trim() || null,
            is_duct: isDuct,
            ...(polyline ? {
              polyline: isDuct ? null : polyline,
              duct_bbox: isDuct ? markDialog.bbox : null,
            } : {}),
          });
          const next = {
            ...pnidState,
            nodes: pnidState.nodes.map((n) => n.id === updated.id ? updated : n),
          };
          setPnidState(next);
          setActiveNodeId(updated.id);
          setFocusBbox(nodeGeometryBbox(updated));
          savedNodeId = updated.id;
          savedItemId = updated.id;
          if (!isDuct && polyline && polyline.length >= 4) {
            void runNodePointClassification(updated.id, polyline);
          }
        } else {
          const polyline = markDialog.bbox?.polygon || [];
          // RAV-844 duct authoring is intentionally P&ID-only.
          const isDuct = false;
          const created = await pnidApi.createNode(selectedDoc.id, {
            line_no: lineTypePayload.line_no,
            source_ref: payload.source_ref,
            source_pnid: payload.source_pnid,
            dest_ref: payload.dest_ref,
            dest_pnid: payload.dest_pnid,
            line_type: lineTypePayload.line_type,
            instrument_line_subtype: lineTypePayload.instrument_line_subtype,
            drain_type: values.drain_type ?? null,
            painting_spec: values.painting_spec?.trim() || null,
            is_duct: isDuct,
            line_spec: payload.line_spec ?? {},
            linked_pfd_streams: payload.linked_pfd_streams ?? null,
            polyline: isDuct ? null : polyline,
            duct_bbox: isDuct ? markDialog.bbox : null,
          });
          setPnidState({ ...pnidState, nodes: [...pnidState.nodes, created] });
          setActiveNodeId(created.id);
          setFocusBbox(nodeGeometryBbox(created));
          savedNodeId = created.id;
          savedItemId = created.id;
          if (!isDuct && polyline.length >= 4) {
            void runNodePointClassification(created.id, polyline);
          }
        }
        setCanvasClearSignal((prev) => prev + 1);
        setLineMarkPopover(null);
      }

      if (markDialog.kind === 'equipment' && savedEquipmentId) {
        await syncTemplateAttachmentChanges('equipment', savedEquipmentId);
      } else if (markDialog.kind === 'node' && savedNodeId) {
        await syncTemplateAttachmentChanges('node', savedNodeId);
      }

      trackAction(isEdit ? 'item_edited' : 'item_marked', {
        item_type: markDialog.kind,
        item_id: savedItemId ?? markDialog.editTarget?.id ?? null,
        field_change_count: values._field_change_count ?? 0,
        fields_changed_count: (values._fields_changed as string[] | undefined)?.length ?? 0,
      });

      closeMarkDialog();
    } catch (error) {
      console.error(error);
      showAlert("Save failed", "Could not save this PFD mark.", "error");
    }
  };


  const handleWizardSelect = (target: { kind: WizardTargetKind; id: string }) => {
    if (!pnidState) return;
    const kind = target.kind;
    if (kind === "equipment") {
      const e = pnidState.equipments.find((x) => x.id === target.id);
      if (!e) return;
      setActiveEquipmentId(e.id);
      // Navigate to user-marked bbox, or AI label_bbox for unmarked equipment
      if (e.bbox) setFocusBbox(e.bbox);
      else if (e.label_bbox) setFocusBbox(e.label_bbox);
      return;
    }
    if (kind === "node") {
      const n = pnidState.nodes.find((x) => x.id === target.id);
      if (!n) return;
      setActiveNodeId(n.id);
      // Navigate to pipeline path, duct box, or AI label for unmarked lines.
      setFocusBbox(nodeGeometryBbox(n));
      return;
    }
    if (kind === "equipmentInstrument") {
      const i = equipmentInstruments.find((x) => x.id === target.id);
      if (!i) return;
      const effectiveContext = getInstrumentEffectiveContext(i, pnidState);
      setActiveEquipmentId(effectiveContext.context_type === 'equipment' ? effectiveContext.context_id : null);
      if (i.bbox) setFocusBbox(i.bbox);
      return;
    }
    if (kind === "nodeInstrument") {
      const i = nodeInstruments.find((x) => x.id === target.id);
      if (!i) return;
      const effectiveContext = getInstrumentEffectiveContext(i, pnidState);
      setActiveNodeId(effectiveContext.context_type === 'node' ? effectiveContext.context_id : null);
      if (i.bbox) setFocusBbox(i.bbox);
    }
  };

  /**
   * Writes a dragged rectangle into local state before the save answers. `regionIndex`
   * says which of an instrument's regions moved; every other kind is single-region and
   * ignores it.
   */
  const patchPersistedBbox = useCallback((
    target: PersistedBboxTarget,
    bbox: BoundingBox,
    regionIndex = 0,
  ) => {
    setPnidState((prev) => {
      if (!prev) return prev;
      if (target.kind === "equipment") {
        return {
          ...prev,
          equipments: prev.equipments.map((equipment) =>
            equipment.id === target.id ? { ...equipment, bbox } : equipment
          ),
        };
      }
      if (target.kind === "controlLoop") {
        return {
          ...prev,
          control_loops: prev.control_loops.map((loop) =>
            loop.id === target.id ? { ...loop, bbox } : loop
          ),
        };
      }
      if (target.kind === "node") {
        return {
          ...prev,
          nodes: prev.nodes.map((node) =>
            node.id === target.id ? { ...node, duct_bbox: bbox } : node
          ),
        };
      }
      return {
        ...prev,
        instruments: prev.instruments.map((instrument) =>
          instrument.id === target.id ? withRegionAt(instrument, regionIndex, bbox) : instrument
        ),
      };
    });
    setInstrumentEditorState((prev) =>
      prev?.instrument.id === target.id
        ? { ...prev, instrument: withRegionAt(prev.instrument, regionIndex, bbox) }
        : prev
    );
    setFocusBbox(bbox);
  }, []);

  const handleBeginPersistedBboxUpdate = useCallback((target: PersistedBboxTarget) => {
    const label =
      target.kind === "equipment"
        ? "Move or resize equipment annotation"
        : target.kind === "controlLoop"
          ? "Move or resize control-loop annotation"
          : target.kind === "node"
            ? "Move or resize duct annotation"
            : "Move or resize instrument annotation";
    pushPnidUndoSnapshot(label);
  }, [pushPnidUndoSnapshot]);

  const handlePersistedBboxUpdate = useCallback(async (
    target: PersistedBboxTarget,
    bbox: BoundingBox,
    options?: { commit?: boolean; regionIndex?: number },
  ) => {
    if (!selectedDoc?.id) return;
    const regionIndex = options?.regionIndex ?? 0;
    patchPersistedBbox(target, bbox, regionIndex);
    if (!options?.commit) return;

    try {
      if (target.kind === "equipment") {
        const updated = await pnidApi.updateEquipment(selectedDoc.id, target.id, { bbox });
        patchPersistedBbox(target, updated.bbox ?? bbox);
      } else if (target.kind === "controlLoop") {
        const updated = await pnidApi.updateControlLoop(selectedDoc.id, target.id, { bbox });
        patchPersistedBbox(target, updated.bbox ?? bbox);
      } else if (target.kind === "node") {
        const updated = await pnidApi.updateNode(selectedDoc.id, target.id, { duct_bbox: bbox });
        patchPersistedBbox(target, updated.duct_bbox ?? bbox);
      } else {
        // Only the dragged region moves; the instrument's other regions travel
        // unchanged, and the first of them stays the anchor the backend derives.
        const dragged = pnidState?.instruments.find((inst) => inst.id === target.id);
        const regions = dragged ? instrumentRegions(dragged) : [];
        const updated = await instrumentApi.updateInstrument(selectedDoc.id, target.id, {
          bboxes: regions.length > 0
            ? regions.map((region, index) => (index === regionIndex ? bbox : region))
            : [bbox],
        });
        patchPersistedBbox(target, instrumentRegions(updated)[regionIndex] ?? bbox, regionIndex);
      }
    } catch (error) {
      console.error("Failed to update mark box:", error);
      try {
        await refreshPnidState(selectedDoc.id);
      } catch (refreshError) {
        console.error("Failed to refresh after mark box update error:", refreshError);
      }
      showAlert("Box update failed", "Could not save the adjusted markup box.", "error");
    }
  }, [patchPersistedBbox, pnidState?.instruments, refreshPnidState, selectedDoc?.id]);

  const handleUpdateAnnotationBbox = useCallback(async (
    annotationId: string,
    bbox: BoundingBox,
    options?: { commit?: boolean },
  ) => {
    if (!selectedDoc?.id) return;
    // Optimistic update: keep savedTemplateAnnotations and pnidState.instruments in sync
    // (template annotations are backed by the same instrument rows).
    setSavedTemplateAnnotations((prev) => prev.map((a) => a.id === annotationId
      ? { ...a, x: bbox.x1, y: bbox.y1, width: bbox.x2 - bbox.x1, height: bbox.y2 - bbox.y1 }
      : a));
    setPnidState((prev) => prev ? {
      ...prev,
      instruments: prev.instruments.map((inst) =>
        inst.id === annotationId ? { ...inst, bbox } : inst
      ),
    } : prev);
    if (!options?.commit) return;

    try {
      const updated = await templateApi.updateAnnotation(selectedDoc.id, annotationId, { bbox });
      setSavedTemplateAnnotations((prev) => prev.map((a) => (a.id === updated.id ? updated : a)));
      setPnidState((prev) => prev ? {
        ...prev,
        instruments: prev.instruments.map((inst) => inst.id === updated.id
          ? { ...inst, bbox: { x1: updated.x, y1: updated.y, x2: updated.x + updated.width, y2: updated.y + updated.height } }
          : inst),
      } : prev);
    } catch (error) {
      console.error("Failed to update template annotation box:", error);
      try {
        await refreshPnidState(selectedDoc.id);
      } catch (refreshError) {
        console.error("Failed to refresh after annotation box update error:", refreshError);
      }
      showAlert("Box update failed", "Could not save the adjusted annotation box.", "error");
    }
  }, [refreshPnidState, selectedDoc?.id]);

  const handleShiftWizardAnnotations = useCallback(async (
    delta: { dx: number; dy: number },
    options?: { lineSegmentScale?: { sx: number; sy: number } },
  ) => {
    if (!selectedDoc?.id) return;
    const { dx, dy } = delta;
    if (!Number.isFinite(dx) || !Number.isFinite(dy)) {
      showAlert("Move failed", "Could not save an invalid annotation offset.", "error");
      return;
    }
    if (Math.abs(dx) < 0.1 && Math.abs(dy) < 0.1) return;

    try {
      pushPnidUndoSnapshot("Move all annotations");
      const updated = await pnidApi.shiftAnnotations(selectedDoc.id, dx, dy, options);
      setPnidState(updated);
      setSavedTemplateAnnotations(getTemplateAnnotationsFromInstruments(updated.instruments));
      setFocusBbox((current) => current ? shiftBbox(current, dx, dy) : current);
      showAlert(
        "Annotations moved",
        `Shifted all saved annotations by ${Math.round(dx)} px horizontally and ${Math.round(dy)} px vertically.`,
        "success",
      );
    } catch (error) {
      console.error("Failed to shift annotations:", error);
      try {
        const refreshed = await refreshPnidState(selectedDoc.id);
        setPnidState(refreshed);
      } catch (refreshError) {
        console.error("Failed to refresh after annotation shift error:", refreshError);
      }
      showAlert("Move failed", "Could not save the annotation offset.", "error");
      throw error;
    }
  }, [pushPnidUndoSnapshot, refreshPnidState, selectedDoc?.id]);

  // Check if a node is referenced by other entities (for deletion protection)
  const getNodeReferences = (nodeId: string) => {
    if (!pnidState) return { refs: [], canDelete: true };

    const node = pnidState.nodes.find((n) => n.id === nodeId);
    if (!node) return { refs: [], canDelete: true };

    const refs: string[] = [];
    const nodeNoRef = `Node #${node.node_no}`;

    // Check other nodes that reference this node in source_ref/dest_ref
    for (const n of pnidState.nodes) {
      if (n.id === nodeId) continue;
      if (n.source_ref === nodeNoRef)
        refs.push(`Node #${n.node_no} (as source)`);
      if (n.dest_ref === nodeNoRef)
        refs.push(`Node #${n.node_no} (as destination)`);
      if (n.parent_node_id === nodeId)
        refs.push(`Node #${n.node_no} (as parent bifurcation)`);
    }

    // Check node instruments that branch to this node
    for (const ni of directNodeInstruments) {
      if (ni.branch_ref === String(node.node_no)) {
        const parentNode = pnidState.nodes.find((n) => n.id === ni.context_id);
        refs.push(
          `Instrument on Node #${parentNode?.node_no || "?"} (branch point)`
        );
      }
    }

    return { refs, canDelete: refs.length === 0 };
  };

  const executeDelete = async (target: { kind: WizardTargetKind; id: string }) => {
    if (!selectedDoc || !pnidState) return;
    const kind = target.kind;
    try {
      if (kind === "equipment") {
        pushPnidUndoSnapshot("Delete equipment annotation");
        await pnidApi.deleteEquipment(selectedDoc.id, target.id);
        const next = {
          ...pnidState,
          equipments: pnidState.equipments.filter((e) => e.id !== target.id),
          instruments: pnidState.instruments.filter(
            (i) => i.context_id !== target.id
          ),
        };
        setPnidState(next);
      } else if (kind === "equipmentInstrument") {
        pushPnidUndoSnapshot("Delete instrument annotation");
        await instrumentApi.deleteInstrument(selectedDoc.id, target.id);
        setPnidState({
          ...pnidState,
          instruments: pnidState.instruments.filter(
            (i) => i.id !== target.id
          ),
        });
      } else if (kind === "node") {
        // Check for references before deleting
        const { refs, canDelete } = getNodeReferences(target.id);
        if (!canDelete) {
          showAlert(
            "Cannot Delete",
            `This node is referenced by:\n• ${refs.join(
              "\n• "
            )}\n\nRemove these references first.`,
            "error"
          );
          return;
        }
        pushPnidUndoSnapshot("Delete line annotation");
        await pnidApi.deleteNode(selectedDoc.id, target.id);
        const next = {
          ...pnidState,
          nodes: pnidState.nodes.filter((n) => n.id !== target.id),
          instruments: pnidState.instruments.filter(
            (i) => i.context_id !== target.id
          ),
        };
        setPnidState(next);
      } else if (kind === "nodeInstrument") {
        pushPnidUndoSnapshot("Delete instrument annotation");
        await instrumentApi.deleteInstrument(selectedDoc.id, target.id);
        setPnidState({
          ...pnidState,
          instruments: pnidState.instruments.filter(
            (i) => i.id !== target.id
          ),
        });
      }
      showAlert("Deleted", "Annotation deleted. Press Cmd/Ctrl+Z to undo.", "success");
    } catch (error) {
      console.error(error);
      showAlert("Delete failed", "Could not delete item.", "error");
    }
  };

  const handleWizardDelete = (target: { kind: WizardTargetKind; id: string }) => {
    if (!pnidState) return;
    const kind = target.kind;

    // Build a descriptive name for the item being deleted
    let itemName = "this item";
    if (kind === "equipment") {
      const eq = pnidState.equipments.find((e) => e.id === target.id);
      itemName = eq ? `equipment "${eq.equipment_no}"` : "this equipment";
    } else if (kind === "equipmentInstrument") {
      const inst = equipmentInstruments.find(
        (i) => i.id === target.id
      );
      itemName = inst
        ? `instrument "${formatInstrumentIdentity(inst)}"`
        : "this instrument";
    } else if (kind === "node") {
      const node = pnidState.nodes.find((n) => n.id === target.id);
      itemName = node
        ? `line "${node.line_no || `Node #${node.node_no}`}"`
        : "this line";
    } else if (kind === "nodeInstrument") {
      const inst = nodeInstruments.find((i) => i.id === target.id);
      itemName = inst
        ? `instrument "${formatInstrumentIdentity(inst)}"`
        : "this instrument";
    }

    setConfirmDialog({
      isOpen: true,
      title: "Confirm Delete",
      message: `Are you sure you want to delete ${itemName}? This can be undone with Cmd/Ctrl+Z.`,
      type: "danger",
      confirmText: "Delete",
      onConfirm: () => {
        setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
        executeDelete(target);
      },
    });
  };

  // One-click delete from the canvas "×" badge — no confirm dialog, undoable via Cmd/Ctrl+Z.
  const handleQuickDeleteWizardItem = (target: {
    kind: "equipmentInstrument" | "nodeInstrument";
    id: string;
  }) => {
    if (instrumentEditorState?.instrument.id === target.id) {
      setInstrumentEditorState(null);
    }
    setSelectedInstrumentId((prev) => (prev === target.id ? null : prev));
    setSelectedWizardItemKeys((prev) => {
      const key = wizardItemKey(target);
      if (!prev.has(key)) return prev;
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
    void executeDelete(target);
  };

  const handleReorderNodeInstruments = async (
    nodeId: string,
    reorderedInstruments: PnidInstrument[]
  ) => {
    if (!pnidState || !selectedDoc) return;

    const previousState = pnidState;
    const orderedIds = reorderedInstruments.map((i) => i.id);
    const reorderedWithSeq = reorderedInstruments.map((inst, idx) => ({
      ...inst,
      sequence_order: idx + 1,
    }));
    const otherInstruments = pnidState.instruments.filter(
      (i) => i.context_type !== 'node' || i.context_id !== nodeId
    );
    setPnidState({
      ...pnidState,
      instruments: [...otherInstruments, ...reorderedWithSeq],
    });

    try {
      await pnidApi.reorderNodeInstruments(selectedDoc.id, nodeId, orderedIds);
    } catch (err) {
      setPnidState(previousState);
      console.error('Failed to reorder instruments', err);
      alert('Failed to reorder instruments');
    }
  };

  // Auto-sequence node instruments based on position along polyline
  // Compute counts for ContextBar
  const equipmentCount = pnidState
    ? {
        total: pnidState.equipments.length,
        marked: pnidState.equipments.filter((e) => e.status !== "ai_identified")
          .length,
      }
    : undefined;

  const nodeCount = pnidState
    ? {
        total: pnidState.nodes.length,
        marked: pnidState.nodes.filter(
          (n) => n.status !== "ai_identified" && n.status !== "skipped"
        ).length,
        skipped: pnidState.nodes.filter((n) => n.status === "skipped").length,
      }
    : undefined;

  const activeEquipment = getActiveEquipment();
  const activeNode = getActiveNode();

  const handleDrawingContextNavigate = useCallback((target: NoteTarget) => {
    if (!pnidState) return;
    setDrawingContextOpen(false);

    if (target.kind === 'equipment') {
      const equipment = pnidState.equipments.find((item) => item.id === target.id);
      setWizardStep(3);
      setActiveEquipmentId(target.id);
      setActiveNodeId(null);
      setSelectedInstrumentId(null);
      setFocusBbox(equipment?.bbox ?? equipment?.label_bbox ?? null);
      return;
    }

    if (target.kind === 'line') {
      const node = pnidState.nodes.find((item) => item.id === target.id);
      setWizardStep(4);
      setActiveNodeId(target.id);
      setActiveEquipmentId(null);
      setSelectedInstrumentId(null);
      setFocusBbox(nodeGeometryBbox(node));
      return;
    }

    focusInstrument(target.id);
  }, [focusInstrument, pnidState]);

  const handleDrawingContextSaveNote = useCallback(async (target: NoteTarget, note: string) => {
    if (!selectedDoc?.id || !pnidState) {
      throw new Error('No P&ID document is selected.');
    }
    const persistedNote = note || null;
    pushPnidUndoSnapshot(`${target.note ? 'Edit' : 'Add'} ${target.kind} note`);

    if (target.kind === 'equipment') {
      const updated = await pnidApi.updateEquipment(selectedDoc.id, target.id, { notes: persistedNote });
      setPnidState({
        ...pnidState,
        equipments: pnidState.equipments.map((item) => item.id === updated.id ? updated : item),
      });
      trackAction('object_note_saved', { item_type: 'equipment', item_id: target.id, cleared: !persistedNote });
      void checkObjectPolicy('equipment', target.id, target.tag);
      return;
    }

    if (target.kind === 'line') {
      const updated = await pnidApi.updateNode(selectedDoc.id, target.id, { notes: persistedNote });
      setPnidState({
        ...pnidState,
        nodes: pnidState.nodes.map((item) => item.id === updated.id ? updated : item),
      });
      trackAction('object_note_saved', { item_type: 'line', item_id: target.id, cleared: !persistedNote });
      void checkObjectPolicy('node', target.id, target.tag);
      return;
    }

    const updated = await instrumentApi.updateInstrument(selectedDoc.id, target.id, { notes: persistedNote });
    setPnidState({
      ...pnidState,
      instruments: pnidState.instruments.map((item) => item.id === updated.id ? updated : item),
    });
    trackAction('object_note_saved', { item_type: 'instrument', item_id: target.id, cleared: !persistedNote });
    void checkObjectPolicy('instrument', target.id, target.tag);
  }, [checkObjectPolicy, pnidState, pushPnidUndoSnapshot, selectedDoc?.id, trackAction]);

  const formatNodeRoute = (node: {
    source_ref?: string | null;
    dest_ref?: string | null;
  }) => `${node.source_ref || "?"} → ${node.dest_ref || "?"}`;

  const drawTarget: DrawTargetInfo | null = (() => {
    if (!selectedDoc) return null;
    switch (wizardStep) {
      case 3:
        if (activeEquipment) {
          return {
            label: activeEquipment.equipment_no || "Equipment",
            detail: activeEquipment.equipment_type || undefined,
            tone: activeEquipment.status,
          };
        }
        return { label: "Draw new equipment", tone: "new" };
      case 4:
        if (activeNode) {
          return {
            label: activeNode.line_no || `Line #${activeNode.node_no}`,
            detail: formatNodeRoute(activeNode),
            tone: activeNode.status,
          };
        }
        return { label: "Draw new line", tone: "new" };
      case 2:
        return { label: "Draw instrument box near equipment or line", tone: "new" };
      default:
        return null;
    }
  })();

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        /* Fills the workspace shell's content card instead of the viewport. */
        flex: 1,
        minHeight: 0,
        height: "100%",
      }}>
      {/* The shell-standard header band every other page carries. */}
      <PageHeader
        title={pnidState?.pnid_no || selectedDoc.original_filename || selectedDoc.filename}
        onBack={() => navigate('/drawings')}
        actions={!isBooting ? (
          <div className="pnid-ws-headeractions">
          {/* The steps ride the header row rather than a band of their own —
              they are this page's primary navigation, and a separate strip made
              them read as a peer of the action band below. */}
          <OverflowScroller className="pnid-ws-steptabs" role="navigation" ariaLabel="Workflow steps">
            {STEP_ORDER.map((step) => {
              const active = wizardStep === step;
              return (
                <button
                  key={step}
                  type="button"
                  className={active ? 'pnid-ws-steptab pnid-ws-steptab--active' : 'pnid-ws-steptab'}
                  aria-current={active ? 'step' : undefined}
                  onClick={() => handleStepChangeRequest(step)}
                >
                  <span className="pnid-ws-steptab-num">{step}</span>
                  <span className="pnid-ws-steptab-label">{STEP_LABELS[step]}</span>
                </button>
              );
            })}
          </OverflowScroller>
          {wizardStep !== 1 && (
            <div className="pnid-ws-headerctas">
              <WorkspaceRailItem
                icon={Send}
                label="Publish"
                variant="contained"
                disabled={!selectedDoc || readOnlyWizard}
                onClick={() => setPublishPanelOpen(true)}
                title={
                  readOnlyWizard
                    ? "You need edit access to publish"
                    : "Check this drawing and publish it as a new version"
                }
              />
              <WorkspaceRailItem
                icon={FileSpreadsheet}
                label="Export Excel"
                variant="outlined"
                disabled={!selectedDoc}
                onClick={async () => {
                  if (!selectedDoc) return;
                  try {
                    await pnidApi.exportExcel(selectedDoc.id);
                  } catch (err) {
                    console.error('Failed to export Excel:', err);
                    alert('Failed to export Excel');
                  }
                }}
                title="Export P&ID data to Excel"
              />
            </div>
          )}
          </div>
        ) : undefined}
      />
      {/* One band: the step's context and target tabs on the left, every
          drawing action on the right. Two strips became one. */}
      {!isBooting && wizardStep !== 1 && (
        <div className="pnid-ws-band">
          <div className="pnid-ws-band-context">
          <ContextBar
                      step={wizardStep}
                      pnidNo={pnidState?.pnid_no}
                      drawTarget={drawTarget}
                      equipments={pnidState?.equipments}
                      activeEquipmentId={activeEquipmentId}
                      onSelectEquipment={(id) => {
                        setActiveEquipmentId(id);
                        // Navigate to equipment's user-marked bbox, or AI label_bbox for unmarked equipment
                        const equipment = pnidState?.equipments.find((e) => e.id === id);
                        if (equipment?.bbox) {
                          setFocusBbox(equipment.bbox);
                        } else if (equipment?.label_bbox) {
                          setFocusBbox(equipment.label_bbox);
                        } else {
                          setFocusBbox(null);
                        }
                      }}
                      onEditEquipment={(equipmentId) =>
                        openEditDialog("equipment", equipmentId, {
                          shouldFocus: false,
                        })
                      }
                      nodes={pnidState?.nodes}
                      activeNodeId={activeNodeId}
                      onSelectNode={(id) => {
                        setActiveNodeId(id);
                        // Navigate to pipeline path, duct box, or AI label for unmarked nodes.
                        const node = pnidState?.nodes.find((n) => n.id === id);
                        setFocusBbox(nodeGeometryBbox(node));
                      }}
                      onMarkNode={handleMarkNode}
                      onUnmarkNode={handleUnmarkNode}
                      onEditNode={(nodeId) => openEditDialog("node", nodeId, { shouldFocus: false })}
                      isNodeMarkingMode={isNodeMarkingMode}
                      onNext={
                        wizardStep === 3
                          ? nextEquipment
                          : wizardStep === 4
                          ? nextNode
                          : undefined
                      }
                      hasNext={
                        (wizardStep === 3 &&
                          pnidState?.equipments.some(
                            (e) => e.status === "ai_identified"
                          )) ||
                        (wizardStep === 4 &&
                          pnidState?.nodes.some(
                            (n) => n.status === "ai_identified"
                          ))
                      }
                      equipmentCount={equipmentCount}
                      nodeCount={nodeCount}
                      // Show active only toggle (steps 3-4)
                      showActiveOnly={showActiveOnly}
                      onShowActiveOnlyChange={setShowActiveOnly}
                      // Step 2 Template Navigation props
                      templateAnnotationCount={templateAnnotationCount}
                      activeAnnotationId={activeAnnotationId}
                      unverifiedOnlyFilter={unverifiedOnlyFilter}
                      onUnverifiedOnlyFilterChange={setUnverifiedOnlyFilter}
                      onNavigateAnnotation={handleNavigateAnnotation}
                    />
          </div>
          <div className="pnid-ws-actionbar" role="group" aria-label="Drawing actions">
          {!readOnlyWizard && (
            <div className="pnid-ws-rail-pair" aria-label="Undo redo annotation actions">
              <WorkspaceRailItem
                icon={Undo2}
                label="Undo"
            iconOnly
                onClick={handleUndoPnidAction}
                disabled={historyApplying || undoStack.length === 0}
                title="Undo last annotation action (Cmd/Ctrl+Z)"
              />
              <WorkspaceRailItem
                icon={Redo2}
                label="Redo"
            iconOnly
                onClick={handleRedoPnidAction}
                disabled={historyApplying || redoStack.length === 0}
                title="Redo annotation action (Cmd/Ctrl+Shift+Z)"
              />
            </div>
          )}
          {!readOnlyWizard && (
            <WorkspaceRailItem
              icon={Download}
              label="Import Annotations"
              iconOnly
              flagged={targetHasAnnotations}
              sub={targetHasAnnotations ? `Confirm replace: ${targetAnnotationSummary}` : undefined}
              onClick={openAnnotationImportDialog}
              disabled={annotationImportDisabled}
              aria-label={
                annotationImportDisabledReason
                  ? `Import annotations disabled. ${annotationImportDisabledReason}`
                  : targetHasAnnotations
                  ? `Import annotations. Target has ${targetAnnotationSummary || "existing annotations"}; confirmation required before replacement.`
                  : `Import ${diagramTypeLabel(diagramMode)} annotations from another document`
              }
              title={
                annotationImportDisabledReason ||
                (targetHasAnnotations
                  ? `Target has ${targetAnnotationSummary || "existing annotations"}. You will be asked to confirm before replacing them.`
                  : "") ||
                `Import ${diagramTypeLabel(diagramMode)} annotations from another document`
              }
            />
          )}
{import.meta.env.DEV && (
                        <ViewModeToggle
                          viewMode={viewMode}
                          onViewModeChange={setViewMode}
                          hasPlantModel={hasPlantModel}
                        />
                      )}
                      {import.meta.env.DEV && viewMode === 'intelligence' && plantModel && (
                        <>
                          <button
                            type="button"
                            onClick={() => setShowProcessLens(!showProcessLens)}
                            title={showProcessLens ? 'Hide Process View' : 'Show Process View'}
                            style={{
                              borderRadius: 7,
                              border: showProcessLens ? '1px solid #0ea5e9' : '1px solid rgba(0, 0, 0, 0.08)',
                              backgroundColor: showProcessLens ? '#e0f2fe' : 'white',
                              padding: '6px 10px',
                              fontSize: 12,
                              color: showProcessLens ? '#0369a1' : '#334155',
                              cursor: 'pointer',
                              fontWeight: 600,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 4,
                            }}
                          >
                            <span>📊</span>
                            <span>Process</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => setShowControlLens(!showControlLens)}
                            title={showControlLens ? 'Hide Controls View' : 'Show Controls View'}
                            style={{
                              borderRadius: 7,
                              border: showControlLens ? '1px solid #a855f7' : '1px solid rgba(0, 0, 0, 0.08)',
                              backgroundColor: showControlLens ? '#f3e8ff' : 'white',
                              padding: '6px 10px',
                              fontSize: 12,
                              color: showControlLens ? '#7c3aed' : '#334155',
                              cursor: 'pointer',
                              fontWeight: 600,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 4,
                            }}
                          >
                            <span>🎛️</span>
                            <span>Controls</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => setShowWalkthrough(!showWalkthrough)}
                            title={showWalkthrough ? 'Hide Walkthrough' : 'Show Walkthrough'}
                            style={{
                              borderRadius: 7,
                              border: showWalkthrough ? '1px solid #f59e0b' : '1px solid rgba(0, 0, 0, 0.08)',
                              backgroundColor: showWalkthrough ? '#fef3c7' : 'white',
                              padding: '6px 10px',
                              fontSize: 12,
                              color: showWalkthrough ? '#92400e' : '#334155',
                              cursor: 'pointer',
                              fontWeight: 600,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 4,
                            }}
                          >
                            <span>🎬</span>
                            <span>Walkthrough</span>
                          </button>
                        </>
                      )}
          {isAdmin && (
            <WorkspaceRailItem icon={FolderOpen} label="File Manager"
            iconOnly onClick={onOpenFileManager} />
          )}
          <hr className="pnid-ws-rail-divider" />
          <WorkspaceRailItem
            icon={ClipboardCheck}
            label="Review"
            iconOnly
            onClick={() => { trackAction('review_opened'); setReviewModalOpen(true); }}
          />
          <WorkspaceRailItem
            icon={StickyNote}
            label="Notes & Revisions"
            iconOnly
            disabled={!selectedDoc || !pnidState}
            onClick={() => setDrawingContextOpen(true)}
            title="Review drawing notes and revision metadata, or attach an SME note to an object"
          />
          <WorkspaceRailItem
            icon={Link2}
            label="Copy Link"
            iconOnly
            disabled={!selectedDoc}
            onClick={handleCopyDocumentLink}
            title="Copy a shareable link for this PNID"
          />
          <WorkspaceRailItem
            icon={History}
            label="Version History"
            iconOnly
            onClick={() => setVersionHistoryOpen(true)}
            title="View and restore previous versions"
          />
          <WorkspaceRailItem
            icon={AlertTriangle}
            label="Review Issues"
            iconOnly
            disabled={!selectedDoc}
            onClick={() => setReviewIssuesOpen(true)}
            title="Check this P&ID against the review policy"
          />
          <WorkspaceRailItem
            icon={Shield}
            label="Policy"
            iconOnly
            onClick={() => navigate('/policy-admin')}
            title="View and edit the review policy (SME admin)"
          />
          <WorkspaceRailItem
            icon={Flag}
            label="Mark for QC"
            iconOnly
            disabled={!selectedDoc}
            onClick={async () => {
              if (!selectedDoc) return;
              try {
                await documentApi.updateStatus(selectedDoc.id, 'needs_review');
                trackAction('marked_for_qc', { diagram_name: selectedDoc.original_filename });
                alert('Diagram marked for QC');
              } catch (err) {
                console.error('Failed to mark for QC:', err);
                alert('Failed to mark for QC');
              }
            }}
            title="Mark this diagram for quality check"
          />

          </div>
        </div>
      )}

    <div
      style={{
        display: "flex",
        flex: 1,
        /* The bands above are fixed height; this row absorbs the remainder and
           clips, so the canvas scrolls inside it instead of growing the page. */
        minHeight: 0,
        overflow: "hidden",
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        backgroundColor: "var(--raven-bg-page)",
      }}>
      {/* Targets live beside the canvas, not in a chip strip above it. */}
      {!isBooting && wizardStep === 3 && (
        <EquipmentInstrumentRail
          tab={assetRailTab} onTabChange={setAssetRailTab}
          assets={publishableAssets.assets}
          stale={publishableAssets.stale} loading={publishableAssets.loading}
          error={publishableAssets.error} onRefresh={() => void publishableAssets.refresh()}
          refreshDisabled={historyApplying || loadingAI}
          historicalEquipment={previewingVersion !== null ? pnidState?.equipments : undefined}
          onExitPreview={() => {
            if (originalStateBeforePreview) setPnidState(originalStateBeforePreview);
            setPreviewingVersion(null);
            setOriginalStateBeforePreview(null);
          }}
          activeEquipmentId={activeEquipmentId} selectedInstrumentId={selectedInstrumentId}
          drawNewActive={assetRailTab === 'instruments'
            ? boxDraft?.kind === 'instrument' && !boxDraft.target
            : !activeEquipmentId && !boxDraft}
          onDrawNew={() => {
            setSelectedInstrumentId(null);
            setFocusBbox(null);
            handleInstrumentSubModeToggle(assetRailTab === 'instruments');
            if (assetRailTab === 'equipment') setActiveEquipmentId(null);
          }}
          onSelectEquipment={(id) => {
            const equipment = pnidState?.equipments.find((candidate) => candidate.id === id);
            if (!equipment) return;
            setSelectedInstrumentId(null);
            setActiveEquipmentId(id);
            setFocusBbox(equipment.bbox ?? equipment.label_bbox ?? null);
          }}
          onSelectInstrument={(id) => {
            const instrument = pnidState?.instruments.find((candidate) => candidate.id === id);
            if (!instrument) return;
            setSelectedInstrumentId(id);
            setFocusBbox(instrument.bbox ?? null);
            setShowActiveOnly(false);
          }}
        />
      )}
      {!isBooting && wizardStep === 4 && (
        <WorkspaceTargetRail
          title="Lines"
          items={nodeTargets(pnidState?.nodes ?? [], nodeSortOrder)}
          activeId={activeNodeId}
          sortOrder={nodeSortOrder}
          onSortOrderChange={setNodeSortOrder}
          drawNewLabel="Draw New Line"
          onDrawNew={() => {
            setActiveNodeId(null);
            setFocusBbox(null);
          }}
          onSelect={(id) => {
            setActiveNodeId(id);
            const node = pnidState?.nodes.find((n) => n.id === id);
            setFocusBbox(nodeGeometryBbox(node));
          }}
        />
      )}
      {/* Left action rail — the canvas header's former right-hand cluster.
          Same JSX and handlers, relocated; it renders exactly when the header
          does (after boot, past the step-1 setup form). */}
      {/* Main Content Area - Canvas fills entire space */}
      <div
        style={{
          flex: 1,
          display: "flex",
          position: "relative",
          overflow: "hidden",
        }}>
        {/* Canvas Area */}
        <div
          style={{
            flex: 1,
            display: "flex",
            position: "relative",
            overflow: "hidden",
          }}>
          {/* Canvas */}
          <div
            style={{
              flex: 1,
              padding: 8,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}>
            {isBooting ? (
              <WorkspaceSkeleton />
            ) : wizardStep === 1 ? (
              /* Step 1: P&ID Setup Form */
              <div
                style={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "white",
                  borderRadius: 12,
                }}>
                <div style={{ width: "100%", maxWidth: 400, padding: 32 }}>
                  <h2 style={{ margin: "0 0 8px 0", fontSize: 20 }}>
                    P&ID Setup
                  </h2>
                  <p
                    style={{
                      color: "var(--raven-text-secondary)",
                      marginBottom: 24,
                      fontSize: 14,
                    }}>
                    Configure the P&ID document details
                  </p>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: "#64748b" }}>
                      Diagram mode
                    </span>
                    {renderDiagramModeToggle()}
                  </div>

                  {/* AI Prefill Button - editors only */}
                  {!readOnlyWizard && hasAIData && !pnidState?.ai_data_loaded && (
                    <button
                      onClick={handlePrefillFromAI}
                      disabled={loadingAI}
                      style={{
                        width: "100%",
                        padding: "12px",
                        backgroundColor: loadingAI ? "var(--raven-text-secondary)" : "#8b5cf6",
                        color: "white",
                        border: "none",
                        borderRadius: 8,
                        cursor: loadingAI ? "not-allowed" : "pointer",
                        marginBottom: 16,
                        fontSize: 14,
                        fontWeight: 500,
                      }}>
                      {loadingAI
                        ? "⏳ Loading AI data..."
                        : "✨ Prefill from AI Data"}
                    </button>
                  )}

                  {pnidState?.ai_data_loaded && (
                    <div
                      style={{
                        padding: "10px 14px",
                        backgroundColor: "#dcfce7",
                        border: "1px solid #bbf7d0",
                        borderRadius: 8,
                        marginBottom: 16,
                        fontSize: 13,
                        color: "#166534",
                      }}>
                      ✓ AI data loaded: {pnidState.equipments.length} equipment,{" "}
                      {pnidState.nodes.length} lines
                    </div>
                  )}

                  <label
                    style={{
                      display: "block",
                      fontSize: 13,
                      marginBottom: 6,
                      fontWeight: 500,
                    }}>
                    P&ID Number
                  </label>
                  <input
                    type="text"
                    value={pnidNoDraft}
                    onChange={(e) => !readOnlyWizard && setPnidNoDraft(e.target.value)}
                    placeholder="e.g., 1T4031 PR/5AE-9"
                    disabled={readOnlyWizard}
                    readOnly={readOnlyWizard}
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      border: "1px solid #e5e7eb",
                      borderRadius: 8,
                      marginBottom: 16,
                      fontSize: 14,
                      ...(readOnlyWizard && { backgroundColor: "#f3f4f6", cursor: "not-allowed" }),
                    }}
                  />

                  <label
                    style={{
                      display: "block",
                      fontSize: 13,
                      marginBottom: 6,
                      fontWeight: 500,
                    }}>
                    P&ID Title (optional)
                  </label>
                  <input
                    type="text"
                    value={pnidTitleDraft}
                    onChange={(e) => !readOnlyWizard && setPnidTitleDraft(e.target.value)}
                    placeholder="e.g., AMINE REGENERATION UNIT"
                    disabled={readOnlyWizard}
                    readOnly={readOnlyWizard}
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      border: "1px solid #e5e7eb",
                      borderRadius: 8,
                      marginBottom: 16,
                      fontSize: 14,
                      ...(readOnlyWizard && { backgroundColor: "#f3f4f6", cursor: "not-allowed" }),
                    }}
                  />

                  <label htmlFor="drawing-class" style={{ display: "block", fontSize: 13, marginBottom: 6, fontWeight: 500 }}>
                    Drawing class (optional)
                  </label>
                  {/* The select drops its native chrome and takes the text inputs' unstyled browser font (Arial), so it matches them in height and look. */}
                  <div style={{ position: "relative", marginBottom: 24 }}>
                    <select
                      id="drawing-class"
                      value={drawingClassDraft}
                      onChange={(event) => setDrawingClassDraft(event.target.value as DrawingClass | "")}
                      disabled={readOnlyWizard}
                      style={{
                        width: "100%",
                        padding: "10px 36px 10px 12px",
                        border: "1px solid #e5e7eb",
                        borderRadius: 8,
                        fontSize: 14,
                        fontFamily: "Arial",
                        color: "inherit",
                        backgroundColor: "#fff",
                        appearance: "none",
                        ...(readOnlyWizard && { backgroundColor: "#f3f4f6", cursor: "not-allowed" }),
                      }}>
                      <option value="">Not assigned</option>
                      <option value="process">Process</option>
                      <option value="utility">Utility</option>
                      <option value="vendor">Vendor</option>
                      <option value="package">Package</option>
                      <option value="interconnection">Interconnection</option>
                    </select>
                    <AppIcon
                      icon={ChevronDown}
                      size={16}
                      style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}
                    />
                  </div>

                  <button
                    disabled={setupMutation.isPending}
                    onClick={() =>
                      readOnlyWizard
                        ? setWizardStep(3)
                        : savePnidSetup()
                    }
                    className="pnid-btn pnid-btn--contained"
                    style={{ width: "100%" }}>
                    {readOnlyWizard
                      ? "Continue to View →"
                      : pnidSetupReturnStep
                        ? "Save & Return →"
                        : "Save & Continue →"}
                  </button>
                </div>
              </div>
            ) : (
              /* Canvas for Steps 2-6 */
              <>
                {/* Version Preview Banner */}
                {previewingVersion && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "8px 14px",
                      marginBottom: 8,
                      backgroundColor: "#fef3c7",
                      border: "1px solid #fcd34d",
                      borderRadius: 8,
                    }}>
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 16 }}>👁️</span>
                      <span
                        style={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: "#92400e",
                        }}>
                        Viewing version {previewingVersion}
                      </span>
                      <span style={{ fontSize: 12, color: "#a16207" }}>
                        — Changes will not be saved until you restore
                      </span>
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button
                        onClick={() => {
                          if (originalStateBeforePreview) {
                            setPnidState(originalStateBeforePreview);
                          }
                          setPreviewingVersion(null);
                          setOriginalStateBeforePreview(null);
                        }}
                        style={{
                          padding: "5px 12px",
                          fontSize: 12,
                          backgroundColor: "white",
                          color: "#374151",
                          border: "1px solid #d1d5db",
                          borderRadius: 5,
                          cursor: "pointer",
                          fontWeight: 500,
                        }}>
                        Exit Preview
                      </button>
                      <button
                        onClick={() => setVersionHistoryOpen(true)}
                        style={{
                          padding: "5px 12px",
                          fontSize: 12,
                          backgroundColor: "var(--raven-purple-900)",
                          color: "white",
                          border: "none",
                          borderRadius: 5,
                          cursor: "pointer",
                          fontWeight: 600,
                        }}>
                        Restore This Version
                      </button>
                    </div>
                  </div>
                )}

                {/* Reviewer mode banner */}
                {user?.is_reviewer && (
                  <div
                    style={{
                      marginBottom: 8,
                      padding: "8px 12px",
                      backgroundColor: "#fef3c7",
                      border: "1px solid #f59e0b",
                      borderRadius: 8,
                      fontSize: 13,
                      color: "#92400e",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                    }}>
                    <span style={{ fontWeight: 600 }}>👁️ Reviewer mode</span>
                    <span>View and add comments only. Editing is disabled.</span>
                  </div>
                )}

                {/* Drawing-level controls. A flat row, not a card: the band
                    above already carries the surface, and the card's padding,
                    border and margin cost the canvas ~40px of height. */}
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                    marginBottom: 4,
                    padding: "2px 0",
                    position: "relative",
                    zIndex: 30,
                    overflow: "visible",
                  }}>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "minmax(240px, 0.9fr) minmax(280px, 1.2fr)",
                      alignItems: "start",
                      gap: 10,
                    }}>
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0, flexWrap: "wrap" }}>
                      {renderDiagramModeToggle()}
                      <button
                        onClick={() => {
                          setConfirmDialog({
                            isOpen: true,
                            title: "Change P&ID Document?",
                            message:
                              "Are you sure you want to switch to a different document? Any unsaved changes will be lost.",
                            type: "warning",
                            confirmText: "Change Document",
                            onConfirm: () => {
                              setConfirmDialog((prev) => ({
                                ...prev,
                                isOpen: false,
                              }));
                              onClose();
                              // Reset preview state
                              setPreviewingVersion(null);
                              setOriginalStateBeforePreview(null);
                            },
                          });
                        }}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                          padding: "5px 8px",
                          fontSize: 12,
                          backgroundColor: "#f1f5f9",
                          border: "1px solid #e2e8f0",
                          borderRadius: 5,
                          cursor: "pointer",
                          color: "#475569",
                          fontWeight: 600,
                          maxWidth: 180,
                        }}
                        title={selectedDoc.original_filename}>
                        <span
                          style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}>
                          {pnidState?.pnid_no || selectedDoc.original_filename}
                        </span>
                        <span style={{ fontSize: 12, color: "var(--raven-text-secondary)" }}>
                          Change
                        </span>
                      </button>
                      {publishedVersion !== null && (
                        <span
                          title={`Published as version ${publishedVersion}`}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: "3px 7px",
                            borderRadius: 6,
                            backgroundColor: "var(--raven-purple-200)",
                            color: "var(--raven-purple-900)",
                            fontFamily: "monospace",
                            fontSize: 12,
                            fontWeight: 700,
                          }}>
                          v{publishedVersion}
                        </span>
                      )}
                      {!readOnlyWizard && !previewingVersion && (
                        <button
                          type="button"
                          onClick={openPnidSetupEditor}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                            padding: "5px 8px",
                            fontSize: 12,
                            backgroundColor: "#ffffff",
                            border: "1px solid #e2e8f0",
                            borderRadius: 5,
                            cursor: "pointer",
                            color: "#475569",
                            fontWeight: 600,
                            whiteSpace: "nowrap",
                          }}
                          title="Edit drawing setup">
                          Edit setup
                        </button>
                      )}
                    </div>

                  </div>
                  {annotationImportAdjustment && !readOnlyWizard && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        padding: "8px 10px",
                        borderRadius: 8,
                        backgroundColor: "var(--raven-purple-100)",
                        border: "1px solid #bfdbfe",
                        color: "var(--raven-purple-900)",
                        fontSize: 12,
                        fontWeight: 600,
                      }}
                    >
                      <span
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: 99,
                          backgroundColor: "var(--raven-purple-900)",
                          flex: "0 0 auto",
                        }}
                      />
                      <span style={{ flex: "1 1 auto", minWidth: 0 }}>
                        Imported {annotationImportAdjustment.copiedSummary} from{" "}
                        <strong>{annotationImportAdjustment.sourceLabel}</strong>
                        {annotationImportAdjustment.overwroteExisting && annotationImportAdjustment.replacedSummary
                          ? ` after replacing ${annotationImportAdjustment.replacedSummary}`
                          : ""}
                        . {annotationImportAdjustment.geometryAdjustmentText || "No automatic scaling was needed."} Drag selected boxes to fine-tune placement, use handles to resize, or delete selected items.
                      </span>
                      <button
                        type="button"
                        onClick={handleSelectAllWizardAnnotations}
                        style={{
                          padding: "5px 8px",
                          fontSize: 12,
                          border: "1px solid #93c5fd",
                          borderRadius: 6,
                          backgroundColor: "white",
                          color: "var(--raven-purple-800)",
                          cursor: "pointer",
                          fontWeight: 700,
                          whiteSpace: "nowrap",
                        }}
                      >
                        Select all
                      </button>
                      <button
                        type="button"
                        onClick={handleUndoPnidAction}
                        disabled={historyApplying || undoStack.length === 0}
                        style={{
                          padding: "5px 8px",
                          fontSize: 12,
                          border: "1px solid #93c5fd",
                          borderRadius: 6,
                          backgroundColor: "white",
                          color: undoStack.length ? "var(--raven-purple-800)" : "var(--raven-text-secondary)",
                          cursor: historyApplying || undoStack.length === 0 ? "not-allowed" : "pointer",
                          fontWeight: 700,
                          whiteSpace: "nowrap",
                        }}
                      >
                        Undo import
                      </button>
                      <button
                        type="button"
                        onClick={() => setAnnotationImportAdjustment(null)}
                        style={{
                          padding: "5px 8px",
                          fontSize: 12,
                          border: "1px solid #bfdbfe",
                          borderRadius: 6,
                          backgroundColor: "var(--raven-purple-200)",
                          color: "#1e3a8a",
                          cursor: "pointer",
                          fontWeight: 700,
                          whiteSpace: "nowrap",
                        }}
                      >
                        Finish
                      </button>
                    </div>
                  )}
                  {geometryTarget &&
                    (() => {
                      // Get label for the instrument being marked
                      let targetLabel: string = geometryTarget.kind;
                      if (
                        geometryTarget.kind === "equipmentInstrument" &&
                        pnidState
                      ) {
                        const inst = equipmentInstruments.find(
                          (i) => i.id === geometryTarget.id
                        );
                        targetLabel =
                          (inst ? formatInstrumentIdentity(inst) : "") || "Equipment Instrument";
                      } else if (
                        geometryTarget.kind === "nodeInstrument" &&
                        pnidState
                      ) {
                        const inst = nodeInstruments.find(
                          (i) => i.id === geometryTarget.id
                        );
                        targetLabel =
                          (inst ? formatInstrumentIdentity(inst) : "") || "Line Instrument";
                      }
                      return (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            padding: "6px 10px",
                            borderRadius: 8,
                            backgroundColor: "var(--raven-purple-100)",
                            border: "1px solid #93c5fd",
                            fontSize: 12,
                            color: "var(--raven-purple-800)",
                            fontWeight: 600,
                          }}>
                          <span
                            style={{
                              width: 6,
                              height: 6,
                              borderRadius: 99,
                              backgroundColor: "var(--raven-purple-900)",
                              animation: "pulse 1.5s infinite",
                            }}
                          />
                          <span>
                            {geometryTarget.op === 'append'
                              ? 'Draw another region for: '
                              : 'Draw box to mark: '}
                            <strong>{targetLabel}</strong>
                          </span>
                          <button
                            onClick={() => setBoxDraft(null)}
                            style={{
                              marginLeft: "auto",
                              padding: "4px 8px",
                              fontSize: 12,
                              border: "1px solid #93c5fd",
                              borderRadius: 6,
                              cursor: "pointer",
                              backgroundColor: "white",
                              color: "var(--raven-purple-900)",
                            }}>
                            Cancel
                          </button>
                        </div>
                      );
                    })()}
                </div>

                {/* Canvas component */}
                <div
                  ref={canvasContainerRef}
                  style={{
                    flex: 1,
                    minHeight: 0,
                    borderRadius: 8,
                    overflow: "hidden",
                    backgroundColor: "white",
                    position: "relative",
                  }}>
                  <PidCanvas
                readOnly={readOnlyWizard}
                imageUrl={selectedDoc.image_url || documentApi.getImageUrl(selectedDoc.id)}
                sections={[]}
                onDraw={handleWizardDraw}
                pendingPieces={[]}
                onCancelPending={undefined}
                onSelectSection={() => {}}
                selectedSectionId={undefined}
                onSelectPending={undefined}
                selectedPendingId={undefined}
                docId={selectedDoc.id}
                onCrop={wizardStep === 2 ? handleCrop : undefined}
                wizardMode={true}
                pnidState={pnidState}
                wizardStep={wizardStep}
                activeEquipmentId={activeEquipmentId}
                activeNodeId={activeNodeId}
                selectedInstrumentId={selectedInstrumentId}
                highlightedEntityId={highlightedEntityId}
                onRightClickInstrument={handleRightClickInstrument}
                onInstrumentSubModeToggle={handleInstrumentSubModeToggle}
                collectingRegions={boxDraft !== null}
                draftRegions={boxDraft?.boxes ?? []}
                onFinishRegions={handleFinishRegions}
                onRemoveLastRegion={handleRemoveLastRegion}
                onExitCollect={handleExitCollect}
                showActiveOnly={showActiveOnly}
                focusBbox={focusBbox}
                pendingMarkBbox={
                  markDialog.isOpen
                    ? markDialog.bbox
                    : lineMarkPopover?.stage === 'destination'
                      ? lineMarkPopover.pendingDraw?.bbox
                      : undefined
                }
                onUpdatePendingMarkBbox={markDialog.isOpen ? handlePendingMarkBboxUpdate : undefined}
                onSelectWizardItem={handleWizardCanvasSelect}
                selectedWizardItemKeys={selectedWizardItemKeys}
                savedAnnotationEditModeSignal={savedAnnotationEditModeSignal}
                onSelectAllWizardItems={handleSelectAllWizardAnnotations}
                onClearWizardItemSelection={handleClearWizardAnnotationSelection}
                onDeleteSelectedWizardItems={handleDeleteSelectedWizardAnnotations}
                onMarkSelectedAsRedundancySet={
                  selectedDoc?.domain_id && !readOnlyWizard ? handleMarkSelectedAsRedundancySet : undefined
                }
                onShiftSelectedWizardAnnotations={handleShiftSelectedWizardAnnotations}
                onBeginWizardItemBboxUpdate={handleBeginPersistedBboxUpdate}
                onUpdateWizardItemBbox={handlePersistedBboxUpdate}
                onQuickDeleteWizardItem={handleQuickDeleteWizardItem}
                onShiftWizardAnnotations={handleShiftWizardAnnotations}
                onDoubleClickNode={handleMarkNode}
                onPathStart={handleLinePathStart}
                identifyingBboxes={identifyingBboxes}
                isNodeMarkingMode={isNodeMarkingMode}
                nodeDrawingShape={diagramMode === 'pid' ? nodeDrawingShape : 'pipeline'}
                onNodeDrawingShapeChange={diagramMode === 'pid' ? handleNodeDrawingShapeChange : undefined}
                drawTarget={drawTarget}
                onDraftStateChange={setCanvasHasDraft}
                clearDraftSignal={canvasClearSignal}
                nodePointCandidates={
                  wizardStep === 2 || wizardStep === 4
                    ? nodePointCandidates
                    : []
                }
                onResolveNodePointCandidate={
                  wizardStep === 2 || wizardStep === 4
                    ? resolveNodePointCandidate
                    : undefined
                }
                onImageSize={handleCanvasImageSize}
                segmentReview={{
                  nodeId: lineSegmentReviewPopover?.nodeId ?? null,
                  hoveredDistinctId: lineSegmentReviewPopover?.hoveredDistinctId ?? null,
                  selectedDistinctId: lineSegmentReviewPopover?.selectedDistinctId ?? null,
                }}
                traceLineNo={
                  lineMarkPopover?.values.line_no?.trim()
                    ? lineMarkPopover.values.line_no
                    : pnidState?.nodes.find((n) => n.id === activeNodeId)?.line_no ?? null
                }
                segmentCoordinateScale={
                  segmentCoordinateInfo ? { sx: segmentCoordinateInfo.sx, sy: segmentCoordinateInfo.sy } : undefined
                }
                // Comment mode props
                comments={comments}
                selectedCommentId={selectedCommentId}
                onCreateComment={handleCreateComment}
                onSelectComment={handleSelectComment}
                showResolvedComments={showResolvedComments}
                onToggleCommentMode={handleToggleCommentMode}
                // Intelligence view walkthrough highlights
                walkthroughHighlights={walkthroughHighlights}
                // Template annotation props
                templateAnnotations={savedTemplateAnnotations}
                unsavedMatches={unsavedMatches}
                templateVisibility={templateVisibility}
                selectedAnnotationIds={selectedAnnotationIds}
                activeAnnotationId={activeAnnotationId}
                onDeleteAnnotation={handleDeleteTemplateAnnotation}
                onSelectAnnotation={handleSelectAnnotation}
                onAnnotationClick={handleAnnotationClick}
                onUpdateAnnotationBbox={handleUpdateAnnotationBbox}
                onBulkDeleteAnnotations={handleBulkDeleteAnnotations}
                onClearAnnotationSelection={handleClearAnnotationSelection}
                onAttachTemplateAnnotations={handleDraftAttachTemplateAnnotations}
                onDirectAttachAnnotation={handleDirectAttachAnnotation}
                draftAttachedAnnotationIds={draftAttachedAnnotationIds}
                draftDetachedAnnotationIds={draftDetachedAnnotationIds}
                onCanvasInteraction={handleCanvasInteraction}
              />
            </div>
              </>
            )}
          </div>

          {/* Template Panel - only visible on step 2 (Templates) */}
          {wizardStep === 2 && selectedDoc && pnidState && (
            <TemplatePanel
              sourcePnidId={selectedDoc.id}
              sourcePnidNo={pnidState.pnid_no || selectedDoc.original_filename}
              croppedImage={croppedImage}
              onSaveComplete={() => setCroppedImage(null)}
              onDiscardCrop={() => setCroppedImage(null)}
              // Template matching props
              onRunMatch={handleRunMatch}
              onSaveMatches={handleSaveMatches}
              onDeleteAllMatches={handleDeleteAllTemplateAnnotations}
              matchStats={matchStats}
              unsavedMatches={unsavedMatches}
              templateVisibility={templateVisibility}
              onToggleVisibility={handleToggleTemplateVisibility}
              // Bulk identify props
              onIdentifyAll={handleBulkIdentify}
              bulkIdentifyProgress={bulkIdentifyProgress}
              savedAnnotationCounts={savedAnnotationCounts}
            />
          )}

          {/* Toast notification for Step 2 */}
          {wizardStep === 2 && templateToast && (
            <div
              style={{
                position: 'absolute',
                top: 16,
                left: '50%',
                transform: 'translateX(-50%)',
                padding: '10px 20px',
                borderRadius: 8,
                fontSize: 13,
                fontWeight: 500,
                zIndex: 1000,
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                backgroundColor: templateToast.type === 'success' ? '#f0fdf4' : 'var(--raven-purple-100)',
                color: templateToast.type === 'success' ? '#166534' : 'var(--raven-purple-900)',
                border: `1px solid ${templateToast.type === 'success' ? '#bbf7d0' : '#bfdbfe'}`,
                animation: 'fadeIn 0.2s ease-out',
              }}
            >
              {templateToast.message}
            </div>
          )}

          {/* Pending Queue Panel for Step 2 Template Identification */}
          {wizardStep === 2 && pendingTemplateItems.length > 0 && (
            <div
              style={{
                position: 'absolute',
                bottom: 16,
                right: 16,
                width: 320,
                maxHeight: 400,
                backgroundColor: 'white',
                borderRadius: 12,
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                zIndex: 40,
              }}
            >
              {/* Header */}
              <div
                style={{
                  padding: '12px 16px',
                  borderBottom: '1px solid #e5e7eb',
                  backgroundColor: '#fefce8',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontWeight: 600, fontSize: 14 }}>Pending Template IDs</span>
                  <span
                    style={{
                      backgroundColor: '#fef08a',
                      color: '#a16207',
                      fontSize: 12,
                      padding: '2px 6px',
                      borderRadius: 10,
                      fontWeight: 600,
                    }}
                  >
                    {pendingTemplateItems.length}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: 6, fontSize: 11 }}>
                  {pendingTemplateItems.filter((i) => i.status === 'running').length > 0 && (
                    <span style={{ color: 'var(--raven-purple-900)' }}>⏳ {pendingTemplateItems.filter((i) => i.status === 'running').length}</span>
                  )}
                  {pendingTemplateItems.filter((i) => i.status === 'ready').length > 0 && (
                    <span style={{ color: '#16a34a' }}>✓ {pendingTemplateItems.filter((i) => i.status === 'ready').length}</span>
                  )}
                  {pendingTemplateItems.filter((i) => i.status === 'error').length > 0 && (
                    <span style={{ color: '#dc2626' }}>✕ {pendingTemplateItems.filter((i) => i.status === 'error').length}</span>
                  )}
                </div>
              </div>

              {/* Items list */}
              <div style={{ overflowY: 'auto', flex: 1 }}>
                {pendingTemplateItems.map((item) => (
                  <div
                    key={item.id}
                    style={{
                      padding: '10px 16px',
                      borderBottom: '1px solid #f1f5f9',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                    }}
                  >
                    {/* Status icon */}
                    <span style={{ fontSize: 16 }}>
                      {item.status === 'running' ? '⏳' : item.status === 'ready' ? '✅' : '❌'}
                    </span>

                    {/* Info */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 500, color: '#374151' }}>
                        {item.templateName}
                      </div>
                      {item.templateCategory && (
                        <div style={{ fontSize: 12, color: 'var(--raven-text-secondary)' }}>
                          {item.templateCategory}
                        </div>
                      )}
                      {item.error && (
                        <div style={{ fontSize: 12, color: '#dc2626' }}>
                          {item.error}
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div style={{ display: 'flex', gap: 4 }}>
                      {item.status === 'ready' && (
                        <button
                          onClick={() => openPendingTemplateItem(item.id)}
                          style={{
                            padding: '4px 8px',
                            fontSize: 12,
                            fontWeight: 600,
                            backgroundColor: '#eab308',
                            color: 'white',
                            border: 'none',
                            borderRadius: 4,
                            cursor: 'pointer',
                          }}
                        >
                          Fill
                        </button>
                      )}
                      {item.status === 'error' && (
                        <button
                          onClick={() => retryPendingTemplateItem(item.id)}
                          style={{
                            padding: '4px 8px',
                            fontSize: 12,
                            backgroundColor: '#f1f5f9',
                            color: '#64748b',
                            border: 'none',
                            borderRadius: 4,
                            cursor: 'pointer',
                          }}
                        >
                          Retry
                        </button>
                      )}
                      <button
                        onClick={() => removePendingTemplateItem(item.id)}
                        style={{
                          padding: '4px 6px',
                          fontSize: 12,
                          backgroundColor: 'transparent',
                          color: 'var(--raven-text-secondary)',
                          border: 'none',
                          borderRadius: 4,
                          cursor: 'pointer',
                        }}
                      >
                        ×
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Template SME Capture Prompt - appears when clicking unverified annotation in Step 2 */}
          {templateSmeCapture && (
            <div
              style={{
                position: "fixed",
                left: templateSmePopupPos?.x ?? templateSmeCapture.anchor.x,
                top: templateSmePopupPos?.y ?? templateSmeCapture.anchor.y,
                width: 280,
                backgroundColor: "white",
                borderRadius: 12,
                boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
                border: "2px solid #eab308",
                zIndex: 1000,
                overflow: "hidden",
              }}
              onMouseMove={(e) => {
                if (isTemplateSmePopupDragging) {
                  const newX = e.clientX - templateSmePopupDragOffset.current.x;
                  const newY = e.clientY - templateSmePopupDragOffset.current.y;
                  // Clamp to viewport
                  setTemplateSmePopupPos({
                    x: Math.max(0, Math.min(newX, window.innerWidth - 280)),
                    y: Math.max(0, Math.min(newY, window.innerHeight - 200)),
                  });
                }
              }}
              onMouseUp={() => setIsTemplateSmePopupDragging(false)}
              onMouseLeave={() => setIsTemplateSmePopupDragging(false)}
            >
              <div
                style={{
                  padding: "10px 14px",
                  backgroundColor: "#fefce8",
                  borderBottom: "1px solid #fef08a",
                  cursor: "move",
                  userSelect: "none",
                }}
                onMouseDown={(e) => {
                  // Start dragging
                  const rect = (e.currentTarget.parentElement as HTMLElement).getBoundingClientRect();
                  templateSmePopupDragOffset.current = {
                    x: e.clientX - rect.left,
                    y: e.clientY - rect.top,
                  };
                  setIsTemplateSmePopupDragging(true);
                  e.preventDefault();
                }}
              >
                <div
                  style={{ fontSize: 12, fontWeight: 600, color: "#a16207", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span>🎯 Verify Template Match</span>
                  <span style={{ fontSize: 12, color: "var(--raven-text-secondary)", cursor: "move" }}>⋮⋮</span>
                </div>
                <div style={{ fontSize: 12, color: "var(--raven-text-secondary)", marginTop: 2 }}>
                  🔷 {templateSmeCapture.annotation.template_name}
                  {templateSmeCapture.annotation.template_category && (
                    <span style={{ marginLeft: 4, color: "#a16207" }}>
                      ({templateSmeCapture.annotation.template_category})
                    </span>
                  )}
                </div>
              </div>
              <div style={{ padding: 12 }}>
                <input
                  ref={templateSmeInputRef}
                  type="text"
                  value={templateSmeCaptureInput}
                  onChange={(e) => setTemplateSmeCaptureInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleTemplateSmeSubmit();
                    } else if (e.key === "Escape") {
                      handleTemplateSmeCancel();
                    } else if (e.key === "Tab") {
                      // Tab/Shift+Tab navigates to other annotations
                      e.preventDefault();
                      handleNavigateAnnotation(e.shiftKey ? 'prev' : 'next');
                    }
                  }}
                  placeholder="e.g., Gate Valve, PT-1234..."
                  autoFocus
                  style={{
                    width: "100%",
                    padding: "8px 10px",
                    fontSize: 12,
                    border: "1px solid #d1d5db",
                    borderRadius: 6,
                    outline: "none",
                    marginBottom: 10,
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#eab308";
                    e.target.style.boxShadow =
                      "0 0 0 2px rgba(234, 179, 8, 0.2)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#d1d5db";
                    e.target.style.boxShadow = "none";
                  }}
                />
                <div
                  style={{
                    display: "flex",
                    gap: 8,
                    justifyContent: "flex-end",
                  }}>
                  <button
                    onClick={handleTemplateSmeCancel}
                    style={{
                      padding: "6px 12px",
                      fontSize: 12,
                      backgroundColor: "transparent",
                      color: "var(--raven-text-secondary)",
                      border: "1px solid #e5e7eb",
                      borderRadius: 6,
                      cursor: "pointer",
                    }}>
                    Cancel
                  </button>
                  <button
                    onClick={handleTemplateSmeSubmit}
                    style={{
                      padding: "6px 14px",
                      fontSize: 12,
                      fontWeight: 600,
                      backgroundColor: "#eab308",
                      color: "white",
                      border: "none",
                      borderRadius: 6,
                      cursor: "pointer",
                    }}>
                    Identify →
                  </button>
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: "var(--raven-text-secondary)",
                    marginTop: 8,
                    textAlign: "center",
                  }}>
                  Press Enter to identify • Esc to cancel
                </div>
              </div>
            </div>
          )}

          {/* Slide Panel for Mark Form */}
          <SlidePanel
            isOpen={markDialog.isOpen}
            onClose={closeMarkDialog}
            title={
              markDialog.editTarget
                ? `Edit ${markDialog.kind}`
                : `Mark ${markDialog.kind}`
            }
            width={400}>
            <PnidMarkDialog
              kind={markDialog.kind}
              bboxes={markDialog.bbox ? [markDialog.bbox] : []}
              pnidNoDefault={pnidState?.pnid_no}
              activeEquipment={getActiveEquipment()}
              activeNode={getActiveNode()}
              equipmentInstruments={equipmentInstruments}
              nodeInstruments={nodeInstruments}
              directNodeInstruments={directNodeInstruments}
              nodes={pnidState?.nodes ?? []}
              equipments={pnidState?.equipments ?? []}
              instruments={unifiedInstrumentRefs}
              aiData={aiData}
              currentDocument={selectedDoc}
              allDocuments={documents}
              linkedDocumentStates={linkedDocumentStates}
              onLoadLinkedDocumentState={loadLinkedDocumentState}
              onOpenLinkedDocument={handleOpenLinkedDocument}
              initialValues={markDialog.initialValues}
              diagramMode={diagramMode}
              onSubmit={diagramMode === 'pfd' ? handleMarkDialogSubmitPfd : handleMarkDialogSubmit}
              onCancel={closeMarkDialog}
              isMarkingExisting={Boolean(
                markDialog.initialValues?._markExistingId
              )}
              onContinueDrawing={
                markDialog.kind === "node" &&
                !markDialog.editTarget &&
                !markDialog.initialValues?._markExistingId
                  ? handleContinueDrawing
                  : undefined
              }
              onClearPath={
                markDialog.kind === "node" &&
                !markDialog.editTarget &&
                !markDialog.initialValues?._markExistingId
                  ? handleClearPath
                  : undefined
              }
              onRedrawPath={
                markDialog.kind === "node" &&
                Boolean(markDialog.initialValues?._markExistingId)
                  ? handleRedrawPath
                  : undefined
              }
              onEditInstrument={(instId) => {
                openEditDialog("nodeInstrument", instId, { shouldFocus: false });
              }}
              onUnattachInstrument={(instId) => {
                handleWizardDelete({ kind: "nodeInstrument", id: instId });
              }}
              onEditEquipmentInstrument={(instId) => {
                openEditDialog("equipmentInstrument", instId, { shouldFocus: false });
              }}
              onUnattachEquipmentInstrument={(instId) => {
                handleWizardDelete({ kind: "equipmentInstrument", id: instId });
              }}
              onReorderInstruments={(reordered) => {
                if (activeNodeId) handleReorderNodeInstruments(activeNodeId, reordered);
              }}
              onAutoSequence={async (nodeId) => {
                if (!selectedDoc) return;
                const result = await pnidApi.autoSequenceNodeInstruments(selectedDoc.id, nodeId);
                const updated = await pnidApi.get(selectedDoc.id);
                setPnidState(updated);
                console.log(`Auto-sequenced: ${result.message}`);
              }}
              onAutoAddBranches={async (nodeId) => {
                if (!selectedDoc) return { created: 0, skipped: 0 };
                try {
                  const result = await pnidApi.autoAddBranchInstruments(selectedDoc.id, nodeId, {
                    dryRun: true,
                    endpointThresholdPx: 60,
                  });
                  const candidates = result.candidates ?? [];
                  const skippedCount = result.skipped_endpoints.length;
                  setNodePointCandidates((prev) => {
                    const withoutBranch = prev.filter(
                      (c) => !(c.nodeId === nodeId && c.source === "branch")
                    );
                    const mapped = candidates.map((candidate) => {
                      const branchRef = String(candidate.other_node_no);
                      const branchLine = candidate.other_line_no?.trim();
                      const label = branchLine || `Node #${branchRef}`;
                      return {
                        id: crypto.randomUUID(),
                        nodeId,
                        point: { x: candidate.x, y: candidate.y },
                        bbox: null,
                        status: "ready" as const,
                        options: [{ label: "Branch" as const, score: 1 }],
                        source: "branch" as const,
                        branchRef,
                        branchLineNo: branchLine || null,
                        branchDirection: candidate.branch_direction,
                        branchLabel: label,
                        branchAngleDeg: candidate.angle_deg ?? null,
                        branchDistancePx: candidate.distance_px ?? null,
                      };
                    });
                    return [...withoutBranch, ...mapped];
                  });
                  if (candidates.length > 0) {
                    showAlert("Branch Candidates", `Found ${candidates.length} candidate point${candidates.length === 1 ? "" : "s"}. Review the list to add branches.`, "info");
                  } else {
                    showAlert("No Candidates", skippedCount ? `Checked ${skippedCount} endpoints. No branch candidates were found.` : "No branch candidates were detected for this line.", "info");
                  }
                  return { created: 0, skipped: skippedCount };
                } catch (error) {
                  console.error("Branch candidate detection failed:", error);
                  showAlert("Branch detection failed", "Could not detect branch candidates. Please try again.", "error");
                  return { created: 0, skipped: 0 };
                }
              }}
              branchCandidates={activeNodeId ? nodePointCandidates.filter(c => c.nodeId === activeNodeId && c.source === "branch") : []}
              onConfirmBranchCandidate={(candidateId) => {
                void resolveNodePointCandidate(candidateId, "Branch");
              }}
              onIgnoreBranchCandidate={(candidateId) => {
                void resolveNodePointCandidate(candidateId, "ignore");
              }}
              draftAttachedAnnotationIds={
                markDialog.kind === 'equipment'
                  ? activeEquipmentDraftAttachmentIds
                  : activeNodeDraftAttachmentIds
              }
              templateAnnotations={savedTemplateAnnotations}
              onSaveAttachments={
                markDialog.kind === 'equipment'
                  ? handleEquipmentOverviewSaveAttachments
                  : handleNodeOverviewSaveAttachments
              }
              onDetachAnnotation={
                markDialog.kind === 'equipment'
                  ? handleEquipmentOverviewDetach
                  : handleNodeOverviewDetach
              }
              onHighlightEntity={setHighlightedEntityId}
              onMarkInstrument={handleMarkInstrumentFromDialog}
              onMarkReviewed={handleMarkReviewed}
              onSplitCompoundEquipment={
                compoundSplitTargetId ? handleSplitCompoundEquipment : undefined
              }
              compoundSplitInheritedType={compoundSplitInheritedType}
              readOnly={readOnlyWizard}
            />
          </SlidePanel>

          {/* Review policy issues panel */}
          <SlidePanel
            isOpen={reviewIssuesOpen}
            onClose={() => setReviewIssuesOpen(false)}
            title={`Review Issues${pnidState?.pnid_no ? ` — ${pnidState.pnid_no}` : ""}`}
            width={460}>
            <ReviewIssuesPanel
              docId={selectedDoc?.id ?? null}
              isOpen={reviewIssuesOpen}
              refreshSignal={reviewPolicyRefreshSignal}
              onNavigate={handleReviewFindingNavigate}
            />
          </SlidePanel>

          <SlidePanel
            isOpen={drawingContextOpen}
            onClose={() => setDrawingContextOpen(false)}
            title={`Notes & Revisions${pnidState?.pnid_no ? ` — ${pnidState.pnid_no}` : ""}`}
            width={520}>
            <DrawingContextPanel
              pnidState={pnidState}
              readOnly={readOnlyWizard}
              onNavigate={handleDrawingContextNavigate}
              onSaveNote={handleDrawingContextSaveNote}
            />
          </SlidePanel>

          <SlidePanel
            isOpen={publishPanelOpen}
            onClose={() => setPublishPanelOpen(false)}
            title="Publish check"
            width={460}>
            <PublishCheckPanel
              docId={selectedDoc?.id ?? null}
              isOpen={publishPanelOpen}
              currentDrawingNo={pnidState?.pnid_no ?? null}
              onClose={() => setPublishPanelOpen(false)}
              onPublished={setPublishedVersion}
              onOpenNotes={() => {
                setPublishPanelOpen(false);
                setDrawingContextOpen(true);
              }}
              showAlert={showAlert}
            />
          </SlidePanel>
        </div>
      </div>

      {/* Per-object review policy callout (after save) */}
      {objectPolicyFindings && (
        <div
          style={{
            position: "fixed",
            right: 18,
            bottom: 18,
            width: 360,
            maxHeight: 300,
            overflowY: "auto",
            backgroundColor: "white",
            border: "1px solid #fde68a",
            borderRadius: 10,
            boxShadow: "0 8px 24px rgba(15, 23, 42, 0.18)",
            zIndex: 1100,
            fontSize: 12,
          }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "8px 12px",
              borderBottom: "1px solid #f3f4f6",
              backgroundColor: "#fffbeb",
              borderRadius: "10px 10px 0 0",
            }}>
            <strong style={{ color: "#92400e", flex: 1 }}>
              {objectPolicyFindings.ref}: {objectPolicyFindings.findings.length} policy{" "}
              {objectPolicyFindings.findings.length === 1 ? "issue" : "issues"}
            </strong>
            <button
              type="button"
              onClick={() => {
                setReviewIssuesOpen(true);
                setObjectPolicyFindings(null);
              }}
              style={{
                border: "none",
                backgroundColor: "transparent",
                color: "var(--raven-purple-900)",
                fontSize: 12,
                fontWeight: 700,
                cursor: "pointer",
              }}>
              View all
            </button>
            <button
              type="button"
              onClick={() => setObjectPolicyFindings(null)}
              title="Dismiss"
              style={{
                border: "none",
                backgroundColor: "transparent",
                color: "var(--raven-text-secondary)",
                fontSize: 14,
                fontWeight: 700,
                cursor: "pointer",
                lineHeight: 1,
              }}>
              ×
            </button>
          </div>
          <div style={{ padding: "6px 12px 10px" }}>
            {objectPolicyFindings.findings.map((finding, idx) => (
              <div
                key={`${finding.rule}-${idx}`}
                style={{ display: "flex", gap: 7, alignItems: "baseline", padding: "4px 0" }}>
                <span
                  style={{
                    width: 7,
                    height: 7,
                    borderRadius: "50%",
                    flexShrink: 0,
                    backgroundColor: POLICY_SEVERITY_COLORS[finding.severity]?.dot ?? "var(--raven-text-secondary)",
                    position: "relative",
                    top: -1,
                  }}
                />
                <span
                  style={{
                    fontFamily: "monospace",
                    fontSize: 12,
                    fontWeight: 700,
                    color: POLICY_SEVERITY_COLORS[finding.severity]?.text ?? "var(--raven-text-secondary)",
                    whiteSpace: "nowrap",
                  }}>
                  {finding.rule}
                </span>
                <span style={{ color: "#4b5563" }}>{finding.message}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {annotationImportOpen && selectedDoc && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(15, 23, 42, 0.36)",
            zIndex: 1200,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 24,
          }}>
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="annotation-import-title"
            style={{
              width: "min(720px, 100%)",
              maxHeight: "80vh",
              backgroundColor: "white",
              borderRadius: 8,
              boxShadow: "0 24px 60px rgba(15, 23, 42, 0.22)",
              border: "1px solid #e5e7eb",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}>
            <div
              style={{
                padding: "16px 18px",
                borderBottom: "1px solid #e5e7eb",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
              }}>
              <div style={{ minWidth: 0 }}>
                <div id="annotation-import-title" style={{ fontSize: 15, fontWeight: 700, color: "#111827" }}>
                  Import annotations
                </div>
                <div
                  style={{
                    marginTop: 3,
                    fontSize: 12,
                    color: "#64748b",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}>
                  Target: {getDocumentPrimaryLabel(selectedDoc)} - {diagramTypeLabel(diagramMode)}
                </div>
              </div>
              <button
                type="button"
                aria-label="Close import annotations dialog"
                onClick={() => setAnnotationImportOpen(false)}
                disabled={annotationImporting}
                style={{
                  border: "1px solid #e5e7eb",
                  backgroundColor: "white",
                  borderRadius: 6,
                  padding: "5px 9px",
                  cursor: annotationImporting ? "not-allowed" : "pointer",
                  color: "#475569",
                  fontWeight: 700,
                }}>
                x
              </button>
            </div>

            <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 12, minHeight: 0 }}>
              {!targetStateReady && (
                <div
                  style={{
                    padding: "10px 12px",
                    borderRadius: 6,
                    backgroundColor: "#f8fafc",
                    border: "1px solid #e2e8f0",
                    color: "#475569",
                    fontSize: 12,
                    fontWeight: 600,
                  }}>
                  {annotationImportDisabledReason || "Loading target annotations..."}
                </div>
              )}
              {targetHasAnnotations && (
                <div
                  style={{
                    padding: "10px 12px",
                    borderRadius: 6,
                    backgroundColor: "#fef3c7",
                    border: "1px solid #fcd34d",
                    color: "#92400e",
                    fontSize: 12,
                    fontWeight: 600,
                  }}>
                  Target has existing annotations: {targetAnnotationSummary || "annotations"}. Import will ask for confirmation before replacing them.
                </div>
              )}

              <input
                type="text"
                value={annotationImportSearch}
                onChange={(event) => setAnnotationImportSearch(event.target.value)}
                placeholder={`Search source ${diagramTypeLabel(diagramMode)} documents`}
                disabled={annotationImporting}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: "1px solid #d1d5db",
                  borderRadius: 7,
                  fontSize: 13,
                  outline: "none",
                }}
              />

              <div
                style={{
                  border: "1px solid #e5e7eb",
                  borderRadius: 8,
                  overflowY: "auto",
                  maxHeight: "42vh",
                }}>
                {annotationImportSources.length === 0 ? (
                  <div style={{ padding: 18, color: "var(--raven-text-secondary)", fontSize: 13, textAlign: "center" }}>
                    No matching source documents
                  </div>
                ) : (
                  annotationImportSources.map((doc) => {
                    const annotation = getAnnotation(doc.id);
                    const selected = annotationImportSourceId === doc.id;
                    return (
                      <button
                        key={doc.id}
                        type="button"
                        onClick={() => setAnnotationImportSourceId(doc.id)}
                        disabled={annotationImporting}
                        style={{
                          width: "100%",
                          border: "none",
                          borderBottom: "1px solid #f1f5f9",
                          backgroundColor: selected ? "var(--raven-purple-100)" : "white",
                          cursor: annotationImporting ? "not-allowed" : "pointer",
                          padding: "11px 14px",
                          textAlign: "left",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 14,
                        }}>
                        <div style={{ minWidth: 0 }}>
                          <div
                            style={{
                              fontSize: 13,
                              fontWeight: 700,
                              color: selected ? "var(--raven-purple-800)" : "#111827",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}>
                            {getDocumentPrimaryLabel(doc)}
                          </div>
                          <div
                            style={{
                              marginTop: 3,
                              fontSize: 12,
                              color: "#64748b",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}>
                            {doc.pnid_title || getDocumentSecondaryLabel(doc)}
                          </div>
                          <div
                            style={{
                              marginTop: 3,
                              fontSize: 12,
                              color: "var(--raven-text-secondary)",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}>
                            {annotation?.status || "draft"}
                            {annotation?.updated_at ? ` - updated ${annotation.updated_at}` : ""}
                          </div>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                          <span
                            style={{
                              fontSize: 12,
                              fontWeight: 700,
                              padding: "3px 7px",
                              borderRadius: 4,
                              backgroundColor: "#f3f4f6",
                              color: "#374151",
                              whiteSpace: "nowrap",
                            }}>
                            {documentDomainPathLabel(domainIndex, doc)}
                          </span>
                          {selected && (
                            <span style={{ fontSize: 12, fontWeight: 700, color: "var(--raven-purple-800)" }}>
                              Selected
                            </span>
                          )}
                        </div>
                      </button>
                    );
                  })
                )}
              </div>

              {selectedAnnotationImportSource && (
                <div
                  style={{
                    border: targetHasAnnotations ? "1px solid #fed7aa" : "1px solid #bfdbfe",
                    backgroundColor: targetHasAnnotations ? "#fff7ed" : "var(--raven-purple-100)",
                    borderRadius: 8,
                    padding: "10px 12px",
                    color: targetHasAnnotations ? "#9a3412" : "var(--raven-purple-900)",
                    fontSize: 12,
                    display: "flex",
                    flexDirection: "column",
                    gap: 5,
                  }}>
                  <div style={{ fontWeight: 800 }}>
                    {targetHasAnnotations ? "Confirmation required before replacing target annotations" : "Ready to import annotations"}
                  </div>
                  <div>
                    <strong>Import from:</strong> {getDocumentPrimaryLabel(selectedAnnotationImportSource)} - {documentDomainPathLabel(domainIndex, selectedAnnotationImportSource)} - {diagramTypeLabel(diagramMode)}
                  </div>
                  <div>
                    <strong>Into target:</strong> {getDocumentPrimaryLabel(selectedDoc)} - {documentDomainPathLabel(domainIndex, selectedDoc)} - {diagramTypeLabel(diagramMode)}
                  </div>
                  <div style={{ color: targetHasAnnotations ? "#c2410c" : "var(--raven-purple-800)", fontWeight: 700 }}>
                    {targetHasAnnotations
                      ? `Will replace ${targetAnnotationSummary || "existing target annotations"} after confirmation. Target metadata will be retained.`
                      : "Target has no existing annotations, so this will copy directly. Target metadata will be retained."}
                  </div>
                </div>
              )}
            </div>

            <div
              style={{
                padding: "12px 16px",
                borderTop: "1px solid #e5e7eb",
                display: "flex",
                justifyContent: "flex-end",
                gap: 10,
              }}>
              <button
                type="button"
                onClick={() => setAnnotationImportOpen(false)}
                disabled={annotationImporting}
                style={{
                  padding: "8px 14px",
                  borderRadius: 6,
                  border: "1px solid #d1d5db",
                  backgroundColor: "white",
                  color: "#374151",
                  cursor: annotationImporting ? "not-allowed" : "pointer",
                  fontWeight: 600,
                }}>
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void handleImportAnnotations()}
                disabled={annotationImportDisabled || !annotationImportSourceId}
                style={{
                  padding: "8px 14px",
                  borderRadius: 6,
                  border: "none",
                  backgroundColor:
                    annotationImportDisabled || !annotationImportSourceId
                      ? "#cbd5e1"
                      : "var(--raven-purple-900)",
                  color: annotationImportDisabled || !annotationImportSourceId ? "#475569" : "white",
                  cursor:
                    annotationImportDisabled || !annotationImportSourceId
                      ? "not-allowed"
                      : "pointer",
                  fontWeight: 700,
                }}>
                {annotationImporting ? "Importing..." : targetHasAnnotations ? "Review overwrite" : "Import"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Review Modal */}
      <ReviewModal
        isOpen={reviewModalOpen}
        onClose={() => setReviewModalOpen(false)}>
        <PnidReviewTables
          pnidState={pnidState}
          onSelect={handleWizardSelect}
          onEdit={(target) => {
            // The edit panel slides in under this modal, so the modal steps
            // aside for it — the same hand-off the Publish check makes.
            setReviewModalOpen(false);
            openEditDialog(target.kind, target.id);
          }}
          onEditEquipment={handleReviewTableEquipmentEdit}
          onSplitEquipment={
            // Only a P&ID projects into the plant register, so only there does a
            // compound tag warn on publication and only there is the split
            // offered — the same condition the edit panel's split obeys.
            diagramMode === 'pfd'
              ? undefined
              : async (equipmentId, parts) => {
                  // The row itself reports the result once the refresh lands, so
                  // the tags the run wrote are not needed here.
                  await splitCompoundEquipmentOnDrawing(equipmentId, parts);
                }
          }
          onOpenPublishCheck={() => {
            setReviewModalOpen(false);
            setPublishPanelOpen(true);
          }}
          onRebox={(target) => {
            const targetNode = target.kind === 'node'
              ? pnidState?.nodes.find((node) => node.id === target.id)
              : null;
            if (diagramMode === 'pfd' && targetNode?.is_duct) {
              showAlert('P&ID-only geometry', 'Existing ducts remain visible in PFD mode, but duct authoring is available only in P&ID mode.', 'info');
              return;
            }
            setBoxDraft(
              armDraft(IDENTIFY_KIND_BY_GEOMETRY_TARGET[target.kind], {
                ...target,
                op: 'replace',
              }),
            );
          }}
          onDelete={handleWizardDelete}
          onMarkReviewed={handleMarkReviewed}
          onReorderNodeInstruments={handleReorderNodeInstruments}
          directNodeInstruments={directNodeInstruments}
          documentId={selectedDoc?.id}
          readOnly={readOnlyWizard}
        />
      </ReviewModal>

      {/* Version History Modal */}
      <VersionHistoryModal
        isOpen={versionHistoryOpen}
        onClose={() => setVersionHistoryOpen(false)}
        docId={selectedDoc?.id || null}
        currentUser={effectiveUserName || undefined}
        onRestore={(restoredState) => {
          setPnidState(restoredState);
          setSavedTemplateAnnotations(getTemplateAnnotationsFromInstruments(restoredState.instruments));
          if (selectedDoc?.id) {
            void documentApi.get(selectedDoc.id).then((doc) => {
              onPatchDocument(doc.id, doc);
              setDiagramMode(doc.diagram_type === 'pfd' ? 'pfd' : 'pid');
            }).catch((error) => {
              console.error("Failed to refresh restored document:", error);
            });
          }
          setPreviewingVersion(null);
          setOriginalStateBeforePreview(null);
          showAlert(
            "Restored",
            "Successfully restored to the selected version.",
            "success"
          );
        }}
        onPreview={(previewState, versionNumber) => {
          // Store current state before preview (only if not already previewing)
          if (!previewingVersion && pnidState) {
            setOriginalStateBeforePreview(pnidState);
          }
          setPnidState(previewState);
          setSavedTemplateAnnotations(getTemplateAnnotationsFromInstruments(previewState.instruments));
          setPreviewingVersion(versionNumber);
        }}
        previewingVersion={previewingVersion}
        onExitPreview={() => {
          // Restore original state
          if (originalStateBeforePreview) {
            setPnidState(originalStateBeforePreview);
            setSavedTemplateAnnotations(getTemplateAnnotationsFromInstruments(originalStateBeforePreview.instruments));
          }
          setPreviewingVersion(null);
          setOriginalStateBeforePreview(null);
        }}
      />

      {/* Alert Dialog */}
      <AlertDialog
        isOpen={alertDialog.isOpen}
        title={alertDialog.title}
        message={alertDialog.message}
        type={alertDialog.type}
        onClose={closeAlert}
      />

      {/* Confirm Dialog (for delete/change confirmations) */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        type={confirmDialog.type || "danger"}
        confirmText={confirmDialog.confirmText || "Confirm"}
        cancelText="Cancel"
        onConfirm={confirmDialog.onConfirm}
        onCancel={() =>
          setConfirmDialog((prev) => ({ ...prev, isOpen: false }))
        }
      />

      {/* Step Change Confirm Dialog */}
      <ConfirmDialog
        isOpen={stepChangeConfirm.isOpen}
        title="Leave this step?"
        message={
          stepChangeConfirm.targetStep
            ? `You have unfinished items in Step ${wizardStep}. Discard them and switch to Step ${stepChangeConfirm.targetStep}?`
            : "You have unfinished items in this step. Discard them and switch steps?"
        }
        type="warning"
        confirmText="Discard & Switch"
        cancelText="Stay"
        onConfirm={confirmStepChange}
        onCancel={() =>
          setStepChangeConfirm({ isOpen: false, targetStep: null })
        }
      />

      {/* EquipmentOverviewDialog removed - functionality consolidated into PnidMarkDialog */}

      {/* Equipment Operating Card (Intelligence View) */}
      {import.meta.env.DEV && viewMode === 'intelligence' && plantModel && selectedEquipmentTag && (() => {
        const equipment = plantModel.equipment?.[selectedEquipmentTag];
        if (!equipment) return null;
        return (
          <EquipmentOperatingCard
            equipment={equipment}
            onClose={() => setSelectedEquipmentTag(null)}
            onHighlightLines={(lines) => {
              // TODO: Implement line highlighting
              console.log('Highlight lines:', lines);
            }}
          />
        );
      })()}

      {/* Process Lens Panel (Intelligence View - dev only) */}
      {import.meta.env.DEV && viewMode === 'intelligence' && plantModel && showProcessLens && (
        <ProcessLensPanel
          processOverview={plantModel.process_overview}
          functionalBlocks={plantModel.functional_blocks}
          onClose={() => setShowProcessLens(false)}
        />
      )}

      {/* Control Lens Panel (Intelligence View - dev only) */}
      {import.meta.env.DEV && viewMode === 'intelligence' && plantModel && showControlLens && (
        <ControlLensPanel
          controlLoops={plantModel.control_loops}
          interlocks={plantModel.interlocks}
          onClose={() => setShowControlLens(false)}
        />
      )}

      {/* Walkthrough Panel (Intelligence View - dev only) */}
      {import.meta.env.DEV && viewMode === 'intelligence' && plantModel && showWalkthrough && (
        <WalkthroughPanel
          onHighlightChange={setWalkthroughHighlights}
          onClose={() => {
            setShowWalkthrough(false);
            setWalkthroughHighlights(null);
          }}
        />
      )}

      {/* NodeOverviewDialog removed - consolidated into PnidMarkDialog */}

      {/* Comment Input Dialog */}
      {commentInputPosition && (
        <CommentInput
          position={{ x: commentInputPosition.x, y: commentInputPosition.y }}
          onSubmit={handleSubmitComment}
          onCancel={() => setCommentInputPosition(null)}
        />
      )}

      {/* Comment Popover */}
      {selectedCommentId &&
        selectedCommentScreenPos &&
        (() => {
          const comment = comments.find((c) => c.id === selectedCommentId);
          if (!comment) return null;
          return (
            <CommentPopover
              comment={comment}
              position={selectedCommentScreenPos}
              currentUser={effectiveUserName || "Anonymous"}
              onResolve={handleResolveComment}
              onUnresolve={handleUnresolveComment}
              onDelete={handleDeleteComment}
              onClose={() => {
                setSelectedCommentId(null);
                setSelectedCommentScreenPos(null);
              }}
            />
          );
        })()}

      {/* Line Segment Review Popover */}
      {lineSegmentReviewPopover &&
        pnidState &&
        (() => {
          const node = pnidState.nodes.find(
            (n) => n.id === lineSegmentReviewPopover.nodeId
          );
          if (!node) return null;
          return (
            <LineSegmentReviewPopover
              node={node}
              position={lineSegmentReviewPopover.position}
              hoveredDistinctId={lineSegmentReviewPopover.hoveredDistinctId}
              selectedDistinctId={lineSegmentReviewPopover.selectedDistinctId}
              segmentCoordinateInfo={segmentCoordinateInfo}
              onHoverDistinct={(id) =>
                setLineSegmentReviewPopover((p) =>
                  p ? { ...p, hoveredDistinctId: id } : p
                )
              }
              onSelectDistinct={(id) =>
                setLineSegmentReviewPopover((p) =>
                  p ? { ...p, selectedDistinctId: id } : p
                )
              }
              onConfirmDistinct={(distinct) =>
                updateNodeSegmentsForDistinct(node.id, distinct, "confirmed")
              }
              onDeleteDistinct={(distinct) =>
                updateNodeSegmentsForDistinct(node.id, distinct, "deleted")
              }
              onConfirmAll={() => confirmAllNodeSegments(node.id)}
              onClose={() => setLineSegmentReviewPopover(null)}
            />
          );
        })()}

      {/* Line Marking Popover (Step 4) */}
      {wizardStep === 4 && lineMarkPopover?.isOpen && !markDialog.isOpen && (
        <LineMarkPopover
          position={lineMarkPopover.position}
          title={
            lineMarkPopover.nodeId
              ? `Line ${
                  lineMarkPopover.stage === "source" ? "Start" : "End"
                } — Node #${
                  pnidState?.nodes.find((n) => n.id === lineMarkPopover.nodeId)
                    ?.node_no ?? ""
                }`
              : `Line ${
                  lineMarkPopover.stage === "source" ? "Start" : "End"
                } — New`
          }
          stage={lineMarkPopover.stage}
          values={lineMarkPopover.values}
          aiData={aiData}
          nodes={pnidState?.nodes ?? []}
          equipments={pnidState?.equipments ?? []}
          instruments={unifiedInstrumentRefs}
          currentNodeId={lineMarkPopover.nodeId ?? undefined}
          pnidOptions={pnidState?.pnid_no ? [pnidState.pnid_no] : []}
          onChange={(patch) =>
            setLineMarkPopover((prev) =>
              prev ? { ...prev, values: { ...prev.values, ...patch } } : prev
            )
          }
          onNext={() =>
            setLineMarkPopover((prev) =>
              prev
                ? prev.values.is_duct && prev.pendingDraw
                  ? { ...prev, stage: 'destination', isOpen: true }
                  : { ...prev, isOpen: false }
                : prev
            )
          }
          onBack={() =>
            setLineMarkPopover((prev) =>
              prev ? { ...prev, stage: "source", isOpen: true } : prev
            )
          }
          onFinish={() => {
            void finalizeLineFromPopover();
          }}
          onClose={() => {
            setLineMarkPopover(null);
            setIsNodeMarkingMode(false);
            setNodeDrawingShape('pipeline');
            closeMarkDialog();
            setCanvasClearSignal((prev) => prev + 1);
          }}
          groqEnabled={groqEnabled}
          onDetectSource={handleDetectSource}
          onDetectDest={handleDetectDest}
          isDetectingSource={isDetectingSource}
          isDetectingDest={isDetectingDest}
          aiDetectedSource={lineMarkPopover.aiDetectedSource}
          aiDetectedDest={lineMarkPopover.aiDetectedDest}
        />
      )}

      {/* Unified Instrument Editor (right-click any instrument on canvas) */}
      {/*
        The dialog stands aside while its own regions are being redrawn: it is a
        floating panel over the sheet, and the area an SME needs to annotate is
        routinely underneath it. It comes back, updated, when the draft commits.
      */}
      {instrumentEditorState && !boxDraft?.target && (
        <InstrumentEditor
          key={`${instrumentEditorState.instrument.id}:${instrumentEditorState.isUsingLocalSnapshot ? 'local' : 'backend'}`}
          instrument={instrumentEditorState.instrument}
          position={instrumentEditorState.position}
          onSave={handleInstrumentEditorSave}
          onDelete={handleInstrumentEditorDelete}
          onClose={() => { setInstrumentEditorState(null); setSelectedInstrumentId(null); setHighlightedEntityId(null); }}
          onHighlightEntity={setHighlightedEntityId}
          onOpenPnidHyperlink={handleOpenPnidHyperlink}
          onMarkReviewed={handleMarkReviewed}
          docInstruments={pnidState?.instruments ?? []}
          allNodes={pnidState?.nodes ?? []}
          allEquipments={pnidState?.equipments ?? []}
          allInstruments={unifiedInstrumentRefs}
          allDocuments={documents}
          currentDocId={selectedDoc?.id ?? null}
          currentDomainId={selectedDoc?.domain_id ?? null}
          currentPlant={selectedDoc?.plant ?? null}
          diagramMode={diagramMode}
          linkedDocumentStates={linkedDocumentStates}
          onLoadLinkedDocumentState={loadLinkedDocumentState}
          onOpenLinkedDocument={handleOpenLinkedDocument}
          readOnly={readOnlyWizard || Boolean(instrumentEditorState.isUsingLocalSnapshot)}
          latestInstrumentLoading={Boolean(instrumentEditorState.isUsingLocalSnapshot && !instrumentEditorState.latestLoadError)}
          latestInstrumentError={instrumentEditorState.latestLoadError ?? null}
          identifyPrefill={instrumentEditorState.identifyPrefill ?? null}
          onEditRegions={(instrumentId) => {
            setBoxDraft(
              armDraft(
                'instrument',
                {
                  kind:
                    instrumentEditorState.instrument.context_type === 'node'
                      ? 'nodeInstrument'
                      : 'equipmentInstrument',
                  id: instrumentId,
                  op: 'replace',
                },
                instrumentRegions(instrumentEditorState.instrument),
              ),
            );
          }}
        />
      )}
    </div>
    </div>
  );
};

export default DrawingWorkspace;
